import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import "./SellerDashboard.css";

const TABS = [
  ["overview", "Overview"],
  ["shop", "Shop"],
  ["inventory", "Inventory"],
  ["listings", "Listings"],
  ["offers", "Offers"],
  ["orders", "Orders"],
  ["shipping", "Shipping"],
  ["payments", "Payments"],
  ["channels", "Sales Channels"],
  ["plan", "Plan & Fees"],
];

// Countdown is display-only; eBay remains authoritative for offer status.
const offerCountdown = (expiresAt, status, now) => {
  const state = String(status || "").toLowerCase();
  const finalStatuses = ["expired", "declined", "accepted", "withdrawn", "cancelled", "rejected"];
  const deadline = expiresAt ? Date.parse(expiresAt) : NaN;
  if (finalStatuses.includes(state)) return { label: "—", urgency: "closed" };
  if (!Number.isFinite(deadline)) return { label: "—", urgency: "unknown" };
  const ms = deadline - now;
  if (ms <= 0) return { label: "Past deadline", urgency: "expired" };
  if (state === "countered") return { label: "Counter sent", urgency: "closed" };
  const mins = Math.ceil(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  const minutes = mins % 60;
  const label = days ? `${days}d ${hours}h` : hours ? `${hours}h ${minutes}m` : `${minutes}m`;
  return { label, urgency: ms <= 3600000 ? "critical" : ms <= 86400000 ? "soon" : "normal" };
};

const ebayOfferCanCounter = (offer, now = Date.now()) => {
  if (!offer?.saved_offer_id || !["pending", "active"].includes(String(offer.status || "").toLowerCase())) return false;
  const expiry = offerExpiry(offer);
  if (!expiry) return false; // Require a known, valid deadline before offering a live action.
  const deadline = Date.parse(expiry);
  return Number.isFinite(deadline) && deadline > now;
};

const offerExpiry = (offer) => offer?.expires_at || offer?.expiresAt || offer?.expiration_at || offer?.expiration_date || null;

const money = (value, currency = "USD") => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
};

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const errorMessage = (error, fallback = "Something went wrong.") =>
  error?.message || error?.details || error?.hint || fallback;

// Read-only suggestions. Similar titles are NEVER treated as confirmed inventory links.
const ebayMatchKey = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().replace(/\s+/g, " ");
const ebayMatchWords = (value) => new Set(ebayMatchKey(value).split(" ").filter((word) => word.length > 2));
const ebaySuggestionScore = (a, b) => {
  const left = ebayMatchWords(a);
  const right = ebayMatchWords(b);
  if (left.size < 2 || right.size < 2) return 0;
  const common = [...left].filter((word) => right.has(word)).length;
  return common / Math.max(left.size, right.size);
};

const R2_PUBLIC_BASE_URL = "https://media.hobbli.com";

const encodeObjectKey = (objectKey) =>
  String(objectKey)
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");

const resolveImageUrl = (image) => {
  if (!image) return null;
  if (image.storage_provider === "r2" && image.object_key) {
    return `${R2_PUBLIC_BASE_URL}/${encodeObjectKey(image.object_key)}`;
  }
  return null;
};

const sortedInventoryImages = (item) =>
  [...(item?.inventory_images || [])].sort(
    (a, b) => Number(a.position || 0) - Number(b.position || 0)
  );

const primaryInventoryImage = (item) => {
  const images = sortedInventoryImages(item);
  return images.find((image) => image.is_primary) || images[0] || null;
};

const collectionItemTitle = (item) =>
  item?.user_specific_name ||
  item?.items?.name ||
  (item?.item_id ? `Canonical item #${item.item_id}` : `Collection item #${item?.id}`);

const collectionItemMeta = (item) =>
  [
    item?.user_specific_year ?? item?.items?.year,
    item?.user_specific_brand || item?.items?.brand,
    item?.user_specific_set_name || item?.items?.set_name,
    item?.items?.card_number ? `#${item.items.card_number}` : null,
  ].filter(Boolean).join(" · ");

export default function SellerDashboard({ sellerAccess }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    const requestedTab = new URLSearchParams(window.location.search).get("tab");
    return TABS.some(([key]) => key === requestedTab) ? requestedTab : "overview";
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState(null);
  const [shop, setShop] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [listings, setListings] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [usage, setUsage] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [inventoryDraft, setInventoryDraft] = useState({
    title: "",
    description: "",
    condition: "",
    quantity: "1",
    cost: "",
  });
  const [profileDraft, setProfileDraft] = useState({
    name: "",
    description: "",
    shippingAmount: "",
    tareOz: "",
    lengthIn: "",
    widthIn: "",
    heightIn: "",
    freeShipping: false,
    localPickup: false,
    combinedShipping: true,
    isDefault: false,
  });

  const [listingDraft, setListingDraft] = useState({
    inventoryId: "",
    price: "",
    quantity: "1",
    acceptsOffers: false,
    minimumOffer: "",
    shippingMode: "free",
    shippingProfileId: "",
    shippingAmount: "",
    localPickup: false,
    combinedShipping: true,
    publishNow: true,
  });
  const [listingView, setListingView] = useState("current");
  const [listingSourceFilter, setListingSourceFilter] = useState("all");
  const [listingChannelFilter, setListingChannelFilter] = useState("all");
  const [listingStatusFilter, setListingStatusFilter] = useState("all");
  const [bulkStatusSelection, setBulkStatusSelection] = useState(new Set());
  const [bulkStatusAction, setBulkStatusAction] = useState("publish");
  const [bulkStatusBusy, setBulkStatusBusy] = useState(false);
  const [bulkStatusProgress, setBulkStatusProgress] = useState(null);
  const [bulkListingSelection, setBulkListingSelection] = useState(new Set());
  const [bulkListingOverrides, setBulkListingOverrides] = useState({});
  const [bulkListingMode, setBulkListingMode] = useState("draft");
  const [bulkListingReview, setBulkListingReview] = useState(false);
  const [bulkListingProgress, setBulkListingProgress] = useState(null);
  const [bulkListingBusy, setBulkListingBusy] = useState(false);
  const [editingListingId, setEditingListingId] = useState(null);
  const [listingEditDraft, setListingEditDraft] = useState({
    price: "",
    quantity: "1",
    acceptsOffers: false,
    minimumOffer: "",
    shippingMode: "free",
    shippingProfileId: "",
    shippingAmount: "",
    localPickup: false,
    combinedShipping: true,
  });
  const [shopDraft, setShopDraft] = useState({
    name: "",
    description: "",
    visibility: "public",
  });
  const [slugDraft, setSlugDraft] = useState("");
  const [slugCheck, setSlugCheck] = useState(null);

  const [inventoryAddMode, setInventoryAddMode] = useState("shop");
  const [inventoryView, setInventoryView] = useState("current");
  const [editingInventoryId, setEditingInventoryId] = useState(null);
  const [inventoryEditDraft, setInventoryEditDraft] = useState({
    title: "",
    description: "",
    condition: "",
    quantity: "1",
    cost: "",
  });
  const [collectionItems, setCollectionItems] = useState([]);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [collectionSearch, setCollectionSearch] = useState("");
  const [selectedCollectionIds, setSelectedCollectionIds] = useState(new Set());
  const [imageManagerInventoryId, setImageManagerInventoryId] = useState(null);
  const [imageManagerBusy, setImageManagerBusy] = useState(false);
  const [commerceLoading, setCommerceLoading] = useState(false);
  const [sellerOrders, setSellerOrders] = useState([]);
  const [hobbliOrderTitles, setHobbliOrderTitles] = useState({});
  const [ebayOrders, setEbayOrders] = useState([]);
  const [expandedEbayOrder, setExpandedEbayOrder] = useState(null);
  const [offerThreads, setOfferThreads] = useState([]);
  const [ebayNegotiationRows, setEbayNegotiationRows] = useState([]);
  const [ebayNegotiationError, setEbayNegotiationError] = useState(null);
  const [expandedEbayThreads, setExpandedEbayThreads] = useState({});
  const [offerViewFilter, setOfferViewFilter] = useState("all");
  const [offerClockNow, setOfferClockNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setOfferClockNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [offerEvents, setOfferEvents] = useState([]);
  const [offerDetailLoading, setOfferDetailLoading] = useState(false);
  const [offerActionBusy, setOfferActionBusy] = useState(false);
  const [offerCounterAmount, setOfferCounterAmount] = useState("");
  const [ebayCounterOffer, setEbayCounterOffer] = useState(null);
  const [ebayCounterAmount, setEbayCounterAmount] = useState("");
  const [ebayCounterBusy, setEbayCounterBusy] = useState(false);
  const [ebayDeclineBusy, setEbayDeclineBusy] = useState(null);
  const [ebayAcceptBusy, setEbayAcceptBusy] = useState(null);
  const [offerMessage, setOfferMessage] = useState("");
  const [offerConfirmAction, setOfferConfirmAction] = useState(null);
  const [offerStock, setOfferStock] = useState(null);
  const [offerStockLoading, setOfferStockLoading] = useState(false);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [paypalBusy, setPaypalBusy] = useState(false);
  const [paypalResult, setPaypalResult] = useState(null);
  const [feePolicy, setFeePolicy] = useState(null);
  const [planAccess, setPlanAccess] = useState(null);
  const [ebayChannel, setEbayChannel] = useState(null);
  const [ebayChannelLoading, setEbayChannelLoading] = useState(false);
  const [ebayConnectBusy, setEbayConnectBusy] = useState(false);
  const [ebayRefreshTestBusy, setEbayRefreshTestBusy] = useState(false);
  const [ebayScopeBusy, setEbayScopeBusy] = useState(false);
  const [ebayScopeResult, setEbayScopeResult] = useState(null);
  const [ebayFinancialBusy, setEbayFinancialBusy] = useState(false);
  const [ebayFinancialResult, setEbayFinancialResult] = useState(null);
  const [ebayFinancialRunBusy, setEbayFinancialRunBusy] = useState(false);
  const [ebayFinancialRunResult, setEbayFinancialRunResult] = useState(null);
  const [ebayFinancialManualOrder, setEbayFinancialManualOrder] = useState("22-15291-92796");
  const [ebayFinancialQueueBusy, setEbayFinancialQueueBusy] = useState(false);
  const [ebayFinancialQueueResult, setEbayFinancialQueueResult] = useState(null);
  const [ebayFinancialIngestBusy, setEbayFinancialIngestBusy] = useState(false);
  const [ebayFinancialIngestResult, setEbayFinancialIngestResult] = useState(null);
  const [ebayDiscoveryBusy, setEbayDiscoveryBusy] = useState(false);
  const [ebayDiscoveryResult, setEbayDiscoveryResult] = useState(null);
  const [ebayReconcileBusy, setEbayReconcileBusy] = useState(false);
  const [ebayReconcileResult, setEbayReconcileResult] = useState(null);
  const [ebayNotificationRegisterBusy, setEbayNotificationRegisterBusy] = useState(false);
  const [ebaySubscriptionId, setEbaySubscriptionId] = useState('');
  const [ebayNotificationRegisterResult, setEbayNotificationRegisterResult] = useState(null);
  const [ebayQueueTestBusy, setEbayQueueTestBusy] = useState(false);
  const [ebayQueueTestResult, setEbayQueueTestResult] = useState(null);
  const [ebayOrderDiscoveryBusy, setEbayOrderDiscoveryBusy] = useState(false);
  const [ebayOrderDiscoveryResult, setEbayOrderDiscoveryResult] = useState(null);
  const ebayOfferAutoFetched = useRef(false);
  const [ebayOfferBusy, setEbayOfferBusy] = useState(false);
  const [ebayOfferResult, setEbayOfferResult] = useState(null);
  const [ebayOfferItemId, setEbayOfferItemId] = useState("");
  const [ebayBuyerDiagnosticBusy, setEbayBuyerDiagnosticBusy] = useState(false);
  const [ebayBuyerDiagnosticResult, setEbayBuyerDiagnosticResult] = useState(null);
  const [ebayPreferencesBusy, setEbayPreferencesBusy] = useState(false);
  const [ebayPreferencesResult, setEbayPreferencesResult] = useState(null);
  const [ebayTradingBusy, setEbayTradingBusy] = useState(false);
  const [ebayTradingResult, setEbayTradingResult] = useState(null);
  const [ebayPreviewBusy, setEbayPreviewBusy] = useState(false);
  const [ebayPreview, setEbayPreview] = useState(null);
  const [channelLinks, setChannelLinks] = useState([]);
  const [channelLinksLoaded, setChannelLinksLoaded] = useState(false);
  const [channelLinksLoading, setChannelLinksLoading] = useState(false);
  const [channelLinksError, setChannelLinksError] = useState(null);
  const [ebayPreviewFilter, setEbayPreviewFilter] = useState("all");
  const [ebayImportSelection, setEbayImportSelection] = useState(new Set());
  const [ebayImportBusy, setEbayImportBusy] = useState(false);
  const [ebayImportProgress, setEbayImportProgress] = useState(null);
  const [ebayRefreshTestResult, setEbayRefreshTestResult] = useState(null);
  const [ebayEnrichBusy, setEbayEnrichBusy] = useState(false);
  const [ebayEnrichResult, setEbayEnrichResult] = useState(null);
  const [ebayEnrichProgress, setEbayEnrichProgress] = useState(null);
  const [ebayEnrichBatchSize, setEbayEnrichBatchSize] = useState('10');
  const [selectedSellerOrderId, setSelectedSellerOrderId] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [orderDetailError, setOrderDetailError] = useState(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundMode, setRefundMode] = useState("full");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("buyer_request");
  const [refundNotes, setRefundNotes] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundRequestId, setRefundRequestId] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [sellerReturns, setSellerReturns] = useState([]);
  const [returnBusyId, setReturnBusyId] = useState(null);
  const [fulfillmentOpen, setFulfillmentOpen] = useState(false);
  const [fulfillmentBusy, setFulfillmentBusy] = useState(false);
  const [orderCancelBusy, setOrderCancelBusy] = useState(false);
  const [fulfillmentDraft, setFulfillmentDraft] = useState({
    carrier: "USPS",
    service: "Ground Advantage",
    trackingNumber: "",
    trackingUrl: "",
    sellerNotes: "",
  });

  const sellerAccountId =
    sellerAccess?.sellerAccountId || sellerAccess?.seller_account_id || null;
  const shopId = sellerAccess?.shopId || sellerAccess?.shop_id || null;
  const isAdmin = sellerAccess?.isAdmin ?? sellerAccess?.is_admin ?? false;
  const canSell =
    sellerAccess?.canSellOnHobbli ?? sellerAccess?.can_sell_on_hobbli ?? false;

  const showNotice = useCallback((type, message) => {
    setNotice({ type, message });
    window.clearTimeout(showNotice._timer);
    showNotice._timer = window.setTimeout(() => setNotice(null), 5000);
  }, []);

  const loadDashboard = useCallback(async (quiet = false) => {
    if (!sellerAccountId || !shopId) {
      setLoading(false);
      return;
    }

    quiet ? setRefreshing(true) : setLoading(true);

    try {
      const [shopResult, inventoryResult, listingResult, profileResult, usageResult, entitlementResult] =
        await Promise.all([
          supabase.from("shops").select("*").eq("id", shopId).single(),
          supabase
            .from("seller_inventory")
            .select(`
              *,
              inventory_images (
                id, storage_provider, storage_bucket, object_key, position, is_primary
              )
            `)
            .eq("shop_id", shopId)
            .order("created_at", { ascending: false }),
          supabase
            .from("listings")
            .select(`
              *,
              seller_inventory (
                id, shop_id, title, item_id, source_user_item_id,
                quantity_on_hand, quantity_reserved, status, condition,
                inventory_images (
                  id, storage_provider, storage_bucket, object_key, position, is_primary
                )
              )
            `)
            .order("created_at", { ascending: false }),
          supabase
            .from("seller_shipping_profiles")
            .select("*")
            .eq("seller_account_id", sellerAccountId)
            .order("is_default", { ascending: false })
            .order("name", { ascending: true }),
          supabase.rpc("get_shop_listing_usage", { p_shop_id: shopId }),
          supabase.rpc("get_shop_entitlements", { p_shop_id: shopId }),
        ]);

      const firstError = [
        shopResult,
        inventoryResult,
        listingResult,
        profileResult,
        usageResult,
        entitlementResult,
      ].find((result) => result.error)?.error;

      if (firstError) throw firstError;

      setShop(shopResult.data || null);
      setInventory(inventoryResult.data || []);
      setListings(
        (listingResult.data || []).filter(
          (listing) => listing.seller_inventory?.shop_id === shopId
        )
      );
      setProfiles(profileResult.data || []);
      setUsage(Array.isArray(usageResult.data) ? usageResult.data[0] || null : usageResult.data);
      setEntitlements(entitlementResult.data || []);
    } catch (error) {
      console.error("SELLER DASHBOARD LOAD ERROR:", error);
      showNotice("error", errorMessage(error, "Unable to load the seller dashboard."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [sellerAccountId, shopId, showNotice]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!shop) return;
    setShopDraft({
      name: shop.name || "",
      description: shop.description || "",
      visibility: shop.visibility || "public",
    });
    setSlugDraft(shop.slug || "");
    setSlugCheck(null);
  }, [shop?.id]);

  useEffect(() => {
    if (!shopId || !slugDraft.trim()) {
      setSlugCheck(null);
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      const { data, error } = await supabase.rpc("check_shop_slug_availability", {
        p_slug: slugDraft,
        p_shop_id: shopId,
      });

      if (error) {
        setSlugCheck({ available: false, reason: errorMessage(error), normalized_slug: slugDraft });
        return;
      }

      const result = Array.isArray(data) ? data[0] : data;
      setSlugCheck(result || null);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [slugDraft, shopId]);

  const loadEbayChannel = useCallback(async () => {
    if (!shopId) return;
    setEbayChannelLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_my_ebay_channel_status", { p_shop_id: shopId });
      if (error) throw error;
      setEbayChannel(data || null);
    } catch (error) {
      setEbayChannel(null);
      showNotice("error", errorMessage(error, "Unable to load eBay connection status."));
    } finally {
      setEbayChannelLoading(false);
    }
  }, [shopId, showNotice]);

  useEffect(() => {
    if (tab === "channels" || tab === "offers") loadEbayChannel();
  }, [tab, loadEbayChannel]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const result = url.searchParams.get("ebay");
    if (!result) return;
    setTab("channels");
    if (result === "connected") showNotice("success", "eBay authorization completed. Verifying your connection…");
    else if (result === "declined") showNotice("error", "eBay authorization was declined or cancelled.");
    else showNotice("error", "eBay connection could not be completed. Please try again.");
    url.searchParams.delete("ebay");
    url.searchParams.set("tab", "channels");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [showNotice]);

  const connectEbay = async () => {
    if (!ebayChannel?.can_connect || ebayConnectBusy) return;
    setEbayConnectBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("ebay-oauth-start", { body: {} });
      if (error) {
        let message = errorMessage(error, "Unable to start eBay authorization.");
        if (error.context && typeof error.context.json === "function") {
          try { const body = await error.context.json(); if (body?.error) message = body.error; } catch (_) { /* preserve original */ }
        }
        throw new Error(message);
      }
      const authorizationUrl = data?.authorization_url;
      const target = new URL(authorizationUrl);
      if (target.protocol !== "https:" || target.hostname !== "auth.ebay.com") {
        throw new Error("Unexpected eBay authorization destination.");
      }
      window.location.assign(target.href);
    } catch (error) {
      showNotice("error", errorMessage(error, "Unable to start eBay authorization."));
      setEbayConnectBusy(false);
    }
  };

  // Read-only seller-authenticated permission check; never handles eBay secrets in React.
  const checkEbayPermissions = async () => {
    if (!isAdmin || !ebayChannel?.connected || ebayScopeBusy) return;
    setEbayScopeBusy(true);
    setEbayScopeResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("ebay-scope-check", { body: { shop_id: shopId } });
      if (error) {
        let message = errorMessage(error, "eBay permission check failed.");
        if (error.context && typeof error.context.json === "function") {
          try { const details = await error.context.json(); message = details?.message || details?.error || message; } catch (_) { /* keep message */ }
        }
        throw new Error(message);
      }
      setEbayScopeResult(data || { status: "unknown" });
      if (data?.status === "completed") showNotice("success", "eBay permission check completed.");
      else showNotice("error", data?.message || "eBay permission check requires attention.");
    } catch (error) {
      const message = errorMessage(error, "eBay permission check failed.");
      setEbayScopeResult({ status: "failed", message });
      showNotice("error", message);
    } finally {
      setEbayScopeBusy(false);
    }
  };

  // Read-only financial discovery for the known eBay test order.
  const checkEbayOrderFinancials = async () => {
    if (!isAdmin || !ebayChannel?.connected || ebayFinancialBusy) return;
    setEbayFinancialBusy(true);
    setEbayFinancialResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("ebay-order-financial-discovery", { body: { shop_id: shopId } });
      if (error) {
        let message = errorMessage(error, "eBay financial discovery failed.");
        if (error.context && typeof error.context.json === "function") {
          try {
            const details = await error.context.json();
            setEbayFinancialResult(details);
            message = details?.message || details?.error || message;
          } catch (_) { /* Keep original message. */ }
        }
        throw new Error(message);
      }
      setEbayFinancialResult(data || { status: "unknown" });
      if (data?.status === "completed") showNotice("success", "eBay order financials retrieved.");
      else showNotice("error", data?.message || "eBay financial discovery requires attention.");
    } catch (error) {
      const message = errorMessage(error, "eBay financial discovery failed.");
      setEbayFinancialResult((previous) => previous || { status: "failed", message });
      showNotice("error", message);
    } finally {
      setEbayFinancialBusy(false);
    }
  };

  // Admin-only pilot: writes one server-verified eBay order's financial ledger.
  const importEbayPilotFinancials = async () => {
    if (!isAdmin || !shopId || !ebayChannel?.connected || ebayFinancialIngestBusy) return;
    if (!window.confirm("Import financial records for eBay order 22-15291-92796 into Hobbli?\n\nThis writes financial ledger records only. It does not change inventory, listings, or payments.")) return;
    setEbayFinancialIngestBusy(true);
    setEbayFinancialIngestResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("ebay-financial-ingest", { body: { shop_id: shopId } });
      if (error) {
        let message = errorMessage(error, "eBay financial import failed.");
        let details = null;
        if (error.context && typeof error.context.json === "function") {
          try { details = await error.context.json(); message = details?.message || details?.error || message; } catch (_) { /* Preserve original error. */ }
        }
        setEbayFinancialIngestResult(details || { status: "failed", message });
        throw new Error(message);
      }
      setEbayFinancialIngestResult(data || { status: "unknown" });
      if (data?.status === "imported") showNotice("success", "eBay pilot financials imported. Repeat to check idempotency.");
      else showNotice("error", data?.reason || data?.message || "eBay financial import needs attention.");
    } catch (error) {
      const message = errorMessage(error, "eBay financial import failed.");
      setEbayFinancialIngestResult((previous) => previous || { status: "failed", message });
      showNotice("error", message);
    } finally {
      setEbayFinancialIngestBusy(false);
    }
  };

  // Read-only financial queue inspection. Seller ownership is enforced server-side.
  const inspectEbayFinancialQueue = async (quiet = false) => {
    if (!isAdmin || !shopId || !ebayChannel?.connected || ebayFinancialQueueBusy) return;
    setEbayFinancialQueueBusy(true);
    setEbayFinancialQueueResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("ebay-financial-queue-inspect", {
        body: { shop_id: shopId },
      });
      if (error) {
        let message = errorMessage(error, "Unable to inspect financial reconciliation queue.");
        if (error.context && typeof error.context.json === "function") {
          try { const details = await error.context.json(); message = details?.error || message; } catch (_) { /* keep message */ }
        }
        throw new Error(message);
      }
      setEbayFinancialQueueResult(data);
      if (!quiet) showNotice("success", `Financial queue inspected: ${data?.due_count ?? 0} due order(s).`);
    } catch (error) {
      const message = errorMessage(error);
      setEbayFinancialQueueResult({ status: "failed", error: message });
      if (!quiet) showNotice("error", message);
    } finally {
      setEbayFinancialQueueBusy(false);
    }
  };

  // Explicit manual run: processes at most one due order; no inventory or listing writes.
  const runEbayFinancialReconciliation = async (manualRetry = false) => {
    if (!isAdmin || !shopId || !ebayChannel?.connected || ebayFinancialRunBusy) return;
    const orderId = ebayFinancialManualOrder.trim();
    if (manualRetry && !/^\d{2}-\d{5}-\d{5}$/.test(orderId)) { showNotice("error", "Enter a valid eBay order ID."); return; }
    if (!window.confirm(manualRetry
      ? `Retry financial reconciliation for eBay order ${orderId} now?\n\nThis bypasses the scheduled retry time for this order, writes only financial ledger records, and does not change inventory or listings.`
      : "Reconcile one due eBay order now?\n\nThis retrieves eBay financial transactions and writes the financial ledger. It will not change inventory or listings.")) return;
    setEbayFinancialRunBusy(true);
    setEbayFinancialRunResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("ebay-financial-reconcile", { body: { shop_id: shopId, mode: manualRetry ? "retry_order" : "due", ...(manualRetry ? { external_order_id: orderId } : {}) } });
      if (error) {
        let message = errorMessage(error, "Financial reconciliation failed.");
        if (error.context && typeof error.context.json === "function") {
          try { const details = await error.context.json(); message = details?.error || details?.message || message; } catch (_) { /* preserve error */ }
        }
        throw new Error(message);
      }
      setEbayFinancialRunResult(data);
      const result = data?.results?.[0];
      if (result?.status === "current") showNotice("success", `eBay financial reconciliation saved ${result.transaction_count} transaction(s).`);
      else if (result?.status === "retry") showNotice("error", "Financial details not ready; retry scheduled. See results.");
      else if (result?.status === "failed") showNotice("error", result.error_code || "Financial reconciliation failed.");
      else showNotice("success", "No due eBay orders to reconcile.");
      await inspectEbayFinancialQueue(true);
    } catch (error) {
      const message = errorMessage(error);
      setEbayFinancialRunResult({ status: "failed", error: message });
      showNotice("error", message);
    } finally {
      setEbayFinancialRunBusy(false);
    }
  };

  // Temporary diagnostic only. Remove after eBay token refresh is verified.
  const testEbayRefresh = async () => {
    if (!ebayChannel?.connected || ebayRefreshTestBusy) return;
    setEbayRefreshTestBusy(true);
    setEbayRefreshTestResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("ebay-refresh-test", { body: {} });
      if (error) {
        let message = errorMessage(error, "eBay token refresh test failed.");
        if (error.context && typeof error.context.json === "function") {
          try {
            const body = await error.context.json();
            message = body?.message || body?.error || message;
          } catch (_) { /* Keep the original error. */ }
        }
        throw new Error(message);
      }
      setEbayRefreshTestResult(data || { status: "unknown" });
      if (data?.status === "ready" && data?.refreshed === true) {
        showNotice("success", "eBay access token refreshed successfully.");
        await loadEbayChannel();
      } else {
        showNotice("error", "eBay refresh test did not confirm a new token. Check the result below.");
      }
    } catch (error) {
      const message = errorMessage(error, "eBay token refresh test failed.");
      setEbayRefreshTestResult({ status: "failed", message });
      showNotice("error", message);
    } finally {
      setEbayRefreshTestBusy(false);
    }
  };

  // A batch is sequential, one server-verified linked listing per request.
  // The user explicitly starts each batch. Never changes rollout settings.
  const runEbayEnrichment = async (apply, batch = false) => {
    if (!shopId || !ebayChannel?.connected || !ebayChannel?.import_enabled || ebayEnrichBusy) return;
    setEbayEnrichBusy(true);
    setEbayEnrichResult(null);
    setEbayEnrichProgress(null);
    try {
      let ids = ['397496987255'];
      if (batch) {
        const { data: links, error: linksError } = await supabase.from('seller_channel_listings')
          .select('external_listing_id,metadata')
          .eq('seller_account_id', sellerAccountId)
          .eq('channel_account_id', '2899fa4a-4e2b-4381-b483-596cac836191')
          .not('external_listing_id', 'is', null)
          .order('created_at', { ascending: true });
        if (linksError) throw linksError;
        const eligible = (links || []).filter((link) => /^\d{8,20}$/.test(String(link.external_listing_id || '')));
        // Prefer listings not previously enriched. The completed pilot is left untouched.
        const pending = eligible.filter((link) => !link.metadata?.ebay_enrichment?.enriched_at);
        ids = pending.slice(0, Number(ebayEnrichBatchSize)).map((link) => String(link.external_listing_id));
        if (!ids.length) { setEbayEnrichResult({ message: 'No pending linked listings found.' }); return; }
        if (!window.confirm(`Enrich ${ids.length} linked eBay listing(s)?\n\nThis imports photos and fills blank descriptions. Existing seller descriptions and photos are preserved. Shipping details remain metadata.\n\nNo listings will be published and sync remains disabled.`)) return;
      }
      const results = [];
      const failures = [];
      let uploaded = 0, skipped = 0, descriptions = 0;
      setEbayEnrichProgress({ processed: 0, total: ids.length, uploaded, skipped, descriptions, failures: [] });
      for (const id of ids) {
        try {
          const { data, error } = await supabase.functions.invoke('ebay-inventory-enrich', {
            body: { shop_id: shopId, listing_id: id, apply },
          });
          if (error) {
            let message = errorMessage(error, 'Enrichment failed');
            if (error.context && typeof error.context.json === 'function') {
              try { const detail = await error.context.json(); message = detail?.error || detail?.message || message; } catch (_) { /* retain message */ }
            }
            throw new Error(message);
          }
          if (data?.error) throw new Error(data.error);
          uploaded += Number(data?.uploaded || 0);
          skipped += Number(data?.skipped || 0);
          if (data?.description_imported) descriptions++;
          if (data?.failures?.length) failures.push({ id, message: JSON.stringify(data.failures) });
          results.push({ id, ...data });
        } catch (error) {
          const message = errorMessage(error, 'Enrichment failed');
          failures.push({ id, message });
          if (/unauthoriz|forbidden|rollout|not enabled|authentication|permission/i.test(message)) break;
        }
        setEbayEnrichProgress({ processed: results.length + failures.filter((f) => !results.some((r) => r.id === f.id)).length, total: ids.length, uploaded, skipped, descriptions, failures: [...failures] });
      }
      setEbayEnrichResult(batch ? { processed: results.length, uploaded, skipped, descriptions_imported: descriptions, failures, listing_results: results } : (results[0] || { failures }));
      showNotice(failures.length ? 'error' : 'success', `${results.length} listing(s) processed; ${uploaded} photos uploaded, ${descriptions} descriptions added, ${failures.length} issues.`);
      if (apply) await loadDashboard(true);
    } catch (error) {
      const message = errorMessage(error, 'eBay enrichment failed.');
      setEbayEnrichResult({ error: message });
      showNotice('error', message);
    } finally {
      setEbayEnrichBusy(false);
    }
  };

  const loadChannelLinks = useCallback(async () => {
    if (!sellerAccountId) return [];
    setChannelLinksLoading(true);
    setChannelLinksLoaded(false);
    setChannelLinksError(null);
    try {
      const { data, error } = await supabase.from("seller_channel_listings")
        .select("id, channel_account_id, seller_inventory_id, external_listing_id, external_listing_url, listing_status, external_price, currency")
        .eq("seller_account_id", sellerAccountId);
      if (error) throw error;
      setChannelLinks(data || []);
      setChannelLinksLoaded(true);
      return data || [];
    } catch (error) {
      setChannelLinksError(errorMessage(error, "Unable to load eBay prices."));
      throw error;
    } finally {
      setChannelLinksLoading(false);
    }
  }, [sellerAccountId]);

  useEffect(() => {
    if (tab !== "inventory" && tab !== "channels" && tab !== "listings") return;
    loadChannelLinks().catch((error) => console.warn("Unable to load marketplace listing links", error));
  }, [tab, loadChannelLinks]);

  const previewEbayMatches = async () => {
    const rows = ebayTradingResult?.trading?.rows || [];
    if (ebayTradingResult?.status !== "ready" || !rows.length || ebayPreviewBusy) return;
    setEbayPreviewBusy(true);
    setEbayPreview(null);
    setEbayImportSelection(new Set());
    setEbayImportProgress(null);
    try {
      const links = await loadChannelLinks();
      const existingById = new Map(inventory.map((item) => [String(item.id), item]));
      const linkedByExternalId = new Map(links.filter((link) =>
        String(link.channel_account_id) === "2899fa4a-4e2b-4381-b483-596cac836191" && link.external_listing_id
      ).map((link) => [String(link.external_listing_id), link]));
      const preview = rows.map((row) => {
        const link = linkedByExternalId.get(String(row.item_id));
        if (link) return { ...row, match_state: "linked", match_item: existingById.get(String(link.seller_inventory_id)) || null };
        const ranked = inventory.map((item) => ({ item, score: ebaySuggestionScore(row.title, item.title) }))
          .filter((candidate) => candidate.score >= 0.55)
          .sort((a, b) => b.score - a.score);
        return ranked.length ? {
          ...row, match_state: "review", match_item: ranked[0].item,
          match_score: ranked[0].score, candidate_count: ranked.length,
        } : { ...row, match_state: "unmatched", match_item: null };
      });
      setEbayPreview(preview);
      setEbayPreviewFilter("all");
      showNotice("success", `Compared ${preview.length} eBay listings with ${inventory.length} Hobbli inventory records. No changes made.`);
    } catch (error) {
      showNotice("error", errorMessage(error, "Unable to build matching preview."));
    } finally {
      setEbayPreviewBusy(false);
    }
  };

  // Batch orchestration deliberately reuses the server-verified single-listing importer.
  // Never send browser-provided listing details to the database.
  const importSelectedEbayListing = async () => {
    if (!ebayChannel?.connected || !ebayChannel?.import_enabled || ebayImportBusy || !channelLinksLoaded) return;
    if (ebayTradingResult?.status !== "ready" || ebayTradingResult?.trading?.has_more || !ebayPreview) {
      showNotice("error", "Complete eBay discovery and matching preview before importing.");
      return;
    }
    const selected = ebayPreview.filter((row) =>
      row.match_state === "unmatched" && ebayImportSelection.has(String(row.item_id)) && /^\d{8,20}$/.test(String(row.item_id || ""))
    );
    if (!selected.length || selected.length !== ebayImportSelection.size) {
      showNotice("error", "Select only unmatched listings with valid eBay IDs.");
      return;
    }
    if (!window.confirm(`Import ${selected.length} eBay listing${selected.length === 1 ? "" : "s"} into Hobbli inventory?\n\nEach listing is reverified against eBay. This does NOT publish on Hobbli or enable automatic synchronization.\n\nPossible inventory matches are excluded. Importing may take a few minutes.`)) return;
    setEbayImportBusy(true);
    let imported = 0;
    let already = 0;
    const failures = [];
    const completed = new Set();
    setEbayImportProgress({ total: selected.length, processed: 0, imported: 0, already: 0, failures: [] });
    try {
      for (const row of selected) {
        const id = String(row.item_id);
        try {
          const { data, error } = await supabase.functions.invoke("ebay-inventory-import", {
            body: { listing_ids: [id], shop_id: shopId },
          });
          if (error) {
            let message = errorMessage(error, "Import failed");
            if (error.context && typeof error.context.json === "function") {
              try { const body = await error.context.json(); message = body?.error || body?.message || message; } catch (_) { /* retain error */ }
            }
            throw new Error(message);
          }
          if (data?.status === "imported") imported++;
          else if (data?.status === "already_imported") already++;
          else throw new Error(data?.error || "Server did not confirm import");
          completed.add(id);
        } catch (error) {
          failures.push({ id, message: errorMessage(error, "Import failed") });
          // Stop on an authorization/rollout failure rather than hammering the endpoint.
          if (/unauthoriz|forbidden|rollout|not enabled|authentication|permission/i.test(failures[failures.length - 1].message)) {
            setEbayImportProgress({ total: selected.length, processed: imported + already + failures.length, imported, already, failures: [...failures], stopped: true });
            break;
          }
        }
        setEbayImportProgress({ total: selected.length, processed: imported + already + failures.length, imported, already, failures: [...failures] });
      }
      // Preserve failed selections for a deliberate retry; update preview from fresh database links.
      await loadDashboard(true);
      const links = await loadChannelLinks();
      const linkedIds = new Set(links.filter((link) => String(link.channel_account_id) === "2899fa4a-4e2b-4381-b483-596cac836191").map((link) => String(link.external_listing_id)));
      setEbayPreview((previous) => previous?.map((row) => linkedIds.has(String(row.item_id))
        ? { ...row, match_state: "linked", match_item: null }
        : row) || null);
      setEbayImportSelection(new Set(failures.map((failure) => failure.id)));
      showNotice(failures.length ? "error" : "success", `${imported} imported, ${already} already linked, ${failures.length} failed. ${failures.length ? "Review failures before retrying." : "No Hobbli listings were published."}`);
    } catch (error) {
      showNotice("error", errorMessage(error, "Could not refresh inventory after import."));
    } finally {
      setEbayImportBusy(false);
    }
  };

  const runEbayReconciliation = async () => {
    if (!ebayChannel?.connected || !shopId || ebayReconcileBusy) return;
    setEbayReconcileBusy(true);
    setEbayReconcileResult(null);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (sessionError || !accessToken) {
        throw new Error("Your Hobbli login session is missing or expired. Please sign in again.");
      }
      const { data, error } = await supabase.functions.invoke("ebay-sync-request", {
        body: { shop_id: shopId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (error) {
        let message = errorMessage(error, "Unable to run eBay reconciliation.");
        if (error.context && typeof error.context.json === "function") {
          try { const detail = await error.context.json(); message = detail?.error || message; } catch (_) { /* keep original */ }
        }
        throw new Error(message);
      }
      if (!data || data.error) throw new Error(data?.error || "Empty reconciliation response");
      setEbayReconcileResult(data);
      showNotice(data.status === "completed" ? "success" : "error",
        data.status === "completed" ? "Read-only eBay reconciliation finished." : "Reconciliation needs review; see results below.");
    } catch (error) {
      const message = errorMessage(error, "eBay reconciliation failed.");
      setEbayReconcileResult({ status: "failed", error: message });
      showNotice("error", message);
    } finally {
      setEbayReconcileBusy(false);
    }
  };

  // App-wide destination registration through the existing seller-authenticated
  // server bridge. The backend independently verifies administrator privileges.
  const manageEbayNotificationDestination = async (mode) => {
    if (!isAdmin || !ebayChannel?.connected || !shopId || ebayNotificationRegisterBusy) return;
    if (mode === 'test_subscription' && !/^[A-Za-z0-9_-]{1,128}$/.test(ebaySubscriptionId.trim())) { showNotice('error', 'Enter a subscription ID from the inspection result.'); return; }
    if (mode === 'test_subscription' && !window.confirm('Request an eBay-generated test notification for this existing subscription? No inventory mutation is requested.')) return;
    if (mode === 'activate_subscriptions' && !window.confirm('Enable LIVE eBay ORDER_CONFIRMATION and OFFER_ACTIVITY notifications? eBay may immediately send real events to Hobbli. Continue?')) return;
    if (mode === 'register' && !window.confirm('Register Hobbli’s eBay notification destination? This will create a destination at eBay if none exists. No subscriptions or inventory will change.')) return;
    setEbayNotificationRegisterBusy(true);
    setEbayNotificationRegisterResult(null);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (sessionError || !accessToken) throw new Error('Please sign in to Hobbli again.');
      const { data, error } = await supabase.functions.invoke('ebay-sync-request', {
        body: { shop_id: shopId, action: mode === 'register' ? 'notification_destination_register' : mode === 'inspect_subscriptions' ? 'notification_subscriptions_inspect' : mode === 'test_subscription' ? 'notification_subscription_test' : mode === 'activate_subscriptions' ? 'notification_subscriptions_activate' : 'notification_destination_inspect', ...(mode === 'test_subscription' ? { subscription_id: ebaySubscriptionId.trim() } : {}) },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (error) {
        let message = errorMessage(error, 'Notification destination request failed.');
        if (error.context?.json) {
          try { const details = await error.context.json(); setEbayNotificationRegisterResult(details); message = details?.error || details?.message || message; }
          catch (_) { /* Preserve fallback. */ }
        }
        throw new Error(message);
      }
      setEbayNotificationRegisterResult(data || { status: 'unknown' });
      if (['registered', 'already_registered', 'ready_to_register', 'activated', 'inspected'].includes(data?.status)) {
        showNotice('success', `eBay notification destination: ${data.status.replaceAll('_', ' ')}.`);
      } else {
        showNotice('error', data?.error || 'Notification destination needs attention.');
      }
    } catch (error) {
      const message = errorMessage(error, 'Notification destination request failed.');
      setEbayNotificationRegisterResult((previous) => previous || { status: 'failed', error: message });
      showNotice('error', message);
    } finally {
      setEbayNotificationRegisterBusy(false);
    }
  };

  // Admin-only queue test. The seller-authenticated bridge must implement
  // process_discovery_queue and verify seller/account ownership server-side.
  // No service-role or scheduler secrets are sent from the browser.
  const testEbayDiscoveryQueue = async () => {
    if (!isAdmin || !ebayChannel?.connected || !shopId || ebayQueueTestBusy) return;
    setEbayQueueTestBusy(true);
    setEbayQueueTestResult(null);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (sessionError || !accessToken) throw new Error("Please sign in to Hobbli again.");
      const { data, error } = await supabase.functions.invoke("ebay-sync-request", {
        body: { shop_id: shopId, action: "process_discovery_queue", max_events: 1 },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (error) {
        let message = errorMessage(error, "Unable to test eBay discovery queue.");
        if (error.context && typeof error.context.json === "function") {
          try { const details = await error.context.json(); message = details?.error || details?.message || message; }
          catch (_) { /* retain original */ }
        }
        throw new Error(message);
      }
      if (!data || data.error || data.status === "failed") throw new Error(data?.error || "Queue test failed.");
      setEbayQueueTestResult(data);
      showNotice("success", `Discovery queue test finished: ${data.claimed ?? 0} event(s) claimed.`);
    } catch (error) {
      const message = errorMessage(error, "Discovery queue test failed.");
      setEbayQueueTestResult({ status: "failed", error: message });
      showNotice("error", message);
    } finally {
      setEbayQueueTestBusy(false);
    }
  };

  // Read-only order discovery through the authenticated seller bridge.
  const runEbayOrderDiscovery = async () => {
    if (!ebayChannel?.connected || !shopId || ebayOrderDiscoveryBusy) return;
    setEbayOrderDiscoveryBusy(true);
    setEbayOrderDiscoveryResult(null);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (sessionError || !accessToken) throw new Error("Your Hobbli login session is missing or expired. Please sign in again.");
      const { data, error } = await supabase.functions.invoke("ebay-sync-request", {
        body: { shop_id: shopId, action: "discover_orders", save_observations: true },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (error) {
        let message = errorMessage(error, "Unable to discover eBay orders.");
        if (error.context && typeof error.context.json === "function") {
          try { const detail = await error.context.json(); message = detail?.error || detail?.message || message; } catch (_) { /* retain original */ }
        }
        throw new Error(message);
      }
      if (!data || data.error) throw new Error(data?.error || "Empty order discovery response");
      setEbayOrderDiscoveryResult(data);
      showNotice("success", `eBay history saved: ${data?.saved_observations?.processed ?? 0} transaction line(s) processed, ${data?.saved_observations?.failed ?? 0} failed.`);
    } catch (error) {
      const message = errorMessage(error, "eBay order discovery failed.");
      setEbayOrderDiscoveryResult({ error: message, status: "failed" });
      showNotice("error", message);
    } finally {
      setEbayOrderDiscoveryBusy(false);
    }
  };

  // Seller-scoped RPC: the browser never reads private tables or buyer fingerprints directly.
  const loadEbayNegotiations = async () => {
    if (!shopId) return;
    try {
      const { data, error } = await supabase.rpc("get_my_ebay_negotiations", { p_shop_id: shopId });
      if (error) throw error;
      setEbayNegotiationRows(Array.isArray(data) ? data : []);
      setEbayNegotiationError(null);
    } catch (error) {
      setEbayNegotiationError(errorMessage(error, "Negotiation history is unavailable."));
    }
  };

  // eBay Trading API can omit offer timestamps. For a single counter cycle,
  // reconstruct the directionally valid sequence rather than sorting by UUID.
  // For longer/ambiguous histories, keep a deterministic order without inventing dates.
  const orderEbayNegotiationSteps = (steps) => {
    const rank = (e) => e.offer_type === "BuyerBestOffer" ? 0
      : e.offer_type === "SellerCounterOffer" ? 1
      : e.offer_type === "BuyerCounterOffer" ? 2
      : e.event_type === "seller_counter" ? 1 : 3;
    return [...steps].sort((a, b) => {
      const aTime = Date.parse(a.occurred_at || a.offered_at || "");
      const bTime = Date.parse(b.occurred_at || b.offered_at || "");
      if (Number.isFinite(aTime) && Number.isFinite(bTime)) return aTime - bTime;
      const aId = String(a.external_offer_id || "");
      const bId = String(b.external_offer_id || "");
      // Offer IDs are not authoritative timestamps, but can distinguish
      // repeated cycles where eBay omits the event time.
      if (aId && bId && aId !== bId && /^\d+$/.test(aId) && /^\d+$/.test(bId)) {
        return BigInt(aId) < BigInt(bId) ? -1 : 1;
      }
      return rank(a) - rank(b) || aId.localeCompare(bId);
    });
  };

  const ebayNegotiationGroups = useMemo(() => {
    const groups = new Map();
    for (const row of ebayNegotiationRows) {
      if (!row.thread_id || !row.external_listing_id) continue;
      if (!groups.has(row.thread_id)) groups.set(row.thread_id, {
        id: row.thread_id, listingId: String(row.external_listing_id), events: [],
        lastSeen: row.last_seen_at,
      });
      if (row.event_id) groups.get(row.thread_id).events.push(row);
    }
    return [...groups.values()].map((group) => {
      const observed = group.events.filter((e) => e.event_source === "ebay_observation");
      const actions = group.events.filter((e) => e.event_source === "hobbli_action");
      const successful = actions.filter((e) => String(e.event_status).toLowerCase() === "succeeded");
      const failed = actions.filter((e) => String(e.event_status).toLowerCase() !== "succeeded");
      // eBay observations are the authoritative visible steps. Hobbli action
      // attempts are shown separately in the action audit; no speculative joins.
      const timeline = observed.map((e) => ({ ...e, displayKey: e.event_id }));
      if (!observed.some((e) => e.event_type === "seller_counter")) {
        for (const action of successful) timeline.push({ ...action, displayKey: action.event_id });
      }
      const orderedObserved = orderEbayNegotiationSteps(observed);
      const orderedTimeline = orderEbayNegotiationSteps(timeline);
      const latest = orderedObserved[orderedObserved.length - 1];
      const hasCounter = observed.some((e) => e.event_type === "seller_counter") || successful.some((e) => e.event_type === "seller_counter");
      const hasBuyerCounter = observed.some((e) => e.offer_type === "BuyerCounterOffer");
      const latestBuyerCounter = [...orderedObserved].reverse().find((e) => e.offer_type === "BuyerCounterOffer");
      const status = latestBuyerCounter ? (String(latestBuyerCounter.event_status).toLowerCase() === "expired" ? "Buyer counter expired" : "Buyer counter received") :
        hasCounter ? "Seller counter sent" : "Buyer offer received";
      return { ...group, observed: orderedObserved, failed, timeline: orderedTimeline, latest, status, hasCounter, hasBuyerCounter, isClosed: latestBuyerCounter ? ["expired", "declined", "accepted", "withdrawn", "cancelled"].includes(String(latestBuyerCounter.event_status).toLowerCase()) : ["expired", "declined", "accepted", "withdrawn", "cancelled"].includes(String(latest?.event_status || "").toLowerCase()) };
    });
  }, [ebayNegotiationRows]);

  // Every saved eBay offer is independently visible and actionable. A negotiation
  // thread is context, not a substitute for an offer row (and never a buyer ID).
  const ebayOffersGrouped = useMemo(() => {
    const all = ebayOfferResult?.offers || [];
    const groupsByOffer = new Map();
    for (const group of ebayNegotiationGroups) {
      for (const event of group.observed) {
        if (!event.external_offer_id) continue;
        const key = String(event.external_offer_id);
        if (!groupsByOffer.has(key)) groupsByOffer.set(key, group);
      }
    }
    const rows = all.map((offer) => {
      const group = groupsByOffer.get(String(offer.offer_id)) || null;
      const priorBuyerOffers = group ? new Set(group.observed
        .filter((event) => event.event_type === 'buyer_offer' && event.external_offer_id && String(event.external_offer_id) !== String(offer.offer_id))
        .map((event) => String(event.external_offer_id))) : new Set();
      return {
        kind: 'offer', key: `ebay-offer-${offer.saved_offer_id || offer.offer_id}`,
        offer, group, buyerRelationship: group && priorBuyerOffers.size ? 'Previous activity in this verified thread' : 'Buyer relationship unverified',
      };
    });
    // Keep historical threads visible even when the corresponding saved offer
    // is missing. Such rows are never actionable.
    const covered = new Set(rows.map((row) => row.group?.id).filter(Boolean));
    for (const group of ebayNegotiationGroups) {
      if (!covered.has(group.id)) rows.push({ kind: 'history', key: `ebay-history-${group.id}`, group, offer: null, buyerRelationship: 'Buyer relationship unverified' });
    }
    const active = (offer) => ebayOfferCanCounter(offer);
    const timestamp = (offer) => Date.parse(offer?.created_at || offer?.updated_at || offer?.offered_at || '') || 0;
    return rows.sort((a, b) => Number(active(b.offer)) - Number(active(a.offer)) || timestamp(b.offer) - timestamp(a.offer));
  }, [ebayOfferResult, ebayNegotiationGroups]);

  // Render saved Supabase offers on opening; eBay API is only called by explicit refresh.
  const loadSavedEbayOffers = async () => {
    if (!shopId) return;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) return;
      const { data, error } = await supabase.functions.invoke("ebay-sync-request", {
        body: { shop_id: shopId, action: "list_saved_offers" },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (error || data?.error) throw new Error(data?.error || "Unable to read saved eBay offers");
      setEbayOfferResult(data);
    } catch (error) {
      setEbayOfferResult((previous) => previous || { status: "failed", error: errorMessage(error, "Unable to load saved eBay offers") });
    }
  };

  // Buyer offers: read-only lookup via seller-authenticated eBay bridge.
  const runEbayOfferDiscovery = async (auto = false) => {
    if (!ebayChannel?.connected || !shopId || ebayOfferBusy) return;
    const itemId = auto ? "" : ebayOfferItemId.trim();
    if (itemId && !/^\d{1,19}$/.test(itemId)) {
      showNotice("error", "Enter a valid numeric eBay listing ID.");
      return;
    }
    setEbayOfferBusy(true);
    setEbayOfferResult(null);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (sessionError || !accessToken) throw new Error("Please sign in to Hobbli again.");
      const { data, error } = await supabase.functions.invoke("ebay-sync-request", {
        body: { shop_id: shopId, action: "discover_offers", ...(itemId ? { item_id: itemId } : {}) },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (error) {
        let message = errorMessage(error, "Unable to retrieve eBay offers.");
        if (error.context && typeof error.context.json === "function") {
          try { const detail = await error.context.json(); message = detail?.diagnostic || detail?.error || detail?.message || message; } catch (_) { /* retain original */ }
        }
        throw new Error(message);
      }
      if (!data || data.error) throw new Error(data?.diagnostic || data?.error || "Empty offer response");
      await loadSavedEbayOffers();
      await loadEbayNegotiations();
      setEbayCounterOffer(null);
      if (!auto) showNotice(data.status === "incomplete" ? "error" : "success", `Refreshed ${data.offers_observed ?? (data.offers || []).length} eBay offer(s) and updated saved records.`);
    } catch (error) {
      const message = errorMessage(error, "Offer discovery failed.");
      setEbayOfferResult({ status: "failed", error: message });
      if (!auto) showNotice("error", message);
    } finally {
      setEbayOfferBusy(false);
    }
  };

  // Read-only, seller-authenticated eBay buyer relationship diagnostic.
  const diagnoseEbayOfferBuyers = async () => {
    if (!ebayChannel?.connected || !shopId || ebayBuyerDiagnosticBusy) return;
    const listingId = ebayOfferItemId.trim() || "398410938261";
    if (!/^\d{1,19}$/.test(listingId)) {
      showNotice("error", "Enter a valid numeric eBay listing ID.");
      return;
    }
    setEbayBuyerDiagnosticBusy(true);
    setEbayBuyerDiagnosticResult(null);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (sessionError || !token) throw new Error("Please sign in to Hobbli again.");
      const { data, error } = await supabase.functions.invoke("ebay-sync-request", {
        body: { shop_id: shopId, action: "inspect_offer_relationship", listing_id: listingId },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (error) {
        let message = errorMessage(error, "Buyer relationship diagnostic failed.");
        if (error.context && typeof error.context.json === "function") {
          try { const detail = await error.context.json(); message = detail?.error || detail?.message || message; } catch (_) { /* retain message */ }
        }
        throw new Error(message);
      }
      if (!data || data.error) throw new Error(data?.error || "Empty diagnostic response.");
      setEbayBuyerDiagnosticResult(data);
      showNotice("success", "Buyer relationship diagnostic completed.");
    } catch (error) {
      const message = errorMessage(error, "Buyer relationship diagnostic failed.");
      setEbayBuyerDiagnosticResult({ error: message });
      showNotice("error", message);
    } finally {
      setEbayBuyerDiagnosticBusy(false);
    }
  };

  const sendEbayCounter = async () => {
    if (!ebayCounterOffer || ebayCounterBusy) return;
    if (!ebayOfferCanCounter(ebayCounterOffer)) {
      setEbayCounterOffer(null);
      showNotice("error", "This offer is no longer eligible for a counter. Refresh its status from eBay.");
      return;
    }
    const amount = Number(ebayCounterAmount);
    if (!Number.isFinite(amount) || amount <= Number(ebayCounterOffer.amount) || (ebayCounterOffer.asking_price != null && amount > Number(ebayCounterOffer.asking_price)) || !/^\d+(\.\d{1,2})?$/.test(ebayCounterAmount.trim())) { showNotice("error", "Enter a valid counter above the buyer offer and no higher than the asking price."); return; }
    if (!window.confirm(`Send a REAL eBay counteroffer?\n\nItem: ${inventory.find((i) => String(i.id) === String(ebayCounterOffer.seller_inventory_id))?.title || "eBay listing " + ebayCounterOffer.listing_id}\nBuyer offered: ${money(ebayCounterOffer.amount, ebayCounterOffer.currency || "USD")}\nYour counter: ${money(amount, ebayCounterOffer.currency || "USD")}\n\nThis will contact the buyer on eBay.`)) return;
    setEbayCounterBusy(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) throw new Error("Sign in again.");
      const { data, error } = await supabase.functions.invoke("ebay-sync-request", { body: { shop_id: shopId, action: "counter_offer", offer_id: ebayCounterOffer.saved_offer_id, counter_amount: amount }, headers: { Authorization: `Bearer ${accessToken}` } });
      if (error || data?.error || data?.status !== "succeeded") {
        let message = data?.diagnostic || data?.error || errorMessage(error, "Counter could not be confirmed.");
        if (error?.context?.json) { try { const detail = await error.context.json(); message = detail?.diagnostic || detail?.error || message; } catch (_) {} }
        throw new Error(message);
      }
      setEbayCounterOffer(null); setEbayCounterAmount("");
      showNotice("success", "Counteroffer sent to eBay successfully.");
      await loadSavedEbayOffers();
    } catch (error) { showNotice("error", errorMessage(error, "Counter result uncertain. Verify on eBay before retrying.")); }
    finally { setEbayCounterBusy(false); }
  };

  const acceptEbayOffer = async (offer) => {
    if (!offer?.saved_offer_id || ebayAcceptBusy || ebayDeclineBusy || ebayCounterBusy || !ebayOfferCanCounter(offer)) {
      showNotice("error", "This offer is not eligible for acceptance. Refresh eBay offers first.");
      return;
    }
    const itemTitle = inventory.find((i) => String(i.id) === String(offer.seller_inventory_id))?.title || `eBay listing ${offer.listing_id}`;
    if (!window.confirm(`ACCEPT this REAL eBay offer?\n\n${itemTitle}\nBuyer offer: ${money(offer.amount, offer.currency || "USD")}\n\nThis commits the item to the eBay buyer and reserves it in Hobbli. Payment may still be pending. Do not accept if you have already sold the item elsewhere.`)) return;
    setEbayAcceptBusy(offer.saved_offer_id);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (sessionError || !accessToken) throw new Error("Sign in again.");
      const { data, error } = await supabase.functions.invoke("ebay-sync-request", {
        body: { shop_id: shopId, action: "accept_offer", offer_id: offer.saved_offer_id },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (error || data?.error || data?.status !== "succeeded") {
        let message = data?.error || errorMessage(error, "Acceptance could not be confirmed.");
        if (error?.context?.json) { try { const detail = await error.context.json(); message = detail?.error || detail?.diagnostic || message; } catch (_) {} }
        throw new Error(`${message} Check eBay before any retry; inventory may be held.`);
      }
      showNotice("success", "Offer accepted on eBay. Inventory reserved; payment is not yet confirmed.");
      await Promise.all([loadSavedEbayOffers(), loadEbayNegotiations()]);
    } catch (error) {
      showNotice("error", errorMessage(error, "Acceptance uncertain. Verify on eBay before retrying."));
    } finally { setEbayAcceptBusy(null); }
  };

  const declineEbayOffer = async (offer) => {
    if (!offer?.saved_offer_id || ebayDeclineBusy || !ebayOfferCanCounter(offer)) {
      showNotice("error", "This offer is not eligible for a response. Refresh eBay offers first.");
      return;
    }
    const itemTitle = inventory.find((i) => String(i.id) === String(offer.seller_inventory_id))?.title || `eBay listing ${offer.listing_id}`;
    if (!window.confirm(`Decline this REAL eBay buyer offer?\n\n${itemTitle}\nBuyer offer: ${money(offer.amount, offer.currency || "USD")}\n\nThis will notify eBay that you declined the selected offer. It cannot be undone in Hobbli.`)) return;
    setEbayDeclineBusy(offer.saved_offer_id);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (sessionError || !accessToken) throw new Error("Sign in again.");
      const { data, error } = await supabase.functions.invoke("ebay-sync-request", {
        body: { shop_id: shopId, action: "decline_offer", offer_id: offer.saved_offer_id },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (error || data?.error || data?.status !== "succeeded") {
        let message = data?.error || errorMessage(error, "Decline result could not be confirmed.");
        if (error?.context?.json) { try { const detail = await error.context.json(); message = detail?.error || detail?.diagnostic || message; } catch (_) {} }
        throw new Error(`${message} If the result is uncertain, verify on eBay before retrying.`);
      }
      showNotice("success", "Buyer offer declined on eBay.");
      await Promise.all([loadSavedEbayOffers(), loadEbayNegotiations()]);
    } catch (error) {
      showNotice("error", errorMessage(error, "Decline not confirmed. Verify on eBay before retrying."));
    } finally {
      setEbayDeclineBusy(null);
    }
  };

  // One saved-offers database load on each visit to Offers; no eBay API call.
  useEffect(() => {
    if (tab !== "offers") {
      ebayOfferAutoFetched.current = false;
      return;
    }
    if (!shopId || !ebayChannel?.connected || ebayOfferAutoFetched.current) return;
    ebayOfferAutoFetched.current = true;
    loadSavedEbayOffers();
    loadEbayNegotiations();
  // Intentionally keyed to tab, shop, and connection state, not changing request state.
  }, [tab, shopId, ebayChannel?.connected]);

  // Admin-only read-only notification configuration diagnostic.
  const checkEbayNotificationPreferences = async () => {
    if (!isAdmin || !ebayChannel?.connected || !shopId || ebayPreferencesBusy) return;
    setEbayPreferencesBusy(true);
    setEbayPreferencesResult(null);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (sessionError || !accessToken) throw new Error("Please sign in again.");
      const { data, error } = await supabase.functions.invoke("ebay-sync-request", {
        body: { shop_id: shopId, action: "notification_preferences" },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (error) {
        let message = errorMessage(error, "Unable to check eBay notification preferences.");
        if (error.context && typeof error.context.json === "function") {
          try { const detail = await error.context.json(); message = detail?.error || detail?.message || message; } catch (_) { /* preserve original */ }
        }
        throw new Error(message);
      }
      if (!data || data.error) throw new Error(data?.error || "Empty notification preferences response");
      setEbayPreferencesResult(data);
      showNotice("success", "eBay notification preferences retrieved (read only).");
    } catch (error) {
      const message = errorMessage(error, "eBay notification preferences failed.");
      setEbayPreferencesResult({ status: "failed", error: message });
      showNotice("error", message);
    } finally {
      setEbayPreferencesBusy(false);
    }
  };

  const discoverEbayTrading = async () => {
    if (!ebayChannel?.connected || ebayTradingBusy) return;
    setEbayTradingBusy(true);
    setEbayTradingResult(null);
    setEbayPreview(null);
    setEbayImportSelection(new Set());
    setEbayImportProgress(null);
    try {
      const { data, error } = await supabase.functions.invoke("ebay-trading-discovery", { body: {} });
      if (error) {
        let message = errorMessage(error, "Trading API discovery failed.");
        if (error.context && typeof error.context.json === "function") {
          try { const body = await error.context.json(); message = body?.error || message; } catch (_) { /* keep original */ }
        }
        throw new Error(message);
      }
      setEbayTradingResult(data || { status: "unknown" });
      showNotice(data?.status === "ready" ? "success" : "error", data?.status === "ready" ? "Trading API discovery completed." : "Trading API returned an error; see results below.");
    } catch (error) {
      const message = errorMessage(error, "Trading API discovery failed.");
      setEbayTradingResult({ status: "failed", error: message });
      showNotice("error", message);
    } finally {
      setEbayTradingBusy(false);
    }
  };

  const discoverEbayListings = async () => {
    if (!ebayChannel?.connected || ebayDiscoveryBusy) return;
    setEbayDiscoveryBusy(true);
    setEbayDiscoveryResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("ebay-listing-discovery", { body: {} });
      if (error) {
        let message = errorMessage(error, "eBay discovery failed.");
        if (error.context && typeof error.context.json === "function") {
          try { const body = await error.context.json(); message = body?.error || message; } catch (_) { /* use original */ }
        }
        throw new Error(message);
      }
      setEbayDiscoveryResult(data || { status: "unknown" });
      showNotice(data?.status === "ready" ? "success" : "error", data?.status === "ready" ? "eBay read-only discovery completed." : "eBay discovery returned partial results.");
    } catch (error) {
      const message = errorMessage(error, "eBay discovery failed.");
      setEbayDiscoveryResult({ status: "failed", error: message });
      showNotice("error", message);
    } finally {
      setEbayDiscoveryBusy(false);
    }
  };

  const connectPayPal = async () => {
    if (!shopId) return;
    setPaypalBusy(true);
    setPaypalResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("paypal-marketplace", {
        body: { action: "create-onboarding", shopId },
      });
      if (error) throw error;
      if (!data?.success || !data?.onboardingUrl) {
        throw new Error(data?.error || "PayPal onboarding could not be started.");
      }
      window.location.assign(data.onboardingUrl);
    } catch (error) {
      let message = errorMessage(error, "PayPal onboarding could not be started.");
      const response = error?.context;
      if (response && typeof response.json === "function") {
        try {
          const body = await response.json();
          if (body?.error) message = body.error;
        } catch {
          // Keep the safe client error message.
        }
      }
      setPaypalResult({ type: "error", message });
      setPaypalBusy(false);
    }
  };

  const refreshPayPalAccount = useCallback(async ({ fromReturn = false } = {}) => {
    if (!shopId) return;
    setPaypalBusy(true);
    setPaypalResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("paypal-marketplace", {
        body: { action: "refresh-account", shopId },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "PayPal status could not be refreshed.");

      setPaypalResult({
        type: data.paymentsEnabled ? "success" : "info",
        message: data.paymentsEnabled
          ? "PayPal is connected and ready to receive marketplace payments."
          : "PayPal returned your seller account, but onboarding is not fully payment-ready yet.",
      });

      const { data: checkoutOptions, error: checkoutError } = await supabase.rpc(
        "get_shop_checkout_options",
        { p_shop_id: shopId }
      );
      if (!checkoutError) {
        setPaymentAccounts(
          Array.isArray(checkoutOptions?.providers) ? checkoutOptions.providers : []
        );
      }

      if (fromReturn) {
        const url = new URL(window.location.href);
        url.searchParams.delete("paypal");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }
    } catch (error) {
      let message = errorMessage(error, "PayPal status could not be refreshed.");
      const response = error?.context;
      if (response && typeof response.json === "function") {
        try {
          const body = await response.json();
          if (body?.error) message = body.error;
        } catch {
          // Keep the safe client error message.
        }
      }
      setPaypalResult({ type: "error", message });
    } finally {
      setPaypalBusy(false);
    }
  }, [shopId]);

  const loadCommercePanel = useCallback(async () => {
    if (!shopId) return;
    setCommerceLoading(true);
      try {
        const [ordersResult, ebayOrdersResult, offersResult, paymentsResult, feeResult, planResult] = await Promise.all([
          supabase.rpc("get_my_seller_orders"),
          supabase.rpc("get_my_ebay_order_summaries", { p_shop_id: shopId }),
        supabase.from("marketplace_offer_threads").select("*").order("created_at", { ascending: false }).limit(100),
        supabase.rpc("get_shop_checkout_options", { p_shop_id: shopId }),
        supabase.rpc("get_my_shop_fee_policy", { p_shop_id: shopId }),
        supabase.rpc("get_my_seller_plan_access", { p_shop_id: shopId }),
      ]);
        
        if (ordersResult.error) {
          console.error("SELLER ORDERS LOAD ERROR:", ordersResult.error);
        }

      if (!ordersResult.error) {
        const shopOrders = (ordersResult.data || []).filter((row) =>
          [row.shop_id, row.seller_shop_id].filter(Boolean).some((id) => String(id) === String(shopId))
        );
        setSellerOrders(shopOrders);
        // Snapshot titles are stable even if the underlying inventory/listing changes.
        // Read only the line items for orders owned by this shop (subject to order_items RLS).
        const orderIds = [...new Set(shopOrders.map((row) => row.id).filter(Boolean))];
        const titlesByOrder = {};
        let titleReadFailed = false;
        for (let offset = 0; offset < orderIds.length; offset += 100) {
          const ids = orderIds.slice(offset, offset + 100);
          const { data: lines, error: linesError } = await supabase
            .from("order_items")
            .select("seller_order_id,title_snapshot,quantity")
            .eq("shop_id", shopId)
            .in("seller_order_id", ids)
            .order("created_at", { ascending: true })
            .limit(1000);
          if (linesError) {
            console.error("HOBBLI ORDER TITLES LOAD ERROR:", linesError);
            titleReadFailed = true;
            break;
          }
          for (const line of lines || []) {
            if (!line.seller_order_id || !line.title_snapshot) continue;
            const key = String(line.seller_order_id);
            (titlesByOrder[key] ||= []).push(line.title_snapshot);
          }
        }
        setHobbliOrderTitles(titleReadFailed ? {} : titlesByOrder);
      }
      if (ebayOrdersResult.error) {
        console.error("EBAY ORDERS LOAD ERROR:", ebayOrdersResult.error);
      } else {
        setEbayOrders(ebayOrdersResult.data || []);
      }
      if (!offersResult.error) {
        const shopOffers = (offersResult.data || []).filter((row) =>
          [row.shop_id, row.seller_shop_id].filter(Boolean).some((id) => String(id) === String(shopId))
        );

        const listingIds = [...new Set(shopOffers.map((row) => row.listing_id).filter(Boolean))];
        let listingTitleById = new Map();

        if (listingIds.length) {
          const { data: offerListings, error: offerListingsError } = await supabase
            .from("listings")
            .select("id, seller_inventory_id, price_amount, currency")
            .in("id", listingIds);

          if (offerListingsError) {
            console.error("OFFER LISTING TITLES LOAD ERROR:", offerListingsError);
          } else {
            const inventoryIds = [...new Set((offerListings || []).map((row) => row.seller_inventory_id).filter(Boolean))];
            let inventoryById = new Map();
            let itemById = new Map();

            if (inventoryIds.length) {
              const { data: offerInventory, error: offerInventoryError } = await supabase
                .from("seller_inventory")
                .select(`
                  id, title, item_id, source_user_item_id, condition, quantity_on_hand, quantity_reserved, status,
                  inventory_images (id, storage_provider, storage_bucket, object_key, position, is_primary)
                `)
                .in("id", inventoryIds);

              if (offerInventoryError) {
                console.error("OFFER INVENTORY CONTEXT LOAD ERROR:", offerInventoryError);
              } else {
                inventoryById = new Map((offerInventory || []).map((row) => [String(row.id), row]));
                const itemIds = [...new Set((offerInventory || []).map((row) => row.item_id).filter(Boolean))];

                if (itemIds.length) {
                  const { data: offerItems, error: itemsError } = await supabase
                    .from("items")
                    .select("id, name, year, brand, set_name, card_number, subset, release_id")
                    .in("id", itemIds);

                  if (itemsError) console.error("OFFER CANONICAL ITEM LOAD ERROR:", itemsError);
                  else itemById = new Map((offerItems || []).map((row) => [String(row.id), row]));
                }
              }
            }

            listingTitleById = new Map((offerListings || []).map((row) => {
              const inventoryRow = inventoryById.get(String(row.seller_inventory_id)) || null;
              return [String(row.id), {
                title: inventoryRow?.title || null,
                listing_price: row.price_amount,
                listing_currency: row.currency,
                inventory: inventoryRow,
                canonical_item: inventoryRow?.item_id ? itemById.get(String(inventoryRow.item_id)) || null : null,
                market_snapshot: null,
                market_sales: [],
              }];
            }));
          }
        }

        setOfferThreads(shopOffers.map((row) => {
          const context = listingTitleById.get(String(row.listing_id)) || {};
          return {
            ...row,
            listing_title: context.title || null,
            listing_price: context.listing_price ?? null,
            listing_currency: context.listing_currency || row.currency || "USD",
            offer_inventory: context.inventory || null,
            canonical_item: context.canonical_item || null,
            market_snapshot: context.market_snapshot || null,
            market_sales: context.market_sales || [],
          };
        }));
      }
      if (!paymentsResult.error) {
        const checkoutOptions = paymentsResult.data || {};
        setPaymentAccounts(Array.isArray(checkoutOptions?.providers) ? checkoutOptions.providers : []);
      }
      if (!feeResult.error) setFeePolicy(Array.isArray(feeResult.data) ? feeResult.data[0] || null : feeResult.data);
      if (!planResult.error) setPlanAccess(Array.isArray(planResult.data) ? planResult.data[0] || null : planResult.data);
    } finally {
      setCommerceLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    if (["offers", "orders", "payments", "plan"].includes(tab)) loadCommercePanel();
  }, [tab, loadCommercePanel]);

  const selectedOffer = useMemo(
    () => offerThreads.find((offer) => String(offer.id) === String(selectedOfferId)) || null,
    [offerThreads, selectedOfferId]
  );

  const latestActionableOfferEvent = useMemo(
    () => [...offerEvents].reverse().find((event) => ["offer", "counter"].includes(event.event_type)) || null,
    [offerEvents]
  );

  const sellerCanRespondToOffer = Boolean(
    selectedOffer?.status === "open" &&
    !offerDetailLoading &&
    latestActionableOfferEvent?.actor_side === "buyer"
  );

  const refreshOfferStock = useCallback(async (offer) => {
    const inventoryId = offer?.offer_inventory?.id;
    if (!inventoryId) {
      setOfferStock({ available: false, reason: "Inventory could not be verified." });
      return false;
    }
    setOfferStockLoading(true);
    try {
      const { data, error } = await supabase.from("seller_inventory")
        .select("id, quantity_on_hand, quantity_reserved, status")
        .eq("id", inventoryId).single();
      if (error) throw error;
      const onHand = Number(data.quantity_on_hand);
      const reserved = Number(data.quantity_reserved);
      const valid = Number.isFinite(onHand) && Number.isFinite(reserved);
      const available = valid && onHand - reserved > 0;
      const reason = !valid ? "Inventory availability could not be verified."
        : onHand <= 0 ? "This item is sold out. Accept and Counter are unavailable."
        : reserved >= onHand ? "All available units are currently reserved. Accept and Counter are unavailable."
        : null;
      setOfferStock({ available, reason });
      return available;
    } catch (error) {
      console.error("OFFER INVENTORY CHECK ERROR:", error);
      setOfferStock({ available: false, reason: "Inventory availability could not be verified." });
      return false;
    } finally {
      setOfferStockLoading(false);
    }
  }, []);

  const offerCanAcceptOrCounter = Boolean(sellerCanRespondToOffer && offerStock?.available && !offerStockLoading);

  const selectedOfferImageUrl = useMemo(
    () => resolveImageUrl(primaryInventoryImage(selectedOffer?.offer_inventory)),
    [selectedOffer]
  );

  const selectedOfferIdentity = useMemo(() => {
    const item = selectedOffer?.canonical_item;
    if (!item) return "";
    return [item.year, item.brand, item.set_name, item.subset, item.card_number ? `#${item.card_number}` : null]
      .filter(Boolean)
      .join(" · ");
  }, [selectedOffer]);

  const selectedOfferAskPercent = useMemo(() => {
    const offer = Number(selectedOffer?.current_amount);
    const ask = Number(selectedOffer?.listing_price);
    if (!Number.isFinite(offer) || !Number.isFinite(ask) || ask <= 0) return null;
    return Math.round((offer / ask) * 100);
  }, [selectedOffer]);

  const selectedOfferComparableSales = useMemo(() => {
    const snapshot = selectedOffer?.market_snapshot;
    const rows = selectedOffer?.market_sales || [];
    if (!snapshot) return rows.slice(0, 5);
    return rows.filter((row) => {
      if (snapshot.is_graded !== null && snapshot.is_graded !== undefined && row.is_graded !== snapshot.is_graded) return false;
      if (snapshot.is_autographed !== null && snapshot.is_autographed !== undefined && row.is_autographed !== snapshot.is_autographed) return false;
      if (snapshot.is_graded && snapshot.grade != null && Number(row.grade) !== Number(snapshot.grade)) return false;
      return true;
    }).slice(0, 5);
  }, [selectedOffer]);

  const openSellerOffer = useCallback(async (offerId, { syncUrl = true } = {}) => {
    if (!offerId) return;
    setSelectedOfferId(offerId);
    setOfferDetailLoading(true);
    setOfferEvents([]);
    setOfferStock(null);
    setOfferCounterAmount("");
    setOfferMessage("");
    try {
      const [eventsResult, marketContextResult] = await Promise.all([
        supabase
          .from("marketplace_offer_events")
          .select("id, actor_side, event_type, amount, message, created_at")
          .eq("offer_thread_id", offerId)
          .order("created_at", { ascending: true }),
        supabase.rpc("get_marketplace_offer_market_context", {
          p_offer_thread_id: offerId,
        }),
      ]);

      if (eventsResult.error) throw eventsResult.error;
      setOfferEvents(eventsResult.data || []);
      const offerForStock = offerThreads.find((row) => String(row.id) === String(offerId));
      await refreshOfferStock(offerForStock);

      if (marketContextResult.error) {
        console.error("OFFER MARKET CONTEXT LOAD ERROR:", marketContextResult.error);
      } else {
        const marketContext = marketContextResult.data || {};
        const snapshot = marketContext.snapshot
          ? {
              ...marketContext.snapshot,
              evidence_metadata: {
                source: marketContext.snapshot.evidence_source || null,
                evidence_type: marketContext.snapshot.evidence_type || null,
                broad_listing_count: marketContext.snapshot.broad_listing_count ?? null,
                valuation_listing_count: marketContext.snapshot.valuation_listing_count ?? null,
              },
            }
          : null;

        setOfferThreads((current) => current.map((offer) =>
          String(offer.id) === String(offerId)
            ? {
                ...offer,
                market_snapshot: snapshot,
                market_sales: Array.isArray(marketContext.recent_sales) ? marketContext.recent_sales : [],
                market_context_loaded: true,
              }
            : offer
        ));
      }

      if (syncUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set("tab", "offers");
        url.searchParams.set("offer", offerId);
        url.searchParams.delete("order");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }
    } catch (error) {
      showNotice("error", errorMessage(error, "Unable to load this offer."));
    } finally {
      setOfferDetailLoading(false);
    }
  }, [showNotice, offerThreads, refreshOfferStock]);

  const closeSellerOffer = useCallback(() => {
    setSelectedOfferId(null);
    setOfferEvents([]);
    setOfferStock(null);
    setOfferCounterAmount("");
    setOfferMessage("");
    const url = new URL(window.location.href);
    url.searchParams.delete("offer");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const respondToSellerOffer = useCallback(async (action) => {
    if (!selectedOffer?.id || offerActionBusy || selectedOffer.status !== "open") return;
    if (["accept", "counter"].includes(action)) {
      const available = await refreshOfferStock(selectedOffer);
      if (!available) {
        showNotice("error", "This item is unavailable. Refresh the offer before responding.");
        setOfferConfirmAction(null);
        return;
      }
    }
    const counterAmount = action === "counter" ? Number(offerCounterAmount) : null;
    if (action === "counter" && (!Number.isFinite(counterAmount) || counterAmount <= 0)) {
      showNotice("error", "Enter a valid counter-offer amount.");
      return;
    }
    setOfferActionBusy(true);
    try {
      const { error } = await supabase.rpc("respond_to_marketplace_offer", {
        p_offer_thread_id: selectedOffer.id,
        p_action: action,
        p_counter_amount: action === "counter" ? counterAmount : null,
        p_message: offerMessage.trim() || null,
      });
      if (error) throw error;
      showNotice("success", action === "accept" ? "Offer accepted." : action === "decline" ? "Offer declined." : "Counter offer sent.");
      setOfferMessage("");
      setOfferCounterAmount("");
      await loadCommercePanel();
      await openSellerOffer(selectedOffer.id, { syncUrl: false });
    } catch (error) {
      showNotice("error", errorMessage(error, `Unable to ${action} this offer.`));
    } finally {
      setOfferActionBusy(false);
    }
  }, [selectedOffer, offerActionBusy, offerCounterAmount, offerMessage, showNotice, loadCommercePanel, openSellerOffer, refreshOfferStock]);

  useEffect(() => {
    if (tab !== "offers" || commerceLoading || !offerThreads.length || selectedOfferId) return;
    const requestedOffer = new URLSearchParams(window.location.search).get("offer");
    if (!requestedOffer) return;
    const belongsToShop = offerThreads.some((row) => String(row.id) === String(requestedOffer));
    if (belongsToShop) openSellerOffer(requestedOffer, { syncUrl: false });
  }, [tab, commerceLoading, offerThreads, selectedOfferId, openSellerOffer]);

  const loadSellerOrderDetail = useCallback(async (sellerOrderId, { syncUrl = true } = {}) => {
    if (!sellerOrderId) return;
    setSelectedSellerOrderId(sellerOrderId);
    setOrderDetailLoading(true);
    setOrderDetailError(null);
    try {
      const { data, error } = await supabase.rpc("get_my_seller_order_detail", {
        p_seller_order_id: sellerOrderId,
      });
      if (error) throw error;
      setOrderDetail(data || null);

      const { data: shipmentRows, error: shipmentError } = await supabase
        .from("marketplace_shipments")
        .select("*, marketplace_shipment_items(id, order_item_id, quantity)")
        .eq("seller_order_id", sellerOrderId)
        .order("created_at", { ascending: false });
      if (shipmentError) throw shipmentError;
      setShipments(shipmentRows || []);

      const { data: returnRows, error: returnError } = await supabase.rpc(
        "get_my_seller_marketplace_returns_for_order",
        { p_seller_order_id: sellerOrderId }
      );
      if (returnError) throw returnError;
      setSellerReturns(Array.isArray(returnRows) ? returnRows : []);

      if (syncUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set("tab", "orders");
        url.searchParams.set("order", sellerOrderId);
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }
    } catch (error) {
      setOrderDetail(null);
      setOrderDetailError(errorMessage(error, "Unable to load this order."));
    } finally {
      setOrderDetailLoading(false);
    }
  }, []);

  const closeSellerOrderDetail = useCallback(() => {
    setSelectedSellerOrderId(null);
    setOrderDetail(null);
    setOrderDetailError(null);
    setRefundOpen(false);
    setFulfillmentOpen(false);
    setShipments([]);
    setSellerReturns([]);
    const url = new URL(window.location.href);
    url.searchParams.delete("order");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  useEffect(() => {
    if (tab !== "orders" || commerceLoading || !sellerOrders.length || selectedSellerOrderId) return;
    const requestedOrder = new URLSearchParams(window.location.search).get("order");
    if (!requestedOrder) return;
    const belongsToShop = sellerOrders.some((row) => String(row.id) === String(requestedOrder));
    if (belongsToShop) loadSellerOrderDetail(requestedOrder, { syncUrl: false });
  }, [tab, commerceLoading, sellerOrders, selectedSellerOrderId, loadSellerOrderDetail]);

  const approveSellerReturn = useCallback(async (returnRow) => {
    if (!returnRow?.id || returnBusyId) return;
    setReturnBusyId(returnRow.id);
    try {
      const { error } = await supabase.rpc("approve_marketplace_return", {
        p_return_id: returnRow.id,
        p_items: null,
        p_seller_notes: null,
      });
      if (error) throw error;
      showNotice("success", "Return approved.");
      await loadSellerOrderDetail(selectedSellerOrderId, { syncUrl: false });
    } catch (error) {
      showNotice("error", errorMessage(error, "Return could not be approved."));
    } finally {
      setReturnBusyId(null);
    }
  }, [returnBusyId, selectedSellerOrderId, showNotice, loadSellerOrderDetail]);

  const rejectSellerReturn = useCallback(async (returnRow) => {
    if (!returnRow?.id || returnBusyId) return;
    const reason = window.prompt("Reason for rejecting this return (optional):", "");
    if (reason === null) return;
    setReturnBusyId(returnRow.id);
    try {
      const { error } = await supabase.rpc("reject_marketplace_return", {
        p_return_id: returnRow.id,
        p_seller_notes: reason.trim() || null,
      });
      if (error) throw error;
      showNotice("success", "Return rejected.");
      await loadSellerOrderDetail(selectedSellerOrderId, { syncUrl: false });
    } catch (error) {
      showNotice("error", errorMessage(error, "Return could not be rejected."));
    } finally {
      setReturnBusyId(null);
    }
  }, [returnBusyId, selectedSellerOrderId, showNotice, loadSellerOrderDetail]);

  const receiveSellerReturn = useCallback(async (returnRow) => {
    if (!returnRow?.id || returnBusyId) return;
    const items = (returnRow.items || [])
      .filter((item) => Number(item.quantity_approved || 0) > 0)
      .map((item) => ({
        return_item_id: item.id,
        quantity: Number(item.quantity_approved || 0),
        disposition: "restock",
      }));
    if (!items.length) {
      showNotice("error", "This return has no approved quantity to receive.");
      return;
    }
    setReturnBusyId(returnRow.id);
    try {
      const { error } = await supabase.rpc("receive_marketplace_return", {
        p_return_id: returnRow.id,
        p_items: items,
        p_seller_notes: null,
      });
      if (error) throw error;
      showNotice("success", "Return marked received. Inventory has not been restocked yet.");
      await loadSellerOrderDetail(selectedSellerOrderId, { syncUrl: false });
    } catch (error) {
      showNotice("error", errorMessage(error, "Return could not be marked received."));
    } finally {
      setReturnBusyId(null);
    }
  }, [returnBusyId, selectedSellerOrderId, showNotice, loadSellerOrderDetail]);

  const restockSellerReturnItem = useCallback(async (returnItem) => {
    const remaining = Math.max(
      0,
      Number(returnItem?.quantity_received || 0) - Number(returnItem?.quantity_restocked || 0)
    );
    if (!returnItem?.id || !remaining || returnBusyId) return;
    setReturnBusyId(returnItem.id);
    try {
      const { error } = await supabase.rpc("restock_marketplace_return_item", {
        p_return_item_id: returnItem.id,
        p_quantity: remaining,
        p_idempotency_key: crypto.randomUUID(),
      });
      if (error) throw error;
      showNotice("success", `${remaining} returned item${remaining === 1 ? "" : "s"} restocked.`);
      await loadSellerOrderDetail(selectedSellerOrderId, { syncUrl: false });
      await loadCommercePanel();
    } catch (error) {
      showNotice("error", errorMessage(error, "Returned inventory could not be restocked."));
    } finally {
      setReturnBusyId(null);
    }
  }, [returnBusyId, selectedSellerOrderId, showNotice, loadSellerOrderDetail, loadCommercePanel]);

  const openRefundDialog = useCallback(() => {
    const remaining = Number(orderDetail?.payment?.remaining_refundable || 0);
    setRefundMode("full");
    setRefundAmount(remaining > 0 ? remaining.toFixed(2) : "");
    setRefundReason("buyer_request");
    setRefundNotes("");
    setRefundRequestId(crypto.randomUUID());
    setRefundOpen(true);
  }, [orderDetail]);

  const submitSellerRefund = useCallback(async () => {
    const payment = orderDetail?.payment;
    if (!payment?.id || refundBusy) return;
    const remaining = Number(payment.remaining_refundable || 0);
    const amount = refundMode === "full" ? remaining : Number(refundAmount);
    if (!Number.isFinite(amount) || amount <= 0 || amount > remaining + 0.005) {
      showNotice("error", `Refund amount must be between $0.01 and ${money(remaining, payment.currency || "USD")}.`);
      return;
    }
    const reasonLabels = {
      buyer_request: "Buyer request",
      order_cancellation: "Order cancellation",
      item_unavailable: "Item unavailable",
      shipping_adjustment: "Shipping adjustment",
      item_issue_return: "Item issue / return",
      other: "Other",
    };
    const reasonBase = reasonLabels[refundReason] || "Seller refund";
    const reason = refundNotes.trim() ? `${reasonBase}: ${refundNotes.trim()}` : reasonBase;
    const requestId = refundRequestId || crypto.randomUUID();
    if (!refundRequestId) setRefundRequestId(requestId);
    setRefundBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("paypal-marketplace", {
        body: {
          action: "refund-payment-intent",
          paymentIntentId: payment.id,
          refundAmount: amount,
          reason,
          refundRequestId: requestId,
        },
      });
      if (error) {
        let message = errorMessage(error, "Refund could not be completed.");
        const response = error?.context;
        if (response && typeof response.json === "function") {
          try {
            const body = await response.json();
            if (body?.error) message = body.error;
          } catch { /* keep safe message */ }
        }
        throw new Error(message);
      }
      if (!data?.success) throw new Error(data?.error || "Refund could not be completed.");
      setRefundOpen(false);
      setRefundRequestId(null);
      showNotice(data.pending ? "info" : "success", data.pending ? "Refund submitted to PayPal and is pending." : `Refund completed: ${money(data.amount, data.currency || payment.currency || "USD")}.`);
      await loadSellerOrderDetail(selectedSellerOrderId, { syncUrl: false });
      await loadCommercePanel();
    } catch (error) {
      // Keep the same request ID so an ambiguous retry remains idempotent.
      showNotice("error", errorMessage(error, "Refund could not be completed."));
    } finally {
      setRefundBusy(false);
    }
  }, [orderDetail, refundBusy, refundMode, refundAmount, refundReason, refundNotes, refundRequestId, selectedSellerOrderId, showNotice, loadSellerOrderDetail, loadCommercePanel]);


  const openFulfillmentDialog = useCallback(() => {
    setFulfillmentDraft({
      carrier: "USPS",
      service: "Ground Advantage",
      trackingNumber: "",
      trackingUrl: "",
      sellerNotes: "",
    });
    setFulfillmentOpen(true);
  }, []);

  const createShipment = useCallback(async () => {
    if (!orderDetail?.seller_order?.id || fulfillmentBusy) return;
    const items = (orderDetail.items || []).map((item) => ({
      order_item_id: item.id,
      quantity: Number(item.quantity || 0),
    })).filter((item) => item.order_item_id && item.quantity > 0);
    if (!items.length) {
      showNotice("error", "This order has no shippable item quantities.");
      return;
    }
    setFulfillmentBusy(true);
    try {
      const { error } = await supabase.rpc("create_marketplace_shipment", {
        p_seller_order_id: orderDetail.seller_order.id,
        p_items: items,
        p_carrier: fulfillmentDraft.carrier.trim() || null,
        p_service: fulfillmentDraft.service.trim() || null,
        p_tracking_number: fulfillmentDraft.trackingNumber.trim() || null,
        p_tracking_url: fulfillmentDraft.trackingUrl.trim() || null,
        p_seller_notes: fulfillmentDraft.sellerNotes.trim() || null,
      });
      if (error) throw error;
      setFulfillmentOpen(false);
      showNotice("success", "Shipment created. The order is now in processing.");
      await loadSellerOrderDetail(orderDetail.seller_order.id, { syncUrl: false });
      await loadCommercePanel();
    } catch (error) {
      showNotice("error", errorMessage(error, "Shipment could not be created."));
    } finally {
      setFulfillmentBusy(false);
    }
  }, [orderDetail, fulfillmentBusy, fulfillmentDraft, showNotice, loadSellerOrderDetail, loadCommercePanel]);

  const markShipmentShipped = useCallback(async (shipment) => {
    if (!shipment?.id || fulfillmentBusy) return;
    setFulfillmentBusy(true);
    try {
      const { error } = await supabase.rpc("mark_marketplace_shipment_shipped", {
        p_shipment_id: shipment.id,
        p_carrier: shipment.carrier || null,
        p_service: shipment.service || null,
        p_tracking_number: shipment.tracking_number || null,
        p_tracking_url: shipment.tracking_url || null,
      });
      if (error) throw error;
      showNotice("success", "Shipment marked shipped.");
      await loadSellerOrderDetail(selectedSellerOrderId, { syncUrl: false });
      await loadCommercePanel();
    } catch (error) {
      showNotice("error", errorMessage(error, "Shipment could not be marked shipped."));
    } finally {
      setFulfillmentBusy(false);
    }
  }, [fulfillmentBusy, selectedSellerOrderId, showNotice, loadSellerOrderDetail, loadCommercePanel]);

  const cancelFullyRefundedOrder = useCallback(async () => {
    const sellerOrderId = orderDetail?.seller_order?.id;
    if (!sellerOrderId || orderCancelBusy) return;
    const confirmed = window.confirm(
      "Cancel fulfillment for this fully refunded order? Any preparing shipment will be cancelled. Inventory will not be restocked automatically."
    );
    if (!confirmed) return;
    setOrderCancelBusy(true);
    try {
      const { error } = await supabase.rpc("cancel_fully_refunded_marketplace_order", {
        p_seller_order_id: sellerOrderId,
      });
      if (error) throw error;
      showNotice("success", "Order cancelled. No inventory was restocked.");
      await loadSellerOrderDetail(sellerOrderId, { syncUrl: false });
      await loadCommercePanel();
    } catch (error) {
      showNotice("error", errorMessage(error, "Order could not be cancelled."));
    } finally {
      setOrderCancelBusy(false);
    }
  }, [orderDetail, orderCancelBusy, showNotice, loadSellerOrderDetail, loadCommercePanel]);

  const markShipmentDelivered = useCallback(async (shipmentId) => {
    if (!shipmentId || fulfillmentBusy) return;
    setFulfillmentBusy(true);
    try {
      const { error } = await supabase.rpc("mark_marketplace_shipment_delivered", { p_shipment_id: shipmentId });
      if (error) throw error;
      showNotice("success", "Shipment marked delivered. Fulfillment status has been refreshed.");
      await loadSellerOrderDetail(selectedSellerOrderId, { syncUrl: false });
      await loadCommercePanel();
    } catch (error) {
      showNotice("error", errorMessage(error, "Shipment could not be marked delivered."));
    } finally {
      setFulfillmentBusy(false);
    }
  }, [fulfillmentBusy, selectedSellerOrderId, showNotice, loadSellerOrderDetail, loadCommercePanel]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestedTab = params.get("tab");
    if (TABS.some(([key]) => key === requestedTab)) setTab(requestedTab);
  }, []);

  useEffect(() => {
    if (!shopId) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("paypal") !== "return") return;
    setTab("payments");
    refreshPayPalAccount({ fromReturn: true });
  }, [shopId, refreshPayPalAccount]);

  const listingByInventoryId = useMemo(() => {
    const map = new Map();
    listings.forEach((listing) => {
      if (["draft", "published", "paused"].includes(listing.status)) {
        map.set(listing.seller_inventory_id, listing);
      }
    });
    return map;
  }, [listings]);

  const stats = useMemo(() => {
    const currentInventory = inventory.filter((x) => x.status !== "archived");
    return {
      inventory: currentInventory.length,
      archivedInventory: inventory.length - currentInventory.length,
      collectionBacked: currentInventory.filter((x) => x.source_user_item_id).length,
      shopOnly: currentInventory.filter((x) => !x.source_user_item_id).length,
      activeListings: listings.filter((x) => x.status === "published").length,
      drafts: listings.filter((x) => x.status === "draft").length,
      paused: listings.filter((x) => x.status === "paused").length,
    };
  }, [inventory, listings]);

  const listingLimit = usage?.active_listing_limit;
  const unlimited = usage?.is_unlimited || Number(listingLimit) < 0;
  const activeCount = Number(usage?.active_listing_count ?? stats.activeListings);
  const usagePct = unlimited || !Number(listingLimit)
    ? 0
    : Math.min(100, (activeCount / Number(listingLimit)) * 100);

  const activeProfiles = useMemo(
    () => profiles.filter((profile) => profile.is_active),
    [profiles]
  );

  const listableInventory = useMemo(
    () =>
      inventory.filter(
        (item) =>
          item.status === "active" &&
          !listingByInventoryId.has(item.id) &&
          Number(item.quantity_on_hand || 0) - Number(item.quantity_reserved || 0) > 0
      ),
    [inventory, listingByInventoryId]
  );

  const channelLinksByInventory = useMemo(() => {
    const map = new Map();
    channelLinks.forEach((link) => {
      if (!map.has(String(link.seller_inventory_id))) map.set(String(link.seller_inventory_id), []);
      map.get(String(link.seller_inventory_id)).push(link);
    });
    return map;
  }, [channelLinks]);

  // eBay asking price is channel-specific, never an acquisition cost or Hobbli listing.
  const ebayListingForInventory = (inventoryId) =>
    (channelLinksByInventory.get(String(inventoryId)) || []).find((link) =>
      ["active", "published"].includes(String(link.listing_status || "").toLowerCase()) &&
      link.external_listing_id
    ) || null;

  const visibleInventory = useMemo(
    () => inventory.filter((item) =>
      inventoryView === "archived" ? item.status === "archived" : item.status !== "archived"
    ),
    [inventory, inventoryView]
  );

  // Inventory origin is distinct from channels where an item is currently listed.
  const matchesListingFilters = (item, status = null) => {
    if (!item) return false;
    const source = String(item.acquisition_source || "").trim().toLowerCase();
    const isEbayOrigin = source === "ebay" || source.startsWith("ebay_");
    const links = channelLinksByInventory.get(String(item.id)) || [];
    const activeLinks = links.filter((link) => ["active", "published"].includes(String(link.listing_status || "").toLowerCase()));
    const hasEbay = activeLinks.some((link) => String(link.channel_account_id) === "2899fa4a-4e2b-4381-b483-596cac836191");
    if (listingSourceFilter === "ebay" && !isEbayOrigin) return false;
    if (listingSourceFilter === "hobbli" && (isEbayOrigin || (source && source !== "hobbli"))) return false;
    if (listingSourceFilter === "other" && (!source || source === "hobbli" || isEbayOrigin)) return false;
    if (listingChannelFilter === "ebay" && !hasEbay) return false;
    if (listingChannelFilter === "other" && !activeLinks.some((link) => !hasEbay || String(link.channel_account_id) !== "2899fa4a-4e2b-4381-b483-596cac836191")) return false;
    if (listingChannelFilter === "none" && activeLinks.length) return false;
    if (listingChannelFilter === "multi" && activeLinks.length < 2) return false;
    if (listingStatusFilter !== "all" && status !== listingStatusFilter) return false;
    return true;
  };

  // Bulk creation always targets unlisted inventory; the status filter applies only
  // to the existing listings table below. Never hide bulk candidates for Draft/Published.
  const filteredListableInventory = listableInventory.filter((item) => {
    const source = String(item.acquisition_source || "").trim().toLowerCase();
    const isEbayOrigin = source === "ebay" || source.startsWith("ebay_");
    const links = channelLinksByInventory.get(String(item.id)) || [];
    const activeLinks = links.filter((link) => ["active", "published"].includes(String(link.listing_status || "").toLowerCase()));
    const hasEbay = activeLinks.some((link) => String(link.channel_account_id) === "2899fa4a-4e2b-4381-b483-596cac836191");
    if (listingSourceFilter === "ebay" && !isEbayOrigin) return false;
    if (listingSourceFilter === "hobbli" && (isEbayOrigin || (source && source !== "hobbli"))) return false;
    if (listingSourceFilter === "other" && (!source || source === "hobbli" || isEbayOrigin)) return false;
    if (listingChannelFilter === "ebay" && !hasEbay) return false;
    if (listingChannelFilter === "other" && !activeLinks.some((link) => String(link.channel_account_id) !== "2899fa4a-4e2b-4381-b483-596cac836191")) return false;
    if (listingChannelFilter === "none" && activeLinks.length) return false;
    if (listingChannelFilter === "multi" && activeLinks.length < 2) return false;
    return true;
  });
  const listingFilterControls = (
    <div className="seller-listing-filters">
      <label>Inventory source
        <select value={listingSourceFilter} onChange={(e) => { setListingSourceFilter(e.target.value); setBulkListingReview(false); setBulkListingSelection(new Set()); setBulkStatusSelection(new Set()); }}>
          <option value="all">All origins</option><option value="ebay">Imported from eBay</option>
          <option value="hobbli">Hobbli / unspecified</option><option value="other">Other imports</option>
        </select>
      </label>
      <label>External channel
        <select value={listingChannelFilter} onChange={(e) => { setListingChannelFilter(e.target.value); setBulkListingReview(false); setBulkListingSelection(new Set()); setBulkStatusSelection(new Set()); }}>
          <option value="all">All channels</option><option value="ebay">Active on eBay</option>
          <option value="other">Active on other channels</option><option value="multi">Multiple external channels</option>
          <option value="none">No active external channel</option>
        </select>
      </label>
      <label>Existing Hobbli listings status
        <select value={listingStatusFilter} onChange={(e) => { setListingStatusFilter(e.target.value); setBulkListingReview(false); setBulkListingSelection(new Set()); setBulkStatusSelection(new Set()); }}>
          <option value="all">All statuses</option><option value="unlisted">Not yet listed</option>
          <option value="published">Published</option><option value="draft">Draft</option>
          <option value="paused">Paused</option><option value="ended">Ended</option>
        </select>
      </label>
      <button type="button" className="seller-btn" onClick={() => { setListingSourceFilter("all"); setListingChannelFilter("all"); setListingStatusFilter("all"); setBulkListingReview(false); setBulkStatusSelection(new Set()); setBulkListingSelection(new Set()); }}>Clear filters</button>
    </div>
  );

  const visibleListings = useMemo(
    () =>
      listings.filter((listing) =>
        matchesListingFilters(inventory.find((item) => item.id === listing.seller_inventory_id), listing.status) && (listingView === "history"
          ? !["draft", "published", "paused"].includes(listing.status)
          : ["draft", "published", "paused"].includes(listing.status))
      ),
    [listings, listingView, inventory, channelLinksByInventory, listingSourceFilter, listingChannelFilter, listingStatusFilter]
  );

  const bulkStatusEligible = useMemo(() => visibleListings.filter((listing) =>
    bulkStatusAction === "publish" ? ["draft", "paused"].includes(listing.status) : listing.status === "published"
  ), [visibleListings, bulkStatusAction]);
  const bulkStatusSelected = bulkStatusEligible.filter((listing) => bulkStatusSelection.has(String(listing.id)));

  const runBulkStatusAction = async () => {
    if (bulkStatusBusy || !bulkStatusSelected.length) return;
    const action = bulkStatusAction === "publish" ? "publish" : "pause";
    const selected = [...bulkStatusSelected];
    if (!window.confirm(`${action === "publish" ? "Publish" : "Pause"} ${selected.length} existing Hobbli listing(s)?\n\nOnly the selected, currently eligible listings will be changed. Inventory and eBay listings will not be deleted or modified. Failed items will be reported.`)) return;
    setBulkStatusBusy(true);
    const failures = [];
    let completed = 0;
    setBulkStatusProgress({completed:0,total:selected.length,failed:0,failures:[]});
    for (const listing of selected) {
      try {
        const rpc = action === "publish" ? "publish_listing" : "set_listing_status";
        const args = action === "publish" ? {p_listing_id:listing.id} : {p_listing_id:listing.id,p_status:"paused"};
        const {error} = await supabase.rpc(rpc,args);
        if (error) throw error;
        setBulkStatusSelection((prev) => {const next=new Set(prev);next.delete(String(listing.id));return next;});
      } catch (error) {
        failures.push({id:listing.id,title:listing.title_override || listing.seller_inventory?.title || listing.id,message:errorMessage(error)});
      }
      completed++;
      setBulkStatusProgress({completed,total:selected.length,failed:failures.length,failures:[...failures]});
    }
    try { await loadDashboard(true); } finally { setBulkStatusBusy(false); }
    showNotice(failures.length ? "error" : "success", `${completed-failures.length} listing(s) ${action === "publish" ? "published" : "paused"}; ${failures.length} failed.`);
  };

  const managedImageItem = useMemo(
    () => inventory.find((item) => item.id === imageManagerInventoryId) || null,
    [inventory, imageManagerInventoryId]
  );

  const callInventoryMedia = useCallback(async (body) => {
    const { data, error } = await supabase.functions.invoke("seller-inventory-media", { body });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || "Unable to update inventory images.");
    return data;
  }, []);

  const uploadInventoryImages = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!managedImageItem || !files.length) return;

    setImageManagerBusy(true);
    try {
      for (const file of files) {
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
          throw new Error("Inventory images must be JPEG, PNG, or WebP.");
        }
        if (file.size > 10 * 1024 * 1024) {
          throw new Error("Inventory images must be 10 MB or smaller.");
        }

        const auth = await callInventoryMedia({
          action: "authorize_upload",
          inventoryId: managedImageItem.id,
          contentType: file.type,
          fileSize: file.size,
        });

        const uploadResponse = await fetch(auth.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!uploadResponse.ok) {
          throw new Error(`Image upload failed (${uploadResponse.status}).`);
        }

        await callInventoryMedia({
          action: "finalize_upload",
          inventoryId: managedImageItem.id,
          objectKey: auth.storage.objectKey,
          storageBucket: auth.storage.bucket,
          storageProvider: auth.storage.provider || "r2",
        });
      }

      showNotice("success", files.length === 1 ? "Image added." : `${files.length} images added.`);
      await loadDashboard(true);
    } catch (error) {
      console.error("INVENTORY IMAGE UPLOAD ERROR:", error);
      showNotice("error", errorMessage(error, "Unable to upload inventory image."));
    } finally {
      setImageManagerBusy(false);
    }
  };

  const setPrimaryInventoryImage = async (image) => {
    if (!managedImageItem || !image?.id || image.is_primary) return;
    setImageManagerBusy(true);
    try {
      await callInventoryMedia({
        action: "set_primary",
        inventoryId: managedImageItem.id,
        imageId: image.id,
      });
      showNotice("success", "Primary image updated.");
      await loadDashboard(true);
    } catch (error) {
      console.error("SET PRIMARY INVENTORY IMAGE ERROR:", error);
      showNotice("error", errorMessage(error, "Unable to set the primary image."));
    } finally {
      setImageManagerBusy(false);
    }
  };

  const moveInventoryImage = async (image, direction) => {
    if (!managedImageItem || !image?.id) return;
    const images = sortedInventoryImages(managedImageItem);
    const index = images.findIndex((entry) => entry.id === image.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= images.length) return;

    const orderedIds = [...images.map((entry) => entry.id)];
    [orderedIds[index], orderedIds[target]] = [orderedIds[target], orderedIds[index]];

    setImageManagerBusy(true);
    try {
      await callInventoryMedia({
        action: "reorder",
        inventoryId: managedImageItem.id,
        imageIds: orderedIds,
      });
      await loadDashboard(true);
    } catch (error) {
      console.error("REORDER INVENTORY IMAGE ERROR:", error);
      showNotice("error", errorMessage(error, "Unable to reorder inventory images."));
    } finally {
      setImageManagerBusy(false);
    }
  };

  const deleteInventoryImage = async (image) => {
    if (!managedImageItem || !image?.id) return;
    setImageManagerBusy(true);
    try {
      await callInventoryMedia({
        action: "delete",
        inventoryId: managedImageItem.id,
        imageId: image.id,
      });
      showNotice("success", "Image removed.");
      await loadDashboard(true);
    } catch (error) {
      console.error("DELETE INVENTORY IMAGE ERROR:", error);
      showNotice("error", errorMessage(error, "Unable to remove inventory image."));
    } finally {
      setImageManagerBusy(false);
    }
  };

  const beginListing = (item = null) => {
    const defaultProfile = activeProfiles.find((profile) => profile.is_default);
    const availableQuantity = item
      ? Math.max(1, Number(item.quantity_on_hand || 0) - Number(item.quantity_reserved || 0))
      : 1;
    setListingDraft({
      inventoryId: item?.id || "",
      price: item && ebayListingForInventory(item.id)?.external_price != null
        ? String(ebayListingForInventory(item.id).external_price)
        : "",
      quantity: String(item?.inventory_type === "unique" ? 1 : availableQuantity),
      acceptsOffers: false,
      minimumOffer: "",
      shippingMode: defaultProfile ? "profile" : "free",
      shippingProfileId: defaultProfile?.id || "",
      shippingAmount: "",
      localPickup: Boolean(defaultProfile?.local_pickup),
      combinedShipping: defaultProfile ? Boolean(defaultProfile.combined_shipping) : true,
      publishNow: true,
    });
    setTab("listings");
  };

  const bulkListingRow = (item) => {
    const override = bulkListingOverrides[item.id] || {};
    const ebay = ebayListingForInventory(item.id);
    const available = Math.max(0, Number(item.quantity_on_hand || 0) - Number(item.quantity_reserved || 0));
    return {
      item,
      price: override.price ?? (ebay?.external_price != null ? String(ebay.external_price) : ""),
      quantity: override.quantity ?? String(item.inventory_type === "unique" ? 1 : available),
      ebay,
      available,
    };
  };

  const bulkSelectedRows = filteredListableInventory.filter((item) => bulkListingSelection.has(String(item.id))).map(bulkListingRow);
  const bulkInvalidRows = bulkSelectedRows.filter(({ price, quantity, available }) =>
    price === "" || !Number.isFinite(Number(price)) || Number(price) < 0 ||
    !Number.isInteger(Number(quantity)) || Number(quantity) < 1 || Number(quantity) > available
  );

  const submitBulkListings = async () => {
    if (bulkListingBusy || !bulkListingReview || !bulkSelectedRows.length || bulkInvalidRows.length) return;
    const defaultProfile = activeProfiles.find((profile) => profile.is_default);
    const shippingMode = defaultProfile ? "profile" : "free";
    const publishNow = bulkListingMode === "publish";
    const remainingCapacity = unlimited ? Infinity : Math.max(0, Number(listingLimit || 0) - activeCount);
    if (publishNow && bulkSelectedRows.length > remainingCapacity) {
      showNotice("error", `Only ${remainingCapacity} additional published listings are available on this plan.`);
      return;
    }
    if (!window.confirm(`${publishNow ? "PUBLISH" : "SAVE DRAFTS FOR"} ${bulkSelectedRows.length} selected inventory items on Hobbli?\n\nThis does not change any eBay listings or enable synchronization. Each item will be processed separately.`)) return;
    setBulkListingBusy(true);
    let created = 0;
    const failures = [];
    const completed = new Set();
    for (const row of bulkSelectedRows) {
      setBulkListingProgress({ completed: created + failures.length, total: bulkSelectedRows.length, created, failed: failures.length });
      try {
        const { error } = await supabase.rpc("create_seller_listing", {
          p_inventory_id: row.item.id,
          p_price_amount: Number(row.price),
          p_listing_quantity: Number(row.quantity),
          p_title_override: null,
          p_description_override: null,
          p_accepts_offers: false,
          p_minimum_offer_amount: null,
          p_condition_override: null,
          p_shipping_mode: shippingMode,
          p_shipping_profile_id: defaultProfile?.id || null,
          p_shipping_amount: null,
          p_local_pickup_available: Boolean(defaultProfile?.local_pickup),
          p_combined_shipping_allowed: defaultProfile ? Boolean(defaultProfile.combined_shipping) : true,
          p_publish_now: publishNow,
          p_attributes: {},
        });
        if (error) throw error;
        created++;
        completed.add(String(row.item.id));
      } catch (error) {
        failures.push({ id: row.item.id, title: row.item.title, message: errorMessage(error) });
      }
    }
    setBulkListingProgress({ completed: bulkSelectedRows.length, total: bulkSelectedRows.length, created, failed: failures.length, failures });
    setBulkListingSelection((old) => new Set([...old].filter((id) => !completed.has(id))));
    setBulkListingReview(false);
    setBulkListingBusy(false);
    await loadDashboard(true);
    showNotice(failures.length ? "error" : "success", `${created} ${publishNow ? "published" : "drafts created"}; ${failures.length} failed. ${failures.length ? "See results below." : ""}`);
  };

  const createSellerListing = async (event) => {
    event.preventDefault();

    if (!listingDraft.inventoryId) {
      showNotice("error", "Choose an inventory item to list.");
      return;
    }

    const price = toNumberOrNull(listingDraft.price);
    if (price === null || price < 0) {
      showNotice("error", "Enter a valid listing price.");
      return;
    }

    const selectedInventory = inventory.find((item) => String(item.id) === String(listingDraft.inventoryId));
    const listingQuantity = Number(listingDraft.quantity);
    const availableForListing = selectedInventory
      ? Math.max(0, Number(selectedInventory.quantity_on_hand || 0) - Number(selectedInventory.quantity_reserved || 0))
      : 0;
    if (!Number.isInteger(listingQuantity) || listingQuantity < 1) {
      showNotice("error", "Listing quantity must be a whole number of one or greater.");
      return;
    }
    if (listingQuantity > availableForListing) {
      showNotice("error", `Listing quantity cannot exceed ${availableForListing} currently available.`);
      return;
    }

    if (listingDraft.shippingMode === "profile" && !listingDraft.shippingProfileId) {
      showNotice("error", "Choose a shipping profile.");
      return;
    }

    setBusyId("new-listing");
    try {
      const { error } = await supabase.rpc("create_seller_listing", {
        p_inventory_id: listingDraft.inventoryId,
        p_price_amount: price,
        p_listing_quantity: listingQuantity,
        p_title_override: null,
        p_description_override: null,
        p_accepts_offers: listingDraft.acceptsOffers,
        p_minimum_offer_amount: listingDraft.acceptsOffers
          ? toNumberOrNull(listingDraft.minimumOffer)
          : null,
        p_condition_override: null,
        p_shipping_mode: listingDraft.shippingMode,
        p_shipping_profile_id:
          listingDraft.shippingMode === "profile" ? listingDraft.shippingProfileId : null,
        p_shipping_amount:
          listingDraft.shippingMode === "profile"
            ? toNumberOrNull(listingDraft.shippingAmount)
            : null,
        p_local_pickup_available:
          listingDraft.shippingMode === "pickup" ? true : listingDraft.localPickup,
        p_combined_shipping_allowed:
          listingDraft.shippingMode === "pickup" ? false : listingDraft.combinedShipping,
        p_publish_now: listingDraft.publishNow,
        p_attributes: {},
      });

      if (error) throw error;

      showNotice(
        "success",
        listingDraft.publishNow ? "Listing created and published." : "Draft listing created."
      );
      setListingDraft({
        inventoryId: "",
        price: "",
        quantity: "1",
        acceptsOffers: false,
        minimumOffer: "",
        shippingMode: activeProfiles.some((profile) => profile.is_default) ? "profile" : "free",
        shippingProfileId: activeProfiles.find((profile) => profile.is_default)?.id || "",
        shippingAmount: "",
        localPickup: false,
        combinedShipping: true,
        publishNow: true,
      });
      await loadDashboard(true);
    } catch (error) {
      console.error("CREATE SELLER LISTING ERROR:", error);
      showNotice("error", errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const saveShopSettings = async (event) => {
    event.preventDefault();

    if (!shopDraft.name.trim()) {
      showNotice("error", "Shop name is required.");
      return;
    }

    setBusyId("shop-settings");
    try {
      const { data, error } = await supabase.rpc("update_shop_settings", {
        p_shop_id: shopId,
        p_name: shopDraft.name.trim(),
        p_description: shopDraft.description.trim() || null,
        p_visibility: shopDraft.visibility,
      });

      if (error) throw error;
      setShop(data || { ...shop, ...shopDraft });
      showNotice("success", "Shop settings saved.");
      await loadDashboard(true);
    } catch (error) {
      console.error("UPDATE SHOP SETTINGS ERROR:", error);
      showNotice("error", errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const saveShopSlug = async () => {
    if (!slugCheck?.available) {
      showNotice("error", "Choose an available shop URL before saving.");
      return;
    }

    setBusyId("shop-slug");
    try {
      const { data, error } = await supabase.rpc("change_shop_slug", {
        p_shop_id: shopId,
        p_slug: slugDraft,
      });

      if (error) throw error;
      setShop(data || shop);
      setSlugDraft(data?.slug || slugCheck.normalized_slug || slugDraft);
      setSlugCheck(null);
      showNotice("success", "Shop URL updated. Your previous URL is preserved for redirect.");
      await loadDashboard(true);
    } catch (error) {
      console.error("CHANGE SHOP SLUG ERROR:", error);
      showNotice("error", errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const copyShopUrl = async () => {
    const url = `${window.location.origin}/${shop?.slug || ""}`;
    try {
      await navigator.clipboard.writeText(url);
      showNotice("success", "Shop URL copied.");
    } catch {
      showNotice("error", "Unable to copy the shop URL.");
    }
  };

  const saveVisibility = async (visibility) => {
    if (!shopId) return;
    setBusyId("visibility");
    try {
      const { data, error } = await supabase.rpc("set_shop_visibility", {
        p_shop_id: shopId,
        p_visibility: visibility,
      });
      if (error) throw error;
      setShop(data || { ...shop, visibility });
      showNotice("success", visibility === "public" ? "Shop is now public." : "Shop is now visible to Hobbli members only.");
    } catch (error) {
      console.error("SHOP VISIBILITY ERROR:", error);
      showNotice("error", errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const loadCollectionItems = useCallback(async () => {
    setCollectionLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      const userId = authData?.user?.id;
      if (!userId) throw new Error("You must be signed in to load your collection.");

      const { data, error } = await supabase
        .from("user_items")
        .select(`
          id,
          item_id,
          user_id,
          user_specific_name,
          user_specific_brand,
          user_specific_set_name,
          user_specific_year,
          condition,
          created_at,
          items (
            id, name, brand, set_name, year, card_number
          ),
          item_images (
            id, storage_provider, storage_bucket, object_key, position
          )
        `)
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setCollectionItems(
        (data || []).map((item) => ({
          ...item,
          item_images: [...(item.item_images || [])].sort(
            (a, b) => Number(a.position || 0) - Number(b.position || 0)
          ),
        }))
      );
    } catch (error) {
      console.error("LOAD COLLECTION FOR INVENTORY ERROR:", error);
      showNotice("error", errorMessage(error, "Unable to load your collection."));
    } finally {
      setCollectionLoading(false);
    }
  }, [showNotice]);

  useEffect(() => {
    if (tab === "inventory" && inventoryAddMode === "collection" && !collectionItems.length) {
      loadCollectionItems();
    }
  }, [tab, inventoryAddMode, collectionItems.length, loadCollectionItems]);

  const collectionInventoryMap = useMemo(() => {
    const map = new Map();
    inventory.forEach((item) => {
      if (item.source_user_item_id && item.status !== "archived") {
        map.set(String(item.source_user_item_id), item);
      }
    });
    return map;
  }, [inventory]);

  const filteredCollectionItems = useMemo(() => {
    const term = collectionSearch.trim().toLowerCase();
    if (!term) return collectionItems;

    return collectionItems.filter((item) => {
      const haystack = [
        item.id,
        item.user_specific_name,
        item.user_specific_brand,
        item.user_specific_set_name,
        item.user_specific_year,
        item.condition,
        item.items?.name,
        item.items?.brand,
        item.items?.set_name,
        item.items?.year,
        item.items?.card_number,
      ].filter((value) => value !== null && value !== undefined).join(" ").toLowerCase();

      return haystack.includes(term);
    });
  }, [collectionItems, collectionSearch]);

  const selectableCollectionItems = useMemo(
    () => filteredCollectionItems.filter(
      (item) => !collectionInventoryMap.has(String(item.id))
    ),
    [filteredCollectionItems, collectionInventoryMap]
  );

  const toggleCollectionSelection = (itemId) => {
    const key = String(itemId);
    if (collectionInventoryMap.has(key)) return;

    setSelectedCollectionIds((current) => {
      const next = new Set(current);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAllVisibleCollection = () => {
    setSelectedCollectionIds((current) => {
      const next = new Set(current);
      selectableCollectionItems.forEach((item) => next.add(String(item.id)));
      return next;
    });
  };

  const clearCollectionSelection = () => setSelectedCollectionIds(new Set());

  const addSelectedCollectionInventory = async () => {
    const selectedItems = collectionItems.filter((item) =>
      selectedCollectionIds.has(String(item.id))
    );

    if (!selectedItems.length) {
      showNotice("error", "Choose at least one collection item.");
      return;
    }

    setBusyId("collection-import");

    const results = [];
    for (const item of selectedItems) {
      const title = [
        item.user_specific_year ?? item.items?.year,
        item.user_specific_brand || item.items?.brand,
        item.user_specific_set_name || item.items?.set_name,
        item.user_specific_name || item.items?.name,
      ].filter(Boolean).join(" ");

      const { data, error } = await supabase.rpc("create_inventory_from_collection", {
        p_shop_id: shopId,
        p_source_user_item_id: item.id,
        p_item_id: item.item_id || null,
        p_category_id: null,
        p_subcategory_id: null,
        p_inventory_type: "unique",
        p_title: title || collectionItemTitle(item),
        p_description: null,
        p_condition: item.condition || null,
        p_quantity_on_hand: 1,
        p_acquisition_cost_amount: null,
        p_acquisition_currency: "USD",
        p_attributes: {},
      });

      results.push({ item, data, error });
    }

    const succeeded = results.filter((result) => !result.error);
    const failed = results.filter((result) => result.error);

    if (succeeded.length) {
      setSelectedCollectionIds((current) => {
        const next = new Set(current);
        succeeded.forEach(({ item }) => next.delete(String(item.id)));
        return next;
      });
      await loadDashboard(true);
    }

    if (!failed.length) {
      showNotice(
        "success",
        `${succeeded.length} collection item${succeeded.length === 1 ? "" : "s"} added to seller inventory.`
      );
    } else if (succeeded.length) {
      console.error("COLLECTION INVENTORY PARTIAL FAILURES:", failed);
      showNotice(
        "error",
        `${succeeded.length} added; ${failed.length} failed. First error: ${errorMessage(failed[0].error)}`
      );
    } else {
      console.error("CREATE COLLECTION INVENTORY ERROR:", failed);
      showNotice("error", errorMessage(failed[0]?.error, "Unable to add collection inventory."));
    }

    setBusyId(null);
  };

  const addShopInventory = async (event) => {
    event.preventDefault();
    if (!inventoryDraft.title.trim()) {
      showNotice("error", "Item title is required.");
      return;
    }

    setBusyId("new-inventory");
    try {
      const { error } = await supabase.rpc("create_shop_inventory", {
        p_shop_id: shopId,
        p_inventory_type: "quantity",
        p_title: inventoryDraft.title.trim(),
        p_description: inventoryDraft.description.trim() || null,
        p_condition: inventoryDraft.condition.trim() || null,
        p_quantity_on_hand: Math.max(1, Number(inventoryDraft.quantity || 1)),
        p_acquisition_cost_amount: toNumberOrNull(inventoryDraft.cost),
        p_acquisition_currency: "USD",
        p_attributes: {},
      });
      if (error) throw error;
      setInventoryDraft({ title: "", description: "", condition: "", quantity: "1", cost: "" });
      showNotice("success", "Shop inventory item added.");
      await loadDashboard(true);
    } catch (error) {
      console.error("CREATE SHOP INVENTORY ERROR:", error);
      showNotice("error", errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const beginInventoryEdit = (item) => {
    setEditingInventoryId(item.id);
    setInventoryEditDraft({
      title: item.title || "",
      description: item.description || "",
      condition: item.condition || "",
      quantity: String(item.quantity_on_hand ?? 0),
      cost: item.acquisition_cost_amount === null || item.acquisition_cost_amount === undefined
        ? ""
        : String(item.acquisition_cost_amount),
    });
  };

  const cancelInventoryEdit = () => {
    setEditingInventoryId(null);
    setInventoryEditDraft({
      title: "",
      description: "",
      condition: "",
      quantity: "1",
      cost: "",
    });
  };

  const saveInventoryEdit = async (item) => {
    const title = inventoryEditDraft.title.trim();
    if (!title) {
      showNotice("error", "Item title is required.");
      return;
    }

    const isCollectionBacked = Boolean(item.source_user_item_id);
    const quantity = isCollectionBacked
      ? Number(item.quantity_on_hand || 1)
      : Number(inventoryEditDraft.quantity);

    if (!isCollectionBacked && (!Number.isInteger(quantity) || quantity < 0)) {
      showNotice("error", "Quantity must be a whole number of zero or greater.");
      return;
    }

    if (!isCollectionBacked && item.inventory_type === "unique" && quantity > 1) {
      showNotice("error", "This unique inventory item cannot have quantity greater than one.");
      return;
    }

    const reserved = Number(item.quantity_reserved || 0);
    if (!isCollectionBacked && quantity < reserved) {
      showNotice("error", `Quantity cannot be below the ${reserved} currently reserved.`);
      return;
    }

    const cost = toNumberOrNull(inventoryEditDraft.cost);
    if (cost !== null && cost < 0) {
      showNotice("error", "Acquisition cost cannot be negative.");
      return;
    }

    setBusyId(`edit-${item.id}`);

    try {
      const { error: updateError } = await supabase.rpc("update_inventory", {
        p_inventory_id: item.id,
        p_patch: {
          title,
          description: inventoryEditDraft.description.trim() || null,
          condition: inventoryEditDraft.condition.trim() || null,
          acquisition_cost_amount: cost,
          acquisition_currency: item.acquisition_currency || "USD",
        },
      });
      if (updateError) throw updateError;

      if (!isCollectionBacked && quantity !== Number(item.quantity_on_hand || 0)) {
        const { error: quantityError } = await supabase.rpc("adjust_inventory_quantity", {
          p_inventory_id: item.id,
          p_quantity_on_hand: quantity,
        });
        if (quantityError) throw quantityError;
      }

      cancelInventoryEdit();
      showNotice("success", "Inventory updated.");
      await loadDashboard(true);
    } catch (error) {
      console.error("EDIT INVENTORY ERROR:", error);
      showNotice("error", errorMessage(error, "Unable to update inventory."));
    } finally {
      setBusyId(null);
    }
  };

  const updateInventoryStatus = async (item, status) => {
    setBusyId(item.id);
    try {
      const { error } = await supabase.rpc("update_inventory", {
        p_inventory_id: item.id,
        p_patch: { status },
      });
      if (error) throw error;
      showNotice("success", status === "archived" ? "Inventory archived." : status === "active" ? "Inventory restored and active." : "Inventory updated.");
      await loadDashboard(true);
    } catch (error) {
      console.error("UPDATE INVENTORY ERROR:", error);
      showNotice("error", errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const changeListingStatus = async (listing, nextStatus) => {
    setBusyId(listing.id);
    try {
      const rpc = nextStatus === "published" ? "publish_listing" : "set_listing_status";
      const args = nextStatus === "published"
        ? { p_listing_id: listing.id }
        : { p_listing_id: listing.id, p_status: nextStatus };
      const { error } = await supabase.rpc(rpc, args);
      if (error) throw error;
      showNotice("success", nextStatus === "published" ? "Listing published." : `Listing ${nextStatus}.`);
      await loadDashboard(true);
    } catch (error) {
      console.error("LISTING STATUS ERROR:", error);
      showNotice("error", errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const beginListingEdit = (listing) => {
    setEditingListingId(listing.id);
    setListingEditDraft({
      price: listing.price_amount === null || listing.price_amount === undefined ? "" : String(listing.price_amount),
      quantity: String(listing.listing_quantity ?? 1),
      acceptsOffers: Boolean(listing.accepts_offers),
      minimumOffer:
        listing.minimum_offer_amount === null || listing.minimum_offer_amount === undefined
          ? ""
          : String(listing.minimum_offer_amount),
      shippingMode: listing.shipping_mode || "free",
      shippingProfileId: listing.shipping_profile_id || "",
      shippingAmount:
        listing.shipping_amount === null || listing.shipping_amount === undefined
          ? ""
          : String(listing.shipping_amount),
      localPickup: Boolean(listing.local_pickup_available),
      combinedShipping: Boolean(listing.combined_shipping_allowed),
    });
  };

  const cancelListingEdit = () => {
    setEditingListingId(null);
    setListingEditDraft({
      price: "",
      quantity: "1",
      acceptsOffers: false,
      minimumOffer: "",
      shippingMode: "free",
      shippingProfileId: "",
      shippingAmount: "",
      localPickup: false,
      combinedShipping: true,
    });
  };

  const saveListingEdit = async (listing) => {
    const price = toNumberOrNull(listingEditDraft.price);
    const quantity = Number(listingEditDraft.quantity);
    const minimumOffer = listingEditDraft.acceptsOffers
      ? toNumberOrNull(listingEditDraft.minimumOffer)
      : null;

    if (price === null || price < 0) {
      showNotice("error", "Enter a valid listing price.");
      return;
    }
    if (!Number.isInteger(quantity) || quantity < 1) {
      showNotice("error", "Listing quantity must be a whole number of one or greater.");
      return;
    }

    const availableForListing =
      Math.max(
        0,
        Number(listing.seller_inventory?.quantity_on_hand || 0) -
          Number(listing.seller_inventory?.quantity_reserved || 0)
      );
    if (quantity > availableForListing) {
      showNotice("error", `Listing quantity cannot exceed ${availableForListing} currently available.`);
      return;
    }

    if (minimumOffer !== null && (minimumOffer < 0 || minimumOffer > price)) {
      showNotice("error", "Minimum offer must be between $0 and the listing price.");
      return;
    }

    if (listingEditDraft.shippingMode === "profile" && !listingEditDraft.shippingProfileId) {
      showNotice("error", "Choose a shipping profile.");
      return;
    }

    setBusyId(`listing-edit-${listing.id}`);
    try {
      const { error } = await supabase.rpc("update_listing", {
        p_listing_id: listing.id,
        p_patch: {
          price_amount: price,
          listing_quantity: quantity,
          accepts_offers: listingEditDraft.acceptsOffers,
          minimum_offer_amount: minimumOffer,
          shipping_mode: listingEditDraft.shippingMode,
          shipping_profile_id:
            listingEditDraft.shippingMode === "profile"
              ? listingEditDraft.shippingProfileId
              : null,
          shipping_amount:
            listingEditDraft.shippingMode === "profile"
              ? toNumberOrNull(listingEditDraft.shippingAmount)
              : listingEditDraft.shippingMode === "free"
                ? 0
                : null,
          local_pickup_available:
            listingEditDraft.shippingMode === "pickup"
              ? true
              : listingEditDraft.localPickup,
          combined_shipping_allowed:
            listingEditDraft.shippingMode === "pickup"
              ? false
              : listingEditDraft.combinedShipping,
        },
      });
      if (error) throw error;

      cancelListingEdit();
      showNotice("success", listing.status === "published" ? "Live listing updated." : "Listing updated.");
      await loadDashboard(true);
    } catch (error) {
      console.error("EDIT LISTING ERROR:", error);
      showNotice("error", errorMessage(error, "Unable to update the listing."));
    } finally {
      setBusyId(null);
    }
  };

  const createShippingProfile = async (event) => {
    event.preventDefault();
    if (!profileDraft.name.trim()) {
      showNotice("error", "Shipping profile name is required.");
      return;
    }

    const IN_TO_MM = 25.4;
    const OZ_TO_G = 28.349523125;
    setBusyId("new-profile");
    try {
      const { error } = await supabase.rpc("create_shipping_profile", {
        p_seller_account_id: sellerAccountId,
        p_name: profileDraft.name.trim(),
        p_description: profileDraft.description.trim() || null,
        p_package_length_mm: toNumberOrNull(profileDraft.lengthIn) === null ? null : Number(profileDraft.lengthIn) * IN_TO_MM,
        p_package_width_mm: toNumberOrNull(profileDraft.widthIn) === null ? null : Number(profileDraft.widthIn) * IN_TO_MM,
        p_package_height_mm: toNumberOrNull(profileDraft.heightIn) === null ? null : Number(profileDraft.heightIn) * IN_TO_MM,
        p_tare_weight_grams: toNumberOrNull(profileDraft.tareOz) === null ? null : Number(profileDraft.tareOz) * OZ_TO_G,
        p_default_shipping_amount: profileDraft.freeShipping ? 0 : toNumberOrNull(profileDraft.shippingAmount),
        p_currency: "USD",
        p_free_shipping: profileDraft.freeShipping,
        p_local_pickup: profileDraft.localPickup,
        p_combined_shipping: profileDraft.combinedShipping,
        p_is_default: profileDraft.isDefault,
      });
      if (error) throw error;
      setProfileDraft({
        name: "", description: "", shippingAmount: "", tareOz: "", lengthIn: "",
        widthIn: "", heightIn: "", freeShipping: false, localPickup: false,
        combinedShipping: true, isDefault: false,
      });
      showNotice("success", "Shipping profile created.");
      await loadDashboard(true);
    } catch (error) {
      console.error("CREATE SHIPPING PROFILE ERROR:", error);
      showNotice("error", errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const archiveProfile = async (profile) => {
    setBusyId(profile.id);
    try {
      const { error } = await supabase.rpc("archive_shipping_profile", { p_profile_id: profile.id });
      if (error) throw error;
      showNotice("success", "Shipping profile archived.");
      await loadDashboard(true);
    } catch (error) {
      console.error("ARCHIVE SHIPPING PROFILE ERROR:", error);
      showNotice("error", errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  if (!canSell) {
    return (
      <div className="seller-gate">
        <div className="seller-eyebrow">HOBBLI SELLER ACCESS</div>
        <h1>Sell on Hobbli</h1>
        <p>Seller onboarding is currently available only to the private Hobbli marketplace pilot.</p>
        <p>Before selling, you’ll review the marketplace seller rules, agree to the current seller terms, and finish your shop setup.</p>
        <button className="seller-btn seller-btn-primary" onClick={() => navigate("/shop")}>Back to Marketplace</button>
      </div>
    );
  }

  if (loading) {
    return <div className="seller-loading">Loading Seller Dashboard…</div>;
  }

  return (
    <div className="seller-dashboard">
      {notice && <div className={`seller-notice ${notice.type}`} role="status">{notice.message}</div>}

      <header className="seller-hero">
        <div>
          <div className="seller-eyebrow">HOBBLI SELLER</div>
          <h1>{shop?.name || "Seller Dashboard"}</h1>
          <div className="seller-hero-meta">
            <span className={`seller-pill ${shop?.status === "active" ? "good" : ""}`}>{shop?.status || "Unknown"}</span>
            <span className="seller-pill">{shop?.visibility === "members" ? "Members only" : "Public"}</span>
            <span className="seller-slug">hobbli.com/{shop?.slug || "shop"}</span>
          </div>
        </div>
        <div className="seller-hero-actions">
          <button className="seller-btn" disabled={refreshing} onClick={() => loadDashboard(true)}>{refreshing ? "Refreshing…" : "Refresh"}</button>
          <button
            className="seller-btn seller-btn-primary"
            disabled={!shop?.slug}
            onClick={() => shop?.slug && navigate(`/${shop.slug}`)}
          >
            View My Shop
          </button>
          <button className="seller-btn" onClick={() => navigate("/shop")}>View Marketplace</button>
        </div>
      </header>

      <nav className="seller-tabs" aria-label="Seller dashboard sections">
        {TABS.map(([key, label]) => (
          <button key={key} className={tab === key ? "active" : ""} onClick={() => setTab(key)}>{label}</button>
        ))}
      </nav>

      {tab === "overview" && (
        <section className="seller-section">
          <div className="seller-stat-grid">
            <article className="seller-stat"><span>Active listings</span><strong>{activeCount}</strong><small>{unlimited ? "Unlimited plan" : `${usage?.remaining_listings ?? Math.max(Number(listingLimit || 0) - activeCount, 0)} remaining`}</small></article>
            <article className="seller-stat"><span>Inventory items</span><strong>{stats.inventory}</strong><small>{stats.collectionBacked} collection · {stats.shopOnly} shop-only</small></article>
            <article className="seller-stat"><span>Draft listings</span><strong>{stats.drafts}</strong><small>{stats.paused} paused</small></article>
            <article className="seller-stat"><span>Shipping profiles</span><strong>{profiles.filter((x) => x.is_active).length}</strong><small>{profiles.find((x) => x.is_default && x.is_active)?.name || "No default selected"}</small></article>
          </div>

          <div className="seller-card">
            <div className="seller-card-heading"><div><h2>Listing capacity</h2><p>Only published listings count toward your plan limit.</p></div><strong>{activeCount} / {unlimited ? "∞" : listingLimit ?? "—"}</strong></div>
            {!unlimited && <div className="seller-progress"><span style={{ width: `${usagePct}%` }} /></div>}
          </div>

          <div className="seller-card">
            <div className="seller-card-heading"><div><h2>Seller capabilities</h2><p>Effective entitlements after plan and shop overrides.</p></div></div>
            <div className="seller-entitlements">
              {entitlements.map((item) => <div key={item.feature_key}><span>{item.feature_name}</span><strong>{typeof item.effective_value === "boolean" ? (item.effective_value ? "Enabled" : "Not included") : String(item.effective_value)}</strong></div>)}
            </div>
          </div>
        </section>
      )}

      {tab === "inventory" && (
        <section className="seller-section">
          <div className="seller-card">
            <div className="seller-card-heading">
              <div>
                <h2>Add inventory</h2>
                <p>Bring items in from your Hobbli collection or create inventory that exists only in your shop.</p>
              </div>
              <div className="seller-segmented seller-inventory-add-toggle">
                <button type="button" className={inventoryAddMode === "shop" ? "active" : ""} onClick={() => setInventoryAddMode("shop")}>Shop Inventory</button>
                <button type="button" className={inventoryAddMode === "collection" ? "active" : ""} onClick={() => setInventoryAddMode("collection")}>From Collection</button>
              </div>
            </div>

            {inventoryAddMode === "collection" ? (
              <div className="seller-collection-picker">
                <div className="seller-collection-toolbar">
                  <input
                    className="seller-search-input"
                    value={collectionSearch}
                    onChange={(e) => setCollectionSearch(e.target.value)}
                    placeholder="Search name, year, brand, set, card number…"
                  />
                  <button className="seller-btn" type="button" disabled={collectionLoading} onClick={loadCollectionItems}>
                    {collectionLoading ? "Loading…" : "Refresh Collection"}
                  </button>
                </div>

                <div className="seller-collection-summary">
                  <span>{filteredCollectionItems.length} shown · {selectedCollectionIds.size} selected</span>
                  <div className="seller-row-actions">
                    <button className="seller-link-btn" type="button" disabled={!selectableCollectionItems.length} onClick={selectAllVisibleCollection}>Select all shown</button>
                    <button className="seller-link-btn" type="button" disabled={!selectedCollectionIds.size} onClick={clearCollectionSelection}>Clear</button>
                  </div>
                </div>

                <div className="seller-collection-list">
                  {filteredCollectionItems.map((item) => {
                    const existing = collectionInventoryMap.get(String(item.id));
                    const selected = selectedCollectionIds.has(String(item.id));
                    const primaryImage = item.item_images?.[0];
                    const imageUrl = resolveImageUrl(primaryImage);

                    return (
                      <button
                        type="button"
                        key={item.id}
                        className={`seller-collection-item ${selected ? "selected" : ""} ${existing ? "disabled" : ""}`}
                        onClick={() => toggleCollectionSelection(item.id)}
                        disabled={Boolean(existing)}
                      >
                        <span className="seller-collection-check">
                          {existing ? "✓" : <input type="checkbox" tabIndex="-1" readOnly checked={selected} />}
                        </span>
                        <span className="seller-collection-thumb">
                          {imageUrl ? <img src={imageUrl} alt="" loading="lazy" /> : <span>No image</span>}
                        </span>
                        <span className="seller-collection-copy">
                          <strong>{collectionItemTitle(item)}</strong>
                          <span>{collectionItemMeta(item) || "Collection item"}</span>
                          <small>{item.item_id ? `Canonical #${item.item_id}` : "Not yet matched to a canonical item"}{item.condition ? ` · ${item.condition}` : ""}</small>
                        </span>
                        <span className="seller-collection-state">
                          {existing ? "Already in Inventory" : selected ? "Selected" : "Add"}
                        </span>
                      </button>
                    );
                  })}

                  {!collectionLoading && !filteredCollectionItems.length && (
                    <div className="seller-empty">No collection items match your search.</div>
                  )}
                  {collectionLoading && !collectionItems.length && (
                    <div className="seller-empty">Loading your collection…</div>
                  )}
                </div>

                <div className="seller-collection-footer">
                  <span>Collection images are linked into seller inventory automatically; R2 files are not duplicated.</span>
                  <button
                    className="seller-btn seller-btn-primary"
                    type="button"
                    disabled={!selectedCollectionIds.size || busyId === "collection-import"}
                    onClick={addSelectedCollectionInventory}
                  >
                    {busyId === "collection-import"
                      ? "Adding…"
                      : `Add Selected to Inventory${selectedCollectionIds.size ? ` (${selectedCollectionIds.size})` : ""}`}
                  </button>
                </div>
              </div>
            ) : (
              <form className="seller-form seller-form-grid" onSubmit={addShopInventory}>
                <label>Item title<input value={inventoryDraft.title} onChange={(e) => setInventoryDraft({ ...inventoryDraft, title: e.target.value })} placeholder="e.g. 2024 Topps Chrome Caleb Williams" /></label>
                <label>Condition<input value={inventoryDraft.condition} onChange={(e) => setInventoryDraft({ ...inventoryDraft, condition: e.target.value })} placeholder="Raw, NM, PSA 10…" /></label>
                <label>Quantity<input type="number" min="1" value={inventoryDraft.quantity} onChange={(e) => setInventoryDraft({ ...inventoryDraft, quantity: e.target.value })} /></label>
                <label>Acquisition cost<input type="number" min="0" step="0.01" value={inventoryDraft.cost} onChange={(e) => setInventoryDraft({ ...inventoryDraft, cost: e.target.value })} placeholder="Optional" /></label>
                <label className="seller-form-wide">Description<textarea value={inventoryDraft.description} onChange={(e) => setInventoryDraft({ ...inventoryDraft, description: e.target.value })} /></label>
                <div className="seller-form-wide seller-form-actions"><button className="seller-btn seller-btn-primary" disabled={busyId === "new-inventory"}>{busyId === "new-inventory" ? "Adding…" : "Add Inventory Item"}</button></div>
              </form>
            )}
          </div>

          <div className="seller-card seller-table-card">
            <div className="seller-card-heading">
              <div>
                <h2>Inventory</h2>
                <p>{stats.inventory} current item{stats.inventory === 1 ? "" : "s"} across collection-backed and shop-only stock{stats.archivedInventory ? ` · ${stats.archivedInventory} archived` : ""}.</p>
              </div>
              <div className="seller-segmented">
                <button type="button" className={inventoryView === "current" ? "active" : ""} onClick={() => setInventoryView("current")}>Current ({stats.inventory})</button>
                <button type="button" className={inventoryView === "archived" ? "active" : ""} onClick={() => setInventoryView("archived")}>Archived ({stats.archivedInventory})</button>
              </div>
            </div>
            <div className="seller-table-wrap"><table className="seller-table"><thead><tr><th>Image</th><th>Item</th><th>Source</th><th>Qty</th><th>Cost</th><th>Status</th><th>Listing</th><th>Listed on</th><th>Actions</th></tr></thead><tbody>
              {visibleInventory.map((item) => {
                const currentListing = listingByInventoryId.get(item.id);
                const ebayListing = ebayListingForInventory(item.id);
                const isEditing = editingInventoryId === item.id;
                const editBusy = busyId === `edit-${item.id}`;

                return (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          className="seller-inventory-thumb"
                          onClick={() => setImageManagerInventoryId(item.id)}
                          title="Manage images"
                        >
                          {resolveImageUrl(primaryInventoryImage(item)) ? (
                            <img src={resolveImageUrl(primaryInventoryImage(item))} alt="" />
                          ) : (
                            <span>No image</span>
                          )}
                        </button>
                      </td>
                      <td>
                        <strong>{item.title || `Canonical item #${item.item_id}`}</strong>
                        <small>{item.condition || "No condition"}</small>
                      </td>
                      <td>{item.source_user_item_id ? "Collection" : "Shop"}</td>
                      <td>{item.quantity_on_hand} <small>({item.quantity_reserved || 0} reserved)</small></td>
                      <td>{money(item.acquisition_cost_amount, item.acquisition_currency || "USD")}</td>
                      <td><span className={`seller-status ${item.status}`}>{item.status}</span></td>
                      <td>
                        {currentListing ? <span className={`seller-status ${currentListing.status}`}>{currentListing.status}</span> : <small>No Hobbli listing</small>}
                        {ebayListing && (
                          <div className="seller-ebay-price">
                            <span>eBay asking</span>
                            <strong>{ebayListing.external_price == null ? "Not available" : money(ebayListing.external_price, ebayListing.currency || "USD")}</strong>
                            <small>Imported snapshot · sync off</small>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="seller-marketplace-badges">
                          {currentListing?.status === "published" && <span className="seller-marketplace-badge">Hobbli</span>}
                          {(channelLinksByInventory.get(String(item.id)) || []).filter((link) =>
                            ["active", "published"].includes(String(link.listing_status).toLowerCase())
                          ).map((link) => <span key={link.id} className="seller-marketplace-badge ebay">eBay</span>)}
                          {!channelLinksLoaded ? <small>Checking…</small> :
                            !(currentListing?.status === "published") &&
                            !(channelLinksByInventory.get(String(item.id)) || []).some((link) =>
                              ["active", "published"].includes(String(link.listing_status).toLowerCase())
                            ) ? <small>Not listed</small> : null}
                        </div>
                      </td>
                      <td>
                        <div className="seller-row-actions">
                          <button
                            className="seller-btn seller-btn-small"
                            type="button"
                            disabled={busyId === item.id || editBusy}
                            onClick={() => isEditing ? cancelInventoryEdit() : beginInventoryEdit(item)}
                          >
                            {isEditing ? "Cancel" : "Edit"}
                          </button>
                          <button
                            className="seller-btn seller-btn-small"
                            type="button"
                            disabled={editBusy}
                            onClick={() => setImageManagerInventoryId(item.id)}
                          >
                            Images{item.inventory_images?.length ? ` (${item.inventory_images.length})` : ""}
                          </button>
                          {item.status === "draft" && <button className="seller-btn seller-btn-small" type="button" disabled={busyId === item.id || editBusy} onClick={() => updateInventoryStatus(item, "active")}>Activate</button>}
                          {item.status === "active" && !currentListing && <button className="seller-btn seller-btn-small seller-btn-primary" type="button" disabled={editBusy} onClick={() => beginListing(item)}>List</button>}
                          {item.status !== "archived" && <button className="seller-btn seller-btn-small seller-btn-danger" type="button" disabled={busyId === item.id || editBusy} onClick={() => updateInventoryStatus(item, "archived")}>Archive</button>}
                          {item.status === "archived" && <button className="seller-btn seller-btn-small" type="button" disabled={busyId === item.id || editBusy} onClick={() => updateInventoryStatus(item, "active")}>Restore</button>}
                        </div>
                      </td>
                    </tr>

                    {isEditing && (
                      <tr className="seller-inventory-edit-row">
                        <td colSpan="9">
                          <div className="seller-inventory-editor">
                            <div className="seller-inventory-editor-heading">
                              <div>
                                <strong>Edit inventory</strong>
                                <span>
                                  {item.source_user_item_id
                                    ? "Seller-specific details only. Your collection and canonical card are not changed."
                                    : "Update the working details for this shop inventory item."}
                                </span>
                              </div>
                              {currentListing && (
                                <span className="seller-edit-note">
                                  Changes to inventory details and availability can affect the current listing.
                                </span>
                              )}
                            </div>

                            <div className="seller-form seller-form-grid">
                              <label>
                                Item title
                                <input
                                  value={inventoryEditDraft.title}
                                  onChange={(e) => setInventoryEditDraft({ ...inventoryEditDraft, title: e.target.value })}
                                />
                              </label>

                              <label>
                                Condition
                                <input
                                  value={inventoryEditDraft.condition}
                                  onChange={(e) => setInventoryEditDraft({ ...inventoryEditDraft, condition: e.target.value })}
                                  placeholder="Raw, NM, PSA 10…"
                                />
                              </label>

                              {item.source_user_item_id ? (
                                <div className="seller-collection-quantity">
                                  <span className="seller-field-label">Quantity</span>
                                  <div className="seller-readonly-quantity">{item.quantity_on_hand ?? 1}</div>
                                  <small>
                                    Collection-backed inventory represents this specific item in your collection.
                                    To sell another copy, add that copy from your collection as additional inventory,
                                    or create a new Shop Inventory item to sell multiple identical copies.
                                  </small>
                                </div>
                              ) : (
                                <label>
                                  Quantity
                                  <input
                                    type="number"
                                    min={Math.max(0, Number(item.quantity_reserved || 0))}
                                    max={item.inventory_type === "unique" ? 1 : undefined}
                                    step="1"
                                    value={inventoryEditDraft.quantity}
                                    onChange={(e) => setInventoryEditDraft({ ...inventoryEditDraft, quantity: e.target.value })}
                                  />
                                  <small>
                                    {item.inventory_type === "unique"
                                      ? "This inventory item is configured as unique and is limited to one."
                                      : `${item.quantity_reserved || 0} currently reserved.`}
                                  </small>
                                </label>
                              )}

                              <label>
                                Acquisition cost
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={inventoryEditDraft.cost}
                                  onChange={(e) => setInventoryEditDraft({ ...inventoryEditDraft, cost: e.target.value })}
                                  placeholder="Optional"
                                />
                              </label>

                              <label className="seller-form-wide">
                                Description
                                <textarea
                                  value={inventoryEditDraft.description}
                                  onChange={(e) => setInventoryEditDraft({ ...inventoryEditDraft, description: e.target.value })}
                                />
                              </label>

                              <div className="seller-form-wide seller-form-actions seller-edit-actions">
                                <button className="seller-btn" type="button" disabled={editBusy} onClick={cancelInventoryEdit}>Cancel</button>
                                <button className="seller-btn seller-btn-primary" type="button" disabled={editBusy} onClick={() => saveInventoryEdit(item)}>
                                  {editBusy ? "Saving…" : "Save Changes"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!visibleInventory.length && <tr><td colSpan="9" className="seller-empty">{inventoryView === "archived" ? "No archived inventory." : "No current seller inventory yet."}</td></tr>}
            </tbody></table></div>
          </div>
        </section>
      )}

      {tab === "listings" && (
        <section className="seller-section seller-listings-manager">
          <div className="seller-card seller-table-card seller-unified-listings">
            <div className="seller-card-heading">
              <div>
                <h2>Listings</h2>
                <p>Current publications and preserved listing history.</p>
              </div>
              <div className="seller-segmented">
                <button className={listingView === "current" ? "active" : ""} onClick={() => setListingView("current")}>Current</button>
                <button className={listingView === "history" ? "active" : ""} onClick={() => setListingView("history")}>History</button>
              </div>
            </div>

            <div className="seller-unified-filter-panel">
              {listingFilterControls}
              <div className="seller-listing-counts">
                <span><strong>{listings.filter((l) => l.status === "published").length}</strong> Published</span>
                <span><strong>{listings.filter((l) => l.status === "draft").length}</strong> Draft</span>
                <span><strong>{listings.filter((l) => l.status === "paused").length}</strong> Paused</span>
                <span><strong>{listableInventory.length}</strong> Unlisted inventory</span>
              </div>
              <small>{visibleListings.length} listings match the current filters</small>
            </div>
            <div className="seller-bulk-status-panel">
              <div className="seller-bulk-status-controls">
                <label>Bulk action <select value={bulkStatusAction} disabled={bulkStatusBusy} onChange={(e) => {setBulkStatusAction(e.target.value);setBulkStatusSelection(new Set());setBulkStatusProgress(null);}}><option value="publish">Publish drafts / paused</option><option value="pause">Unpublish (pause) published</option></select></label>
                <label className="seller-bulk-status-check"><input type="checkbox" disabled={bulkStatusBusy || !bulkStatusEligible.length} checked={bulkStatusEligible.length > 0 && bulkStatusEligible.every((l)=>bulkStatusSelection.has(String(l.id)))} onChange={(e)=>setBulkStatusSelection(e.target.checked ? new Set(bulkStatusEligible.map((l)=>String(l.id))) : new Set())}/> Select all {bulkStatusEligible.length} eligible filtered listings</label>
                <button type="button" className="seller-btn" disabled={bulkStatusBusy || !bulkStatusSelection.size} onClick={()=>setBulkStatusSelection(new Set())}>Clear selection</button>
                <button type="button" className="seller-btn seller-btn-primary" disabled={bulkStatusBusy || !bulkStatusSelected.length} onClick={runBulkStatusAction}>{bulkStatusBusy ? "Processing…" : `${bulkStatusAction === "publish" ? "Publish" : "Pause"} ${bulkStatusSelected.length} selected`}</button>
              </div>
              <small>{bulkStatusEligible.length} eligible in current filtered listings. Publishing uses the existing listing; pausing removes it from public sale without deleting it. No eBay changes.</small>
              {bulkStatusProgress && <div className="seller-bulk-results" role="status"><strong>{bulkStatusProgress.completed}/{bulkStatusProgress.total} processed · {bulkStatusProgress.failed} failed</strong>{bulkStatusProgress.failures.map((f)=><p key={f.id}>{f.title}: {f.message}</p>)}</div>}
            </div>
            <div className="seller-table-wrap">
              <table className="seller-table seller-responsive-listings">
                <thead><tr><th>Select</th><th>Listing</th><th>Price</th><th>Qty</th><th>Shipping</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {visibleListings.map((listing) => {
                    const isEditingListing = editingListingId === listing.id;
                    const listingEditBusy = busyId === `listing-edit-${listing.id}`;
                    const inventoryAvailable = Math.max(
                      0,
                      Number(listing.seller_inventory?.quantity_on_hand || 0) -
                        Number(listing.seller_inventory?.quantity_reserved || 0)
                    );
                    const configuredListingQuantity = Math.max(
                      0,
                      Number(listing.listing_quantity || 0)
                    );
                    const effectiveListingAvailability = Math.min(
                      configuredListingQuantity,
                      inventoryAvailable
                    );

                    return (
                      <React.Fragment key={listing.id}>
                        <tr>
                          <td data-label="Select"><input type="checkbox" aria-label={`Select listing ${listing.title_override || listing.seller_inventory?.title || listing.id}`} disabled={bulkStatusBusy || !bulkStatusEligible.some((eligible)=>eligible.id===listing.id)} checked={bulkStatusSelection.has(String(listing.id)) && bulkStatusEligible.some((eligible)=>eligible.id===listing.id)} onChange={(e)=>setBulkStatusSelection((prev)=>{const next=new Set(prev);e.target.checked ? next.add(String(listing.id)) : next.delete(String(listing.id));return next;})}/></td>
                          <td data-label="Listing">
                            <strong>{listing.title_override || listing.seller_inventory?.title || "Untitled listing"}</strong>
                            <small>{listing.seller_inventory?.source_user_item_id ? "Collection" : "Shop inventory"} · {listing.accepts_offers ? "Offers accepted" : "Fixed price"}</small>
                          </td>
                          <td data-label="Price">{money(listing.price_amount, listing.currency || "USD")}</td>
                          <td data-label="Quantity">
                            <strong>{effectiveListingAvailability}</strong>
                            <small>
                              available
                              {configuredListingQuantity !== effectiveListingAvailability
                                ? ` · cap ${configuredListingQuantity}`
                                : ""}
                            </small>
                          </td>
                          <td data-label="Shipping">{listing.shipping_mode === "free" ? "Free" : listing.shipping_mode === "pickup" ? "Pickup" : money(listing.shipping_amount, listing.shipping_currency || "USD")}</td>
                          <td data-label="Status"><span className={`seller-status ${listing.status}`}>{listing.status}</span></td>
                          <td data-label="Actions">
                            <div className="seller-row-actions">
                              {["draft", "published", "paused"].includes(listing.status) && (
                                <button
                                  className="seller-btn seller-btn-small"
                                  type="button"
                                  disabled={busyId === listing.id || listingEditBusy}
                                  onClick={() => isEditingListing ? cancelListingEdit() : beginListingEdit(listing)}
                                >
                                  {isEditingListing ? "Cancel" : "Edit"}
                                </button>
                              )}
                              {["draft", "paused"].includes(listing.status) && <button className="seller-btn seller-btn-small seller-btn-primary" disabled={busyId === listing.id || listingEditBusy} onClick={() => changeListingStatus(listing, "published")}>Publish</button>}
                              {listing.status === "published" && <button className="seller-btn seller-btn-small" disabled={busyId === listing.id || listingEditBusy} onClick={() => changeListingStatus(listing, "paused")}>Pause</button>}
                              {["draft", "published", "paused"].includes(listing.status) && <button className="seller-btn seller-btn-small seller-btn-danger" disabled={busyId === listing.id || listingEditBusy} onClick={() => changeListingStatus(listing, "ended")}>End</button>}
                            </div>
                          </td>
                        </tr>

                        {isEditingListing && (
                          <tr className="seller-listing-edit-row">
                            <td colSpan="7">
                              <div className="seller-listing-editor">
                                <div className="seller-inventory-editor-heading">
                                  <div>
                                    <strong>Edit listing</strong>
                                    <span>
                                      {listing.status === "published"
                                        ? "Changes save directly to this live listing."
                                        : "Update the listing before publishing it."}
                                    </span>
                                  </div>
                                  {listing.status === "published" && (
                                    <span className="seller-live-edit-note">Live listing</span>
                                  )}
                                </div>

                                <div className="seller-form seller-form-grid">
                                  <label>
                                    Price
                                    <input type="number" min="0" step="0.01" value={listingEditDraft.price} onChange={(e) => setListingEditDraft({ ...listingEditDraft, price: e.target.value })} />
                                  </label>

                                  <label>
                                    Listing quantity
                                    <input type="number" min="1" step="1" value={listingEditDraft.quantity} onChange={(e) => setListingEditDraft({ ...listingEditDraft, quantity: e.target.value })} />
                                    <small>
                                      Listing cap. {inventoryAvailable} currently available from inventory
                                      {configuredListingQuantity > inventoryAvailable
                                        ? `; buyers can purchase up to ${effectiveListingAvailability} right now.`
                                        : "."}
                                    </small>
                                  </label>

                                  <label>
                                    Shipping
                                    <select value={listingEditDraft.shippingMode} onChange={(e) => setListingEditDraft({ ...listingEditDraft, shippingMode: e.target.value })}>
                                      <option value="free">Free shipping</option>
                                      <option value="profile" disabled={!activeProfiles.length}>Shipping profile</option>
                                      <option value="pickup">Local pickup only</option>
                                    </select>
                                  </label>

                                  {listingEditDraft.shippingMode === "profile" && (
                                    <label>
                                      Shipping profile
                                      <select value={listingEditDraft.shippingProfileId} onChange={(e) => setListingEditDraft({ ...listingEditDraft, shippingProfileId: e.target.value })}>
                                        <option value="">Choose profile…</option>
                                        {activeProfiles.map((profile) => (
                                          <option key={profile.id} value={profile.id}>{profile.name}{profile.is_default ? " · Default" : ""}</option>
                                        ))}
                                      </select>
                                    </label>
                                  )}

                                  {listingEditDraft.shippingMode === "profile" && (
                                    <label>
                                      Shipping override $
                                      <input type="number" min="0" step="0.01" value={listingEditDraft.shippingAmount} onChange={(e) => setListingEditDraft({ ...listingEditDraft, shippingAmount: e.target.value })} placeholder="Use profile default" />
                                    </label>
                                  )}

                                  <div className="seller-checks seller-form-wide">
                                    <label><input type="checkbox" checked={listingEditDraft.acceptsOffers} onChange={(e) => setListingEditDraft({ ...listingEditDraft, acceptsOffers: e.target.checked })} /> Accept offers</label>
                                    <label><input type="checkbox" checked={listingEditDraft.localPickup} disabled={listingEditDraft.shippingMode === "pickup"} onChange={(e) => setListingEditDraft({ ...listingEditDraft, localPickup: e.target.checked })} /> Also allow local pickup</label>
                                    <label><input type="checkbox" checked={listingEditDraft.combinedShipping} disabled={listingEditDraft.shippingMode === "pickup"} onChange={(e) => setListingEditDraft({ ...listingEditDraft, combinedShipping: e.target.checked })} /> Combined shipping</label>
                                  </div>

                                  {listingEditDraft.acceptsOffers && (
                                    <label>
                                      Minimum offer
                                      <input type="number" min="0" step="0.01" value={listingEditDraft.minimumOffer} onChange={(e) => setListingEditDraft({ ...listingEditDraft, minimumOffer: e.target.value })} placeholder="Optional" />
                                    </label>
                                  )}

                                  <div className="seller-form-wide seller-form-actions seller-edit-actions">
                                    <button className="seller-btn" type="button" disabled={listingEditBusy} onClick={cancelListingEdit}>Cancel</button>
                                    <button className="seller-btn seller-btn-primary" type="button" disabled={listingEditBusy} onClick={() => saveListingEdit(listing)}>
                                      {listingEditBusy ? "Saving…" : listing.status === "published" ? "Save Live Listing" : "Save Listing"}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {!visibleListings.length && <tr><td colSpan="7" className="seller-empty">{listingView === "history" ? "No listing history yet." : "No current listings."}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <details className="seller-listing-creation-details">
            <summary><span><strong>Create new Hobbli listings</strong><small>Expand to create a single listing or list unlisted inventory in bulk</small></span><span className="seller-creation-count">{listableInventory.length} available</span></summary>
            <div className="seller-listing-creation-content">
          <div className="seller-card">
            <div className="seller-card-heading">
              <div>
                <h2>Create listing</h2>
                <p>Choose seller inventory, pricing and an explicit shipping method.</p>
              </div>
              <strong>{listableInventory.length} ready to list</strong>
            </div>

            <form className="seller-form seller-form-grid" onSubmit={createSellerListing}>
              <label className="seller-form-wide">
                Inventory item
                <select value={listingDraft.inventoryId} onChange={(e) => {
                  const inventoryId = e.target.value;
                  const selected = listableInventory.find((item) => String(item.id) === String(inventoryId));
                  const available = selected
                    ? Math.max(1, Number(selected.quantity_on_hand || 0) - Number(selected.quantity_reserved || 0))
                    : 1;
                  setListingDraft({
                    ...listingDraft,
                    inventoryId,
                    price: selected && ebayListingForInventory(selected.id)?.external_price != null
                      ? String(ebayListingForInventory(selected.id).external_price)
                      : "",
                    quantity: String(selected?.inventory_type === "unique" ? 1 : available),
                  });
                }}>
                  <option value="">Choose inventory…</option>
                  {listableInventory.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title || `Canonical item #${item.item_id}`} · {item.source_user_item_id ? "Collection" : "Shop inventory"} · {Math.max(0, Number(item.quantity_on_hand || 0) - Number(item.quantity_reserved || 0))} available
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Hobbli listing price
                <input type="number" min="0" step="0.01" value={listingDraft.price} onChange={(e) => setListingDraft({ ...listingDraft, price: e.target.value })} placeholder="0.00" />
                {ebayListingForInventory(listingDraft.inventoryId) && <small className="seller-ebay-price-help">Suggested from imported eBay asking price. Edit freely; this does not update eBay.</small>}
              </label>

              <label>
                Quantity
                <input type="number" min="1" value={listingDraft.quantity} onChange={(e) => setListingDraft({ ...listingDraft, quantity: e.target.value })} />
              </label>

              <label>
                Shipping
                <select value={listingDraft.shippingMode} onChange={(e) => setListingDraft({ ...listingDraft, shippingMode: e.target.value })}>
                  <option value="free">Free shipping</option>
                  <option value="profile" disabled={!activeProfiles.length}>Shipping profile</option>
                  <option value="pickup">Local pickup only</option>
                </select>
              </label>

              {listingDraft.shippingMode === "profile" && (
                <label>
                  Shipping profile
                  <select value={listingDraft.shippingProfileId} onChange={(e) => setListingDraft({ ...listingDraft, shippingProfileId: e.target.value })}>
                    <option value="">Choose profile…</option>
                    {activeProfiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name}{profile.is_default ? " · Default" : ""}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {listingDraft.shippingMode === "profile" && (
                <label>
                  Shipping override $
                  <input type="number" min="0" step="0.01" value={listingDraft.shippingAmount} onChange={(e) => setListingDraft({ ...listingDraft, shippingAmount: e.target.value })} placeholder="Use profile default" />
                </label>
              )}

              <div className="seller-checks seller-form-wide">
                <label><input type="checkbox" checked={listingDraft.acceptsOffers} onChange={(e) => setListingDraft({ ...listingDraft, acceptsOffers: e.target.checked })} /> Accept offers</label>
                <label><input type="checkbox" checked={listingDraft.localPickup} disabled={listingDraft.shippingMode === "pickup"} onChange={(e) => setListingDraft({ ...listingDraft, localPickup: e.target.checked })} /> Also allow local pickup</label>
                <label><input type="checkbox" checked={listingDraft.combinedShipping} disabled={listingDraft.shippingMode === "pickup"} onChange={(e) => setListingDraft({ ...listingDraft, combinedShipping: e.target.checked })} /> Combined shipping</label>
                <label><input type="checkbox" checked={listingDraft.publishNow} onChange={(e) => setListingDraft({ ...listingDraft, publishNow: e.target.checked })} /> Publish immediately</label>
              </div>

              {listingDraft.acceptsOffers && (
                <label>
                  Minimum offer
                  <input type="number" min="0" step="0.01" value={listingDraft.minimumOffer} onChange={(e) => setListingDraft({ ...listingDraft, minimumOffer: e.target.value })} placeholder="Optional" />
                </label>
              )}

              <div className="seller-form-wide seller-form-actions">
                <button className="seller-btn seller-btn-primary" disabled={busyId === "new-listing" || !listableInventory.length}>
                  {busyId === "new-listing" ? "Creating…" : listingDraft.publishNow ? "Create & Publish" : "Save Draft"}
                </button>
              </div>
            </form>

            {!listableInventory.length && (
              <div className="seller-info seller-info-spaced">
                Every active inventory item already has a current listing, or no inventory is currently available.
              </div>
            )}
          </div>

          <div className="seller-card seller-bulk-listing">
            <div className="seller-card-heading">
              <div>
                <h2>List inventory in bulk</h2>
                <p>Select eligible inventory without a Hobbli listing. Source and external-channel filters apply here; the listing-status filter applies to the existing listings table below. Imported eBay asking prices are suggested automatically.</p>
              </div>
              <strong>{filteredListableInventory.length} matching eligible</strong>
            </div>
            {!channelLinksLoaded && (
              <div className="seller-bulk-loading" role="status" aria-live="polite">
                {channelLinksLoading ? "Loading eBay listing prices…" : (channelLinksError || "Waiting for channel listing data…")}
                {channelLinksError && <button type="button" className="seller-btn" onClick={() => loadChannelLinks().catch(() => {})}>Retry</button>}
              </div>
            )}
            {channelLinksLoaded && <>
            <div className="seller-bulk-toolbar">
              <label><input type="checkbox" disabled={bulkListingBusy || !filteredListableInventory.length} checked={Boolean(filteredListableInventory.length) && filteredListableInventory.every((item) => bulkListingSelection.has(String(item.id)))} onChange={(e) => {
                setBulkListingSelection(e.target.checked ? new Set(filteredListableInventory.map((item) => String(item.id))) : new Set());
                setBulkListingReview(false);
              }} /> Select all matching eligible</label>
              <button type="button" className="seller-btn" disabled={bulkListingBusy || !bulkListingSelection.size} onClick={() => { setBulkListingSelection(new Set()); setBulkListingReview(false); }}>Clear selection</button>
              <span>{bulkSelectedRows.length} selected</span>
            </div>
            <div className="seller-table-wrap"><table className="seller-table seller-bulk-table"><thead><tr><th>Select</th><th>Inventory item</th><th>Source</th><th>Hobbli price</th><th>Quantity</th></tr></thead><tbody>
              {filteredListableInventory.map((item) => {
                const row = bulkListingRow(item);
                const selected = bulkListingSelection.has(String(item.id));
                return <tr key={item.id}>
                  <td><input type="checkbox" checked={selected} disabled={bulkListingBusy} onChange={(e) => { setBulkListingSelection((old) => { const next = new Set(old); e.target.checked ? next.add(String(item.id)) : next.delete(String(item.id)); return next; }); setBulkListingReview(false); }} aria-label={`Select ${item.title || item.id}`} /></td>
                  <td><strong>{item.title || `Canonical item #${item.item_id}`}</strong><small>{row.available} available · {item.condition || "Condition not set"}</small></td>
                  <td>{row.ebay ? <><span className="seller-status active">eBay</span><small>Price snapshot; sync off</small></> : <span className="seller-status">Hobbli inventory</span>}</td>
                  <td><input aria-label={`Hobbli price for ${item.title || item.id}`} type="number" min="0" step="0.01" placeholder="Price required" disabled={bulkListingBusy} value={row.price} onChange={(e) => { setBulkListingOverrides((old) => ({ ...old, [item.id]: { ...old[item.id], price: e.target.value } })); setBulkListingReview(false); }} /></td>
                  <td><input aria-label={`Quantity for ${item.title || item.id}`} type="number" min="1" max={row.available} step="1" disabled={bulkListingBusy} value={row.quantity} onChange={(e) => { setBulkListingOverrides((old) => ({ ...old, [item.id]: { ...old[item.id], quantity: e.target.value } })); setBulkListingReview(false); }} /></td>
                </tr>;
              })}
              {!filteredListableInventory.length && <tr><td colSpan="5" className="seller-empty">No eligible unlisted inventory matches the source and channel filters. Items with existing Hobbli drafts or published listings cannot be created again.</td></tr>}
            </tbody></table></div>
            <div className="seller-bulk-footer">
              <label>Action <select value={bulkListingMode} disabled={bulkListingBusy} onChange={(e) => { setBulkListingMode(e.target.value); setBulkListingReview(false); }}><option value="draft">Save as drafts</option><option value="publish">Publish on Hobbli</option></select></label>
              <button type="button" className="seller-btn seller-btn-primary" disabled={bulkListingBusy || !bulkSelectedRows.length} onClick={() => setBulkListingReview(true)}>Review {bulkSelectedRows.length} selected</button>
            </div>
            {bulkListingReview && <div className="seller-bulk-review">
              <strong>{bulkListingMode === "publish" ? "Publish" : "Save drafts for"} {bulkSelectedRows.length} inventory items</strong>
              <p>{bulkSelectedRows.filter((row) => row.ebay).length} use eBay price defaults unless edited. {bulkInvalidRows.length} items need valid prices or quantities. Shipping: {activeProfiles.some((p) => p.is_default) ? "default shipping profile" : "free shipping"}. No eBay updates or automatic sync.</p>
              {bulkInvalidRows.length > 0 && <p className="seller-bulk-error">Fix the {bulkInvalidRows.length} invalid rows before continuing.</p>}
              <button type="button" className="seller-btn seller-btn-primary" disabled={bulkListingBusy || bulkInvalidRows.length > 0} onClick={submitBulkListings}>{bulkListingBusy ? "Processing…" : `Confirm ${bulkListingMode === "publish" ? "publish" : "drafts"}`}</button>
            </div>}
            {bulkListingProgress && <div className="seller-bulk-results" role="status"><strong>{bulkListingProgress.completed}/{bulkListingProgress.total} processed · {bulkListingProgress.created} created · {bulkListingProgress.failed} failed</strong>{bulkListingProgress.failures?.map((f) => <p key={f.id}>{f.title || f.id}: {f.message}</p>)}</div>}
            </>}
          </div>

            </div>
          </details>
        </section>
      )}

      {tab === "offers" && (
        <section className="seller-section">
          <div className="seller-card seller-table-card seller-negotiations">
            <div className="seller-card-heading seller-negotiation-heading">
              <div><span className="seller-offer-eyebrow">SELLER INBOX</span><h2>Offers & negotiations</h2><p>Each eBay offer appears separately. Expand to review verified negotiation history and respond to eligible offers.</p></div>
              <div className="seller-row-actions">
                <span className="seller-pill">{offerThreads.length + ebayOffersGrouped.length} conversations & offers</span>
                {ebayChannel?.connected && <button type="button" className="seller-btn seller-btn-primary" disabled={ebayOfferBusy} onClick={() => runEbayOfferDiscovery(true)}>{ebayOfferBusy ? "Refreshing…" : "Refresh eBay offers"}</button>}
              </div>
            </div>
            <div className="seller-negotiation-toolbar" role="group" aria-label="Filter offers">
              {[['all','All offers'],['negotiating','In negotiation'],['open','Open offers'],['closed','History']].map(([value,label]) => <button key={value} type="button" className={offerViewFilter === value ? 'active' : ''} aria-pressed={offerViewFilter === value} onClick={() => setOfferViewFilter(value)}>{label}</button>)}
            </div>
            <div className="seller-negotiation-list">
              {ebayOffersGrouped.filter((entry) => {
                const status = String(entry.offer?.status || '').toLowerCase();
                const closed = entry.offer ? !ebayOfferCanCounter(entry.offer, offerClockNow) : entry.group ? entry.group.isClosed : ['expired','declined','accepted','withdrawn','cancelled','rejected'].includes(status);
                if (offerViewFilter === 'negotiating') return Boolean(entry.group?.hasCounter) && !closed;
                if (offerViewFilter === 'open') return !closed;
                if (offerViewFilter === 'closed') return closed;
                return true;
              }).map((entry) => {
                const { offer, group } = entry;
                const open = Boolean(expandedEbayThreads[entry.key]);
                const status = offer ? String(offer.status || 'Unknown') : (group?.status || 'History');
                const title = inventory.find((i) => String(i.id) === String(offer?.seller_inventory_id))?.title || `eBay listing ${group?.listingId || offer?.listing_id || '—'}`;
                const lastBuyer = [...(group?.observed || [])].reverse().find((e) => e.event_type === 'buyer_offer');
                const amount = offer?.amount ?? lastBuyer?.amount;
                return <div className={`seller-negotiation-entry ${open ? 'expanded' : ''}`} key={entry.key}>
                  <button type="button" className="seller-negotiation-row" aria-expanded={open} onClick={() => setExpandedEbayThreads((prev) => ({ ...prev, [entry.key]: !prev[entry.key] }))}>
                    <span className="seller-negotiation-chevron" aria-hidden="true">{open ? '⌄' : '›'}</span>
                    <span className="seller-negotiation-item"><strong>{title}</strong><small>eBay · Listing {group?.listingId || offer?.listing_id || '—'}{offer ? ` · Offer ${offer.offer_id}` : ' · History only'}{group ? ` · ${group.observed.length} eBay steps` : ''}</small></span>
                    <span className="seller-negotiation-money"><small>{offer ? "This offer" : "Last recorded offer"}</small><strong>{money(amount, offer?.currency || lastBuyer?.currency || 'USD')}</strong></span>
                    <span className="seller-negotiation-stage"><span className={`seller-negotiation-chip ${group?.hasBuyerCounter ? 'buyer-turn' : group?.hasCounter ? 'counter' : 'neutral'}`}>{status}</span><small>{entry.buyerRelationship}{offer && ebayOfferCanCounter(offer, offerClockNow) ? ' · Action required' : ''}</small></span>
                    <span className="seller-negotiation-countdown">{offerCountdown(offer?.expires_at, offer?.status, offerClockNow).label}</span>
                  </button>
                  {open && <div className="seller-negotiation-expanded">
                    {group ? <>
                      <div className="seller-negotiation-subheading"><strong>Negotiation timeline</strong><span>{group.timeline.length} visible steps · Buyer identity protected · Untimed eBay steps use offer sequence</span></div>
                      <div className="seller-negotiation-timeline">
                        {group.timeline.map((e) => <div className={`seller-negotiation-step ${e.event_type === 'buyer_offer' ? 'buyer' : 'seller'}`} key={e.displayKey}>
                          <span className="seller-negotiation-dot" />
                          <div className="seller-negotiation-step-card"><div><strong>{e.event_type === 'buyer_offer' ? (e.offer_type === 'BuyerCounterOffer' ? 'Buyer counteroffer' : 'Buyer offer') : 'Seller counteroffer'}</strong><small>{e.occurred_at || e.offered_at ? new Date(e.occurred_at || e.offered_at).toLocaleString() : 'Offer time unavailable'}</small></div><div className="seller-negotiation-step-value"><strong>{money(e.amount, e.currency || 'USD')}</strong><span className="seller-status">{e.event_status || 'Observed'}</span></div></div>
                        </div>)}
                      </div>
                      {group.failed.length > 0 && <details className="seller-negotiation-audit"><summary>Action audit · {group.failed.length} unsuccessful or unconfirmed attempt(s)</summary>{group.failed.map((e) => <div key={e.event_id}>{e.event_type.replaceAll('_',' ')} · {money(e.amount,e.currency || 'USD')} · {e.event_status || 'Unknown'} · {e.occurred_at ? new Date(e.occurred_at).toLocaleString() : 'Time unavailable'}</div>)}</details>}
                    </> : <p className="seller-negotiation-nohistory">No verified buyer-linked negotiation history is available for this offer yet.</p>}
                    <div className="seller-negotiation-footer"><span>{offer ? `Offer ${offer.offer_id} · ` : ''}eBay is authoritative for status. Buyer identity is never inferred from listing ID.</span>{offer && ebayOfferCanCounter(offer, offerClockNow) && <span className="seller-row-actions"><button type="button" className="seller-btn seller-btn-primary" disabled={Boolean(ebayDeclineBusy) || Boolean(ebayAcceptBusy)} onClick={() => { setEbayCounterOffer(offer); setEbayCounterAmount(''); }}>Counter offer</button><button type="button" className="seller-btn seller-btn-primary" disabled={Boolean(ebayAcceptBusy) || Boolean(ebayDeclineBusy) || ebayCounterBusy} onClick={() => acceptEbayOffer(offer)}>{ebayAcceptBusy === offer.saved_offer_id ? "Accepting…" : "Accept offer"}</button><button type="button" className="seller-btn seller-btn-danger" disabled={Boolean(ebayDeclineBusy) || Boolean(ebayAcceptBusy) || ebayCounterBusy} onClick={() => declineEbayOffer(offer)}>{ebayDeclineBusy === offer.saved_offer_id ? 'Declining…' : 'Decline offer'}</button></span>}</div>
                    {ebayCounterOffer && offer && String(ebayCounterOffer.saved_offer_id) === String(offer.saved_offer_id) && (
                      <div className="seller-negotiation-inline-counter">
                        <div className="seller-card seller-offer-detail"><div className="seller-card-heading"><div><h2>Counter eBay offer</h2><p>Real production action — the buyer will receive your counteroffer.</p></div><button className="seller-link-btn" type="button" disabled={ebayCounterBusy} onClick={() => setEbayCounterOffer(null)}>Close</button></div><p>Listing {ebayCounterOffer.listing_id} · Buyer offered {money(ebayCounterOffer.amount, ebayCounterOffer.currency || "USD")} · Asking {money(ebayCounterOffer.asking_price, ebayCounterOffer.currency || "USD")}</p><label className="seller-field"><span>Your counteroffer (USD)</span><input type="number" min="0.01" step="0.01" value={ebayCounterAmount} onChange={(e) => setEbayCounterAmount(e.target.value)} disabled={ebayCounterBusy} placeholder="Enter amount" /></label><div className="seller-row-actions"><button type="button" className="seller-btn seller-btn-primary" disabled={ebayCounterBusy || !ebayCounterAmount} onClick={sendEbayCounter}>{ebayCounterBusy ? "Sending…" : "Review and send counter"}</button></div><p>You'll be asked to confirm before Hobbli contacts eBay. If a network error occurs, check eBay before trying again.</p></div>
                      </div>
                    )}
                  </div>}
                </div>;
              })}
              {offerThreads.filter((offer) => { const closed = ['expired','declined','accepted','withdrawn','cancelled','rejected'].includes(String(offer.status).toLowerCase()); return offerViewFilter === 'all' || (offerViewFilter === 'closed' ? closed : offerViewFilter === 'open' ? !closed : false); }).map((offer) => <div className="seller-negotiation-entry" key={`hobbli-${offer.id}`}><div className="seller-negotiation-row seller-negotiation-hobbli"><span className="seller-negotiation-chevron">•</span><span className="seller-negotiation-item"><strong>{offer.listing_title || offer.title || 'Marketplace item'}</strong><small>Hobbli marketplace</small></span><span className="seller-negotiation-money"><small>Current offer</small><strong>{money(offer.current_amount, offer.currency || 'USD')}</strong></span><span className="seller-negotiation-stage"><span className="seller-negotiation-chip neutral">{offer.status || 'Open'}</span></span><button type="button" className="seller-link-btn" onClick={() => openSellerOffer(offer.id)}>View details →</button></div></div>)}
              {!offerThreads.length && !ebayOffersGrouped.length && <div className="seller-negotiation-empty">{commerceLoading || ebayOfferBusy ? 'Loading offers…' : 'No saved offers yet.'}</div>}
            </div>
            {ebayNegotiationError && <div className="seller-negotiation-warning" role="status">Negotiation history unavailable: {ebayNegotiationError}. Saved offers are still shown individually.</div>}
            {ebayOfferResult?.error && <p className="seller-offer-fetch-error" role="alert">Unable to load eBay offers: {ebayOfferResult.error}</p>}
            {ebayChannel?.connected && <details className="seller-offer-advanced"><summary>Advanced · Listing lookup & diagnostics</summary><p>Fetch historical eBay offers for a specific listing.</p><div className="seller-row-actions"><input className="seller-ebay-offer-input" aria-label="eBay listing ID" placeholder="eBay listing ID" value={ebayOfferItemId} onChange={(e) => setEbayOfferItemId(e.target.value)} disabled={ebayOfferBusy} /><button className="seller-btn" type="button" disabled={ebayOfferBusy || !ebayOfferItemId.trim()} onClick={() => runEbayOfferDiscovery(false)}>Look up listing</button><button type="button" className="seller-btn" disabled={ebayBuyerDiagnosticBusy || ebayOfferBusy} onClick={diagnoseEbayOfferBuyers}>Buyer diagnostic</button></div>{ebayBuyerDiagnosticResult && <pre className="seller-ebay-order-json">{JSON.stringify(ebayBuyerDiagnosticResult,null,2)}</pre>}</details>}
          </div>


          {selectedOfferId && (
            <div className="seller-card seller-offer-detail">
              <div className="seller-card-heading">
                <div><h2>Offer details</h2><p>Negotiation history is recorded from the authoritative marketplace offer events.</p></div>
                <button className="seller-link-btn" type="button" onClick={closeSellerOffer}>Close</button>
              </div>
              {!selectedOffer ? <div className="seller-empty">This offer is no longer available for this shop.</div> : (
                <>
                  <div className="seller-offer-summary-grid">
                    <div><span>Status</span><strong className={`seller-status ${selectedOffer.status || ""}`}>{selectedOffer.status || "open"}</strong></div>
                    <div><span>Current offer</span><strong>{money(selectedOffer.current_amount, selectedOffer.currency || "USD")}</strong></div>
                    <div><span>Accepted amount</span><strong>{selectedOffer.accepted_amount == null ? "—" : money(selectedOffer.accepted_amount, selectedOffer.currency || "USD")}</strong></div>
                    <div><span>Updated</span><strong>{selectedOffer.updated_at ? new Date(selectedOffer.updated_at).toLocaleString() : "—"}</strong></div>
                    <div><span>Time remaining</span><strong className={`seller-offer-countdown ${offerCountdown(offerExpiry(selectedOffer), selectedOffer.status, offerClockNow).urgency}`}>{offerCountdown(offerExpiry(selectedOffer), selectedOffer.status, offerClockNow).label}</strong></div>
                  </div>
                  <div className="seller-offer-item-card">
                    <div className="seller-offer-item-media">
                      {selectedOfferImageUrl ? <img src={selectedOfferImageUrl} alt={selectedOffer.listing_title || "Marketplace item"} /> : <div className="seller-offer-image-placeholder">No image</div>}
                    </div>
                    <div className="seller-offer-item-copy">
                      <span className="seller-offer-eyebrow">Marketplace item</span>
                      <h3>{selectedOffer.listing_title || selectedOffer.canonical_item?.name || selectedOffer.title || "Marketplace item"}</h3>
                      {selectedOfferIdentity && <p>{selectedOfferIdentity}</p>}
                      {selectedOffer.offer_inventory?.condition && <p>Condition: {selectedOffer.offer_inventory.condition}</p>}
                      <div className="seller-offer-price-row">
                        <div><span>Asking price</span><strong>{money(selectedOffer.listing_price, selectedOffer.listing_currency || selectedOffer.currency || "USD")}</strong></div>
                        <div><span>Current offer</span><strong>{money(selectedOffer.current_amount, selectedOffer.currency || "USD")}</strong></div>
                        <div><span>Offer vs. ask</span><strong>{selectedOfferAskPercent == null ? "—" : `${selectedOfferAskPercent}%`}</strong></div>
                      </div>
                    </div>
                  </div>

                  <div className="seller-order-block seller-market-context">
                    <div className="seller-order-block-heading"><div><h3>Market context</h3><p>Decision support from Hobbli's matched market evidence. Active asking prices are not completed sales.</p></div></div>
                    {selectedOffer.market_snapshot ? (
                      <>
                        <div className="seller-market-grid">
                          <div><span>{selectedOffer.market_snapshot.evidence_metadata?.source === "ebay" ? "eBay active asking" : "Active asking context"}</span><strong>{money(selectedOffer.market_snapshot.estimated_value ?? selectedOffer.market_snapshot.median_price, selectedOffer.currency || "USD")}</strong><small>{selectedOffer.market_snapshot.listing_count || 0} close comparable{Number(selectedOffer.market_snapshot.listing_count) === 1 ? "" : "s"}</small></div>
                          <div><span>Asking range</span><strong>{selectedOffer.market_snapshot.min_price == null ? "—" : `${money(selectedOffer.market_snapshot.min_price, selectedOffer.currency || "USD")} – ${money(selectedOffer.market_snapshot.max_price, selectedOffer.currency || "USD")}`}</strong><small>{selectedOffer.market_snapshot.calculated_at ? `Updated ${new Date(selectedOffer.market_snapshot.calculated_at).toLocaleDateString()}` : ""}</small></div>
                          <div><span>Recent matched sales</span><strong>{selectedOfferComparableSales.length ? `${selectedOfferComparableSales.length} found` : "None yet"}</strong><small>{selectedOfferComparableSales.length ? "Closest matching completed-sale evidence" : "No matching completed sales are available"}</small></div>
                        </div>
                        {selectedOfferComparableSales.length > 0 && (
                          <div className="seller-market-sales">
                            {selectedOfferComparableSales.map((sale) => <div key={sale.id}><span>{sale.source || "Market sale"}{sale.sale_date ? ` · ${new Date(sale.sale_date).toLocaleDateString()}` : ""}</span><strong>{money(Number(sale.sale_price || 0) + Number(sale.shipping_price || 0), sale.currency || selectedOffer.currency || "USD")}</strong></div>)}
                          </div>
                        )}
                        <div className="seller-market-caution"><strong>Limited market evidence.</strong> Asking prices show what sellers are requesting, not what buyers have paid. Hobbli will strengthen this context as matched sales accumulate.</div>
                      </>
                    ) : (
                      <div className="seller-empty seller-empty-inline">No comparable market data is available for this item yet.</div>
                    )}
                  </div>

                  <div className="seller-order-block">
                    <div className="seller-order-block-heading"><div><h3>Offer history</h3><p>Buyer and seller actions on this negotiation.</p></div></div>
                    {offerDetailLoading ? <div className="seller-empty seller-empty-inline">Loading offer history…</div> : offerEvents.length ? (
                      <div className="seller-offer-history">{offerEvents.map((event) => <div className="seller-offer-event" key={event.id}><div><strong>{event.actor_side === "seller" ? "You" : "Buyer"} · {String(event.event_type || "update").replaceAll("_", " ")}</strong><small>{event.created_at ? new Date(event.created_at).toLocaleString() : ""}</small>{event.message && <p>{event.message}</p>}</div><strong>{event.amount == null ? "" : money(event.amount, selectedOffer.currency || "USD")}</strong></div>)}</div>
                    ) : <div className="seller-empty seller-empty-inline">No offer events are visible yet.</div>}
                  </div>

                  {selectedOffer.status === "open" ? (
                    sellerCanRespondToOffer ? (
                      <div className="seller-order-block seller-offer-response">
                        <div className="seller-order-block-heading"><div><h3>Respond</h3><p>Accept the buyer's current amount, send a counter offer, or decline and close the negotiation.</p></div></div>
                        {(offerStockLoading || !offerStock?.available) && (
                          <div className="seller-info" role="status">
                            {offerStockLoading ? "Checking inventory availability…" : offerStock?.reason || "Inventory availability has not been verified."}
                            {" You can still decline this offer."}
                          </div>
                        )}
                        <div className="seller-offer-response-grid">
                          <label className="seller-field"><span>Counter amount</span><input type="number" min="0.01" step="0.01" value={offerCounterAmount} onChange={(event) => setOfferCounterAmount(event.target.value)} placeholder={Number(selectedOffer.current_amount || 0).toFixed(2)} /></label>
                          <label className="seller-field seller-offer-message"><span>Message (optional)</span><textarea rows="3" maxLength="500" value={offerMessage} onChange={(event) => setOfferMessage(event.target.value)} placeholder="Add a note to the buyer…" /></label>
                        </div>
                        <div className="seller-row-actions seller-offer-actions">
                          <button className="seller-btn seller-btn-primary" type="button" disabled={offerActionBusy || !offerCanAcceptOrCounter} onClick={() => setOfferConfirmAction("accept")}>{`Accept ${money(selectedOffer.current_amount, selectedOffer.currency || "USD")}`}</button>
                          <button className="seller-btn" type="button" disabled={offerActionBusy || !offerCanAcceptOrCounter || !offerCounterAmount} onClick={() => respondToSellerOffer("counter")}>Counter</button>
                          <button className="seller-btn seller-btn-danger" type="button" disabled={offerActionBusy} onClick={() => setOfferConfirmAction("decline")}>Decline</button>
                        </div>
                      </div>
                    ) : offerDetailLoading ? (
                      <div className="seller-info">Checking whose turn it is…</div>
                    ) : (
                      <div className="seller-info"><strong>Waiting for buyer response.</strong>{latestActionableOfferEvent?.actor_side === "seller" && latestActionableOfferEvent?.event_type === "counter" ? ` You countered ${money(latestActionableOfferEvent.amount, selectedOffer.currency || "USD")}.` : " The buyer must respond before you can take another action."}</div>
                    )
                  ) : <div className="seller-info">This negotiation is {String(selectedOffer.status || "closed").replaceAll("_", " ")}. No further seller response is available.</div>}

                  {offerConfirmAction && (
                    <div className="seller-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !offerActionBusy) setOfferConfirmAction(null); }}>
                      <div className="seller-modal" role="dialog" aria-modal="true" aria-labelledby="seller-offer-confirm-title">
                        <div className="seller-card-heading">
                          <div>
                            <h2 id="seller-offer-confirm-title">{offerConfirmAction === "accept" ? "Accept this offer?" : "Decline this offer?"}</h2>
                            <p>{selectedOffer.listing_title || selectedOffer.title || "Marketplace item"}</p>
                          </div>
                          <button className="seller-link-btn" type="button" disabled={offerActionBusy} onClick={() => setOfferConfirmAction(null)}>Close</button>
                        </div>
                        {offerConfirmAction === "accept" ? (
                          <div className="seller-refund-confirm">You are about to accept the buyer's offer of <strong>{money(selectedOffer.current_amount, selectedOffer.currency || "USD")}</strong>. Accepting closes this negotiation and moves the accepted offer into its reservation/checkout flow.</div>
                        ) : (
                          <div className="seller-refund-confirm">Declining will close this negotiation. The buyer will be notified that the offer was declined.</div>
                        )}
                        <div className="seller-modal-actions">
                          <button className="seller-btn" type="button" disabled={offerActionBusy} onClick={() => setOfferConfirmAction(null)}>Cancel</button>
                          <button className={`seller-btn ${offerConfirmAction === "decline" ? "seller-btn-danger" : "seller-btn-primary"}`} type="button" disabled={offerActionBusy || (offerConfirmAction === "accept" && !offerCanAcceptOrCounter)} onClick={async () => { const action = offerConfirmAction; await respondToSellerOffer(action); setOfferConfirmAction(null); }}>{offerActionBusy ? "Working…" : offerConfirmAction === "accept" ? `Accept ${money(selectedOffer.current_amount, selectedOffer.currency || "USD")}` : "Decline offer"}</button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      )}

      {tab === "orders" && (
        <section className="seller-section">
          <div className="seller-card seller-table-card">
            <div className="seller-card-heading"><div><h2>Orders</h2><p>Open an order to review its items, payment state, and refund history.</p></div><span className="seller-pill">{sellerOrders.length + ebayOrders.length} orders</span></div>
            <div className="seller-table-wrap"><table className="seller-table"><thead><tr><th>Order</th><th>Status</th><th>Total</th><th>Payment</th><th>Created</th><th></th></tr></thead><tbody>
              {[...sellerOrders.map((order) => ({ channel: "hobbli", order, date: order.created_at })),
                ...ebayOrders.map((order) => ({ channel: "ebay", order, date: order.order_date }))]
                .sort((a, b) => {
                  const aTime = a.date ? Date.parse(a.date) : NaN;
                  const bTime = b.date ? Date.parse(b.date) : NaN;
                  if (!Number.isFinite(aTime)) return Number.isFinite(bTime) ? 1 : 0;
                  if (!Number.isFinite(bTime)) return -1;
                  return bTime - aTime;
                })
                .map((entry) => <React.Fragment key={`${entry.channel}-${entry.order.id || `${entry.order.channel_account_id}:${entry.order.external_order_id}`}`}>
              {entry.channel === "hobbli" && [entry.order].map((order) => <React.Fragment key={order.id}><tr className={String(selectedSellerOrderId) === String(order.id) ? "seller-order-row selected" : "seller-order-row"}><td>
                <strong>{(hobbliOrderTitles[String(order.id)] || []).slice(0, 2).join(" · ") || order.order_number || order.id}</strong>
                <small>Hobbli · {order.order_number || order.id}{(hobbliOrderTitles[String(order.id)] || []).length > 2 ? ` · +${hobbliOrderTitles[String(order.id)].length - 2} more` : ""}</small></td><td><span className={`seller-status ${order.status || ""}`}>{order.status || "—"}</span></td><td>{money(order.total ?? order.total_amount ?? order.order_total ?? order.subtotal_amount, order.currency || "USD")}</td><td>{order.payment_status || order.payment_provider || "Awaiting configuration"}</td><td>{order.created_at ? new Date(order.created_at).toLocaleDateString() : "—"}</td><td><button className="seller-link-btn" type="button" onClick={() => String(selectedSellerOrderId) === String(order.id) ? closeSellerOrderDetail() : (setExpandedEbayOrder(null), loadSellerOrderDetail(order.id))}>{String(selectedSellerOrderId) === String(order.id) ? "Hide" : "View"}</button></td></tr>
              {String(selectedSellerOrderId) === String(order.id) && (
            <tr className="seller-order-expanded-row"><td colSpan="6"><div className="seller-order-detail">
              <div className="seller-card-heading">
                <div><h2>{orderDetail?.order?.order_number ? `Order ${orderDetail.order.order_number}` : "Order details"}</h2><p>Fulfillment and payment are shown separately so a refunded order can still retain its fulfillment state.</p></div>
                <button className="seller-btn" type="button" onClick={closeSellerOrderDetail}>Close</button>
              </div>
              {orderDetailLoading && <div className="seller-empty">Loading order details…</div>}
              {orderDetailError && <div className="seller-paypal-result error">{orderDetailError}</div>}
              {orderDetail && !orderDetailLoading && (() => {
                const payment = orderDetail.payment;
                const refunded = Number(payment?.refunded_amount || 0);
                const captured = Number(payment?.amount || 0);
                const remaining = Number(payment?.remaining_refundable || 0);
                const netAfterRefunds = Math.max(captured - refunded, 0);
                const shippingAddress = orderDetail.order?.shipping_address || null;
                const pickupOnly = (orderDetail.items || []).length > 0 && (orderDetail.items || []).every(
                  (item) => item?.shipping_snapshot?.shipping_mode === "pickup"
                );
                const paymentLabel = payment?.status === "refunded"
                  ? `Refunded · ${money(netAfterRefunds, payment.currency)} net`
                  : payment?.status === "partially_refunded"
                    ? `Partially refunded · ${money(netAfterRefunds, payment.currency)} net`
                    : payment?.status === "succeeded"
                      ? `Paid · ${money(netAfterRefunds, payment.currency)} net`
                      : payment?.status || "No payment";
                return <>
                  <div className="seller-order-summary-grid">
                    <div><span>Fulfillment</span><strong className={`seller-status ${orderDetail.seller_order?.status || ""}`}>{orderDetail.seller_order?.status || "—"}</strong></div>
                    <div><span>Payment</span><strong>{paymentLabel}</strong></div>
                    <div><span>Order total</span><strong>{money(orderDetail.seller_order?.total, orderDetail.order?.currency || "USD")}</strong></div>
                    <div><span>Placed</span><strong>{orderDetail.order?.placed_at ? new Date(orderDetail.order.placed_at).toLocaleString() : "—"}</strong></div>
                  </div>

                  <div className="seller-order-block">
                    <h3>Items</h3>
                    <div className="seller-order-items">
                      {(orderDetail.items || []).map((item) => <div className="seller-order-item" key={item.id}><div><strong>{item.title || "Marketplace item"}</strong><small>{[item.condition, item.quantity ? `Qty ${item.quantity}` : null].filter(Boolean).join(" · ")}</small></div><strong>{money(Number(item.unit_price || 0) * Number(item.quantity || 1) + Number(item.shipping_amount || 0), item.currency || orderDetail.order?.currency || "USD")}</strong></div>)}
                    </div>
                  </div>

                  <div className="seller-order-block">
                    <div className="seller-order-block-heading">
                      <div>
                        <h3>{pickupOnly ? "Pickup" : "Ship to"}</h3>
                        {pickupOnly ? (
                          <p>Local pickup — no shipping address required.</p>
                        ) : shippingAddress ? (
                          <div className="seller-order-address">
                            {orderDetail.order?.shipping_recipient_name && <div><strong>{orderDetail.order.shipping_recipient_name}</strong></div>}
                            {shippingAddress.address1 && <div>{shippingAddress.address1}</div>}
                            {shippingAddress.address2 && <div>{shippingAddress.address2}</div>}
                            <div>{[shippingAddress.city, shippingAddress.region].filter(Boolean).join(", ")}{shippingAddress.postal_code ? ` ${shippingAddress.postal_code}` : ""}</div>
                            {shippingAddress.country && <div>{shippingAddress.country}</div>}
                          </div>
                        ) : (
                          <p>No shipping address is available for this order.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="seller-order-block">
                    <div className="seller-order-block-heading">
                      <div><h3>Fulfillment</h3><p>Create and advance physical shipments separately from payment and refunds.</p></div>
                      <div className="seller-row-actions">
                        {!pickupOnly && ["new", "processing"].includes(orderDetail.seller_order?.status) && shipments.length === 0 && <button className="seller-btn seller-btn-primary" type="button" onClick={openFulfillmentDialog}>Start fulfillment</button>}
                        {payment?.status === "refunded" && ["new", "processing"].includes(orderDetail.seller_order?.status) && !shipments.some((shipment) => ["shipped", "delivered"].includes(shipment.status)) && <button className="seller-btn seller-btn-danger" type="button" disabled={orderCancelBusy || fulfillmentBusy} onClick={cancelFullyRefundedOrder}>{orderCancelBusy ? "Cancelling…" : "Cancel order"}</button>}
                      </div>
                    </div>
                    {shipments.length ? <div className="seller-shipment-list">{shipments.map((shipment) => <div className="seller-shipment" key={shipment.id}>
                      <div className="seller-shipment-main"><div><strong>{shipment.carrier || "Shipment"}{shipment.service ? ` · ${shipment.service}` : ""}</strong><small>{shipment.tracking_number ? `Tracking ${shipment.tracking_number}` : "No tracking number"}</small></div><span className={`seller-status ${shipment.status || ""}`}>{shipment.status}</span></div>
                      <div className="seller-shipment-meta"><span>{(shipment.marketplace_shipment_items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0)} item{(shipment.marketplace_shipment_items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0) === 1 ? "" : "s"}</span>{shipment.shipped_at && <span>Shipped {new Date(shipment.shipped_at).toLocaleString()}</span>}{shipment.delivered_at && <span>Delivered {new Date(shipment.delivered_at).toLocaleString()}</span>}</div>
                      <div className="seller-row-actions">{shipment.status === "preparing" && <button className="seller-btn seller-btn-primary" type="button" disabled={fulfillmentBusy} onClick={() => markShipmentShipped(shipment)}>{fulfillmentBusy ? "Working…" : "Mark shipped"}</button>}{shipment.status === "shipped" && <button className="seller-btn seller-btn-primary" type="button" disabled={fulfillmentBusy} onClick={() => markShipmentDelivered(shipment.id)}>{fulfillmentBusy ? "Working…" : "Mark delivered"}</button>}</div>
                    </div>)}</div> : <div className="seller-empty seller-empty-inline">No shipment created yet.</div>}
                  </div>

                  <div className="seller-order-block">
                    <div className="seller-order-block-heading">
                      <div>
                        <h3>Returns</h3>
                        <p>Review merchandise returns separately from refunds and inventory restocking.</p>
                      </div>
                    </div>
                    {sellerReturns.length ? (
                      <div className="seller-shipment-list">
                        {sellerReturns.map((returnRow) => (
                          <div className="seller-shipment" key={returnRow.id}>
                            <div className="seller-shipment-main">
                              <div>
                                <strong>{returnRow.reason_label || returnRow.reason_code || "Marketplace return"}</strong>
                                <small>
                                  Requested {returnRow.requested_at ? new Date(returnRow.requested_at).toLocaleString() : "—"}
                                  {returnRow.buyer_reason ? ` · ${returnRow.buyer_reason}` : ""}
                                </small>
                              </div>
                              <span className={`seller-status ${returnRow.status || ""}`}>{String(returnRow.status || "requested").replaceAll("_", " ")}</span>
                            </div>

                            <div className="seller-order-items">
                              {(returnRow.items || []).map((item) => {
                                const remainingRestock = Math.max(
                                  0,
                                  Number(item.quantity_received || 0) - Number(item.quantity_restocked || 0)
                                );
                                return (
                                  <div className="seller-order-item" key={item.id}>
                                    <div>
                                      <strong>{item.title || "Returned item"}</strong>
                                      <small>
                                        Requested {item.quantity_requested || 0}
                                        {Number(item.quantity_approved || 0) > 0 ? ` · Approved ${item.quantity_approved}` : ""}
                                        {Number(item.quantity_received || 0) > 0 ? ` · Received ${item.quantity_received}` : ""}
                                        {Number(item.quantity_restocked || 0) > 0 ? ` · Restocked ${item.quantity_restocked}` : ""}
                                      </small>
                                    </div>
                                    {remainingRestock > 0 && (
                                      <button
                                        className="seller-btn seller-btn-small"
                                        type="button"
                                        disabled={Boolean(returnBusyId)}
                                        onClick={() => restockSellerReturnItem(item)}
                                      >
                                        {returnBusyId === item.id ? "Restocking…" : `Restock ${remainingRestock}`}
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            <div className="seller-row-actions">
                              {returnRow.status === "requested" && (
                                <>
                                  <button
                                    className="seller-btn seller-btn-primary"
                                    type="button"
                                    disabled={Boolean(returnBusyId)}
                                    onClick={() => approveSellerReturn(returnRow)}
                                  >
                                    {returnBusyId === returnRow.id ? "Working…" : "Approve return"}
                                  </button>
                                  <button
                                    className="seller-btn seller-btn-danger"
                                    type="button"
                                    disabled={Boolean(returnBusyId)}
                                    onClick={() => rejectSellerReturn(returnRow)}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                              {["approved", "in_transit"].includes(returnRow.status) && (
                                <button
                                  className="seller-btn seller-btn-primary"
                                  type="button"
                                  disabled={Boolean(returnBusyId)}
                                  onClick={() => receiveSellerReturn(returnRow)}
                                >
                                  {returnBusyId === returnRow.id ? "Working…" : "Mark return received"}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="seller-empty seller-empty-inline">No returns for this order.</div>
                    )}
                  </div>

                  {payment && <div className="seller-order-block">
                    <div className="seller-order-block-heading"><div><h3>Payment & refunds</h3><p>Original payment {money(captured, payment.currency)} · Refunded {money(refunded, payment.currency)} · Net after refunds {money(netAfterRefunds, payment.currency)}{payment.can_refund ? ` · Refundable ${money(remaining, payment.currency)}` : ""}</p></div>{payment.can_refund && <button className="seller-btn seller-btn-primary" type="button" onClick={openRefundDialog}>Refund</button>}</div>
                    {(orderDetail.refunds || []).length ? <div className="seller-refund-history">{orderDetail.refunds.map((refund) => <div key={refund.id}><div><strong>{money(refund.amount, refund.currency)}</strong><small>{refund.reason || "Refund"}</small></div><div><span className={`seller-status ${refund.status || ""}`}>{refund.status}</span><small>{refund.completed_at || refund.requested_at ? new Date(refund.completed_at || refund.requested_at).toLocaleString() : ""}</small></div></div>)}</div> : <div className="seller-empty seller-empty-inline">No refunds for this payment.</div>}
                  </div>}
                </>;
              })()}
            </div></td></tr>
              )}
              </React.Fragment>)}
              {entry.channel === "ebay" && [entry.order].map((order) => <React.Fragment key={`ebay-${order.channel_account_id}-${order.external_order_id}`}>
                <tr className="seller-order-row"><td><strong>{(order.lines || []).map((line) => line.title).filter(Boolean).slice(0, 2).join(" · ") || `eBay order ${order.external_order_id}`}</strong><small>eBay · #{order.external_order_id} · {order.line_count} line(s)</small></td>
                  <td><span className="seller-status">{order.payment_status || "Unknown"}</span></td>
                  <td>{order.observed_item_total == null ? "—" : money(order.observed_item_total, order.currency || "USD")}<small>Observed items only</small></td>
                  <td>eBay-managed</td><td>{order.order_date ? new Date(order.order_date).toLocaleDateString() : "—"}</td>
                  <td><button className="seller-link-btn" type="button" onClick={() => { if (selectedSellerOrderId) closeSellerOrderDetail(); setExpandedEbayOrder((prev) => prev === `${order.channel_account_id}:${order.external_order_id}` ? null : `${order.channel_account_id}:${order.external_order_id}`); }}>{expandedEbayOrder === `${order.channel_account_id}:${order.external_order_id}` ? "Hide" : "View"}</button></td></tr>
                {expandedEbayOrder === `${order.channel_account_id}:${order.external_order_id}` && <tr><td colSpan="6"><div className="seller-ebay-order-lines"><strong>eBay order lines (read-only)</strong>{(order.lines || []).map((line) => <div key={line.line_id} className="seller-ebay-order-line"><span>{line.title || line.line_id} · Qty {line.quantity ?? "—"}</span><span>{line.unit_price == null ? "Price unavailable" : money(line.unit_price, order.currency || "USD")}</span></div>)}<small>Payment, shipping, tax and fulfillment remain managed by eBay. Displayed item totals may exclude shipping and tax.</small></div></td></tr>}
              </React.Fragment>)}
                </React.Fragment>)}
              {!sellerOrders.length && !ebayOrders.length && <tr><td colSpan="6" className="seller-empty">{commerceLoading ? "Loading orders…" : "No seller orders yet."}</td></tr>}
            </tbody></table></div>
          </div>


          {fulfillmentOpen && orderDetail?.seller_order && (
            <div className="seller-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !fulfillmentBusy) setFulfillmentOpen(false); }}>
              <div className="seller-modal" role="dialog" aria-modal="true" aria-labelledby="seller-fulfillment-title">
                <div className="seller-card-heading"><div><h2 id="seller-fulfillment-title">Start fulfillment</h2><p>Create the physical shipment for this seller order. This does not change payment or inventory.</p></div><button className="seller-link-btn" type="button" disabled={fulfillmentBusy} onClick={() => setFulfillmentOpen(false)}>Close</button></div>
                <label className="seller-field"><span>Carrier</span><input value={fulfillmentDraft.carrier} onChange={(event) => setFulfillmentDraft((draft) => ({ ...draft, carrier: event.target.value }))} placeholder="USPS" /></label>
                <label className="seller-field"><span>Service</span><input value={fulfillmentDraft.service} onChange={(event) => setFulfillmentDraft((draft) => ({ ...draft, service: event.target.value }))} placeholder="Ground Advantage" /></label>
                <label className="seller-field"><span>Tracking number (optional)</span><input value={fulfillmentDraft.trackingNumber} onChange={(event) => setFulfillmentDraft((draft) => ({ ...draft, trackingNumber: event.target.value }))} placeholder="Enter tracking number" /></label>
                <label className="seller-field"><span>Tracking URL (optional)</span><input type="url" value={fulfillmentDraft.trackingUrl} onChange={(event) => setFulfillmentDraft((draft) => ({ ...draft, trackingUrl: event.target.value }))} placeholder="Carrier tracking link" /></label>
                <label className="seller-field"><span>Seller notes (internal, optional)</span><textarea rows="3" maxLength="300" value={fulfillmentDraft.sellerNotes} onChange={(event) => setFulfillmentDraft((draft) => ({ ...draft, sellerNotes: event.target.value }))} placeholder="Internal fulfillment note" /></label>
                <div className="seller-refund-confirm">This shipment will allocate the full quantity of every item in this seller order. Split-shipment controls can be added after the base lifecycle is proven.</div>
                <div className="seller-modal-actions"><button className="seller-btn" type="button" disabled={fulfillmentBusy} onClick={() => setFulfillmentOpen(false)}>Cancel</button><button className="seller-btn seller-btn-primary" type="button" disabled={fulfillmentBusy} onClick={createShipment}>{fulfillmentBusy ? "Creating…" : "Create shipment"}</button></div>
              </div>
            </div>
          )}

          {refundOpen && orderDetail?.payment && (
            <div className="seller-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !refundBusy) setRefundOpen(false); }}>
              <div className="seller-modal" role="dialog" aria-modal="true" aria-labelledby="seller-refund-title">
                <div className="seller-card-heading"><div><h2 id="seller-refund-title">Refund order</h2><p>Refunding changes financial state only. Inventory is not automatically restocked.</p></div><button className="seller-link-btn" type="button" disabled={refundBusy} onClick={() => setRefundOpen(false)}>Close</button></div>
                <div className="seller-refund-balance"><span>Maximum refundable</span><strong>{money(orderDetail.payment.remaining_refundable, orderDetail.payment.currency)}</strong></div>
                <div className="seller-segmented">
                  <button type="button" className={refundMode === "full" ? "active" : ""} onClick={() => { setRefundMode("full"); setRefundAmount(Number(orderDetail.payment.remaining_refundable || 0).toFixed(2)); }}>Full refund</button>
                  <button type="button" className={refundMode === "partial" ? "active" : ""} onClick={() => setRefundMode("partial")}>Partial refund</button>
                </div>
                {refundMode === "partial" && <label className="seller-field"><span>Refund amount</span><input type="number" min="0.01" step="0.01" max={Number(orderDetail.payment.remaining_refundable || 0)} value={refundAmount} onChange={(event) => setRefundAmount(event.target.value)} /></label>}
                <label className="seller-field"><span>Reason</span><select value={refundReason} onChange={(event) => setRefundReason(event.target.value)}><option value="buyer_request">Buyer request</option><option value="order_cancellation">Order cancellation</option><option value="item_unavailable">Item unavailable</option><option value="shipping_adjustment">Shipping adjustment</option><option value="item_issue_return">Item issue / return</option><option value="other">Other</option></select></label>
                <label className="seller-field"><span>Notes (optional)</span><textarea rows="3" maxLength="180" value={refundNotes} onChange={(event) => setRefundNotes(event.target.value)} placeholder="Add context for this refund" /></label>
                <div className="seller-refund-confirm">You are about to refund <strong>{money(refundMode === "full" ? orderDetail.payment.remaining_refundable : Number(refundAmount || 0), orderDetail.payment.currency)}</strong> through {orderDetail.payment.provider || "the payment provider"}.</div>
                <div className="seller-modal-actions"><button className="seller-btn" type="button" disabled={refundBusy} onClick={() => setRefundOpen(false)}>Cancel</button><button className="seller-btn seller-btn-primary" type="button" disabled={refundBusy} onClick={submitSellerRefund}>{refundBusy ? "Refunding…" : "Confirm refund"}</button></div>
              </div>
            </div>
          )}
        </section>
      )}

      {tab === "channels" && (
        <section className="seller-section">
          <div className="seller-card">
            <div className="seller-card-heading">
              <div><h2>Sales Channels</h2><p>Connect external marketplaces to your Hobbli seller account. Listing operations remain under Hobbli's rollout controls.</p></div>
              <button className="seller-btn" onClick={loadEbayChannel} disabled={ebayChannelLoading}>{ebayChannelLoading ? "Checking…" : "Refresh status"}</button>
            </div>
            <div className="seller-channel-card">
              <div className="seller-channel-head">
                <div><h3>eBay</h3><p>Production account connection</p></div>
                <span className={`seller-pill ${ebayChannel?.connected ? "good" : ""}`}>{ebayChannelLoading ? "Checking…" : ebayChannel?.connected ? "Connected" : "Not connected"}</span>
              </div>
              {ebayChannel?.connected && <div className="seller-info">Connected account: <strong>{ebayChannel.external_username || ebayChannel.external_account_id || "eBay seller"}</strong>. Status: {ebayChannel.connection_status || "connected"}.</div>}
              {!ebayChannelLoading && ebayChannel && !ebayChannel.can_connect && !ebayChannel.connected && <p className="seller-channel-note">{!ebayChannel.entitled ? "Multichannel selling is not enabled for this shop." : "eBay connection is not currently enabled for this seller."}</p>}
              <div className="seller-channel-features">
                <span>Listing imports: {ebayChannel?.import_enabled ? "Available" : "Not enabled"}</span>
                <span>Publishing: {ebayChannel?.publishing_enabled ? "Available" : "Not enabled"}</span>
                <span>Automatic sync: {ebayChannel?.automatic_sync_enabled ? "Available" : "Not enabled"}</span>
              </div>
              <p className="seller-channel-note">Hobbli does not charge marketplace transaction fees on sales completed through eBay. Connecting an account does not import or publish listings.</p>
              <div className="seller-row-actions">
                <button className="seller-btn seller-btn-primary" onClick={connectEbay} disabled={ebayChannelLoading || ebayConnectBusy || !ebayChannel?.can_connect}>{ebayConnectBusy ? "Opening eBay…" : ebayChannel?.connected ? "Reconnect eBay" : "Connect eBay"}</button>
                {ebayChannel?.connected && <button type="button" className="seller-btn" onClick={discoverEbayTrading} disabled={ebayTradingBusy || ebayChannelLoading}>{ebayTradingBusy ? "Loading listings…" : "Import eBay listings"}</button>}
              </div>
              {isAdmin && ebayChannel?.connected && <details className="seller-ebay-diagnostics">
                <summary>eBay developer diagnostics</summary>
                <div className="seller-ebay-queue-test seller-discovery-diagnostic">
                  <strong>Process Discovery Queue (Test)</strong>
                  <p className="seller-channel-note">Process at most one queued eBay event through the authenticated server bridge. No offer response is sent. Check the returned result for processing errors.</p>
                  <button type="button" className="seller-btn seller-btn-primary" onClick={testEbayDiscoveryQueue} disabled={ebayQueueTestBusy || ebayChannelLoading}>
                    {ebayQueueTestBusy ? "Processing queue…" : "Process Discovery Queue (1 event)"}
                  </button>
                  {ebayQueueTestResult && <div className="seller-reconcile-report" role="status" aria-live="polite">
                    <strong>Discovery queue result: {ebayQueueTestResult.status || "result"}</strong>
                    <pre className="seller-ebay-order-json">{JSON.stringify(ebayQueueTestResult, null, 2)}</pre>
                  </div>}
                </div>
                <p className="seller-channel-note">Admin-only testing tools. Historical order import writes staging observations only. Reconciliation and notification preferences remain read-only. Pilot enrichment may update inventory metadata or images.</p>
                <div className="seller-row-actions">
                  <button type="button" className="seller-btn seller-btn-primary" onClick={checkEbayNotificationPreferences} disabled={ebayPreferencesBusy || ebayChannelLoading}>{ebayPreferencesBusy ? "Checking preferences…" : "Check notification preferences"}</button>
                  <button type="button" className="seller-btn" onClick={discoverEbayListings} disabled={ebayDiscoveryBusy || ebayChannelLoading}>{ebayDiscoveryBusy ? "Reading eBay…" : "Inventory API discovery"}</button>
                  <button type="button" className="seller-btn" onClick={runEbayReconciliation} disabled={ebayReconcileBusy || ebayChannelLoading}>{ebayReconcileBusy ? "Reconciling…" : "Reconcile listings (read only)"}</button>

                  <button type="button" className="seller-btn" onClick={checkEbayPermissions} disabled={ebayScopeBusy || ebayChannelLoading}>{ebayScopeBusy ? "Checking permissions…" : "Check eBay permissions"}</button>
                  <button type="button" className="seller-btn" onClick={checkEbayOrderFinancials} disabled={ebayFinancialBusy || ebayChannelLoading}>{ebayFinancialBusy ? "Reading financials…" : "Check eBay order financials"}</button>
                  <button type="button" className="seller-btn seller-btn-primary" onClick={runEbayFinancialReconciliation} disabled={ebayFinancialRunBusy || ebayChannelLoading}>{ebayFinancialRunBusy ? "Reconciling financials…" : "Reconcile due financials (1 order)"}</button>
                  <label className="seller-financial-retry-field">eBay order ID
                    <input value={ebayFinancialManualOrder} onChange={(e) => setEbayFinancialManualOrder(e.target.value)} placeholder="22-15291-92796" aria-label="eBay order ID to retry" />
                  </label>
                  <button type="button" className="seller-btn" onClick={() => runEbayFinancialReconciliation(true)} disabled={ebayFinancialRunBusy || ebayChannelLoading}>{ebayFinancialRunBusy ? "Processing…" : "Retry order financials now"}</button>
                  <button type="button" className="seller-btn" onClick={() => inspectEbayFinancialQueue() } disabled={ebayFinancialQueueBusy || ebayChannelLoading}>{ebayFinancialQueueBusy ? "Inspecting queue…" : "Inspect financial queue (read only)"}</button>
                  <button type="button" className="seller-btn seller-btn-primary" onClick={importEbayPilotFinancials} disabled={ebayFinancialIngestBusy || ebayFinancialBusy || ebayChannelLoading}>{ebayFinancialIngestBusy ? "Importing pilot financials…" : "Import eBay financials (pilot)"}</button>
                  <button type="button" className="seller-btn" onClick={testEbayRefresh} disabled={ebayRefreshTestBusy || ebayChannelLoading}>{ebayRefreshTestBusy ? "Testing refresh…" : "Test token refresh"}</button>
                  {ebayChannel?.import_enabled && <button type="button" className="seller-btn" onClick={() => runEbayEnrichment(false, false)} disabled={ebayEnrichBusy || ebayChannelLoading}>Preview pilot enrichment</button>}
                  {ebayChannel?.import_enabled && <button type="button" className="seller-btn" onClick={() => runEbayEnrichment(true, false)} disabled={ebayEnrichBusy || ebayChannelLoading}>Apply pilot enrichment</button>}
                </div>
                <div className="seller-ebay-queue-test">
                  <strong>eBay notification destination · application setup</strong>
                  <p className="seller-channel-note">Inspect or register Hobbli's shared eBay webhook. Requires verified application administrator access. Activating subscriptions enables live eBay notifications. The action does not directly change listings or inventory.</p>
                  <div className="seller-row-actions">
                    <button type="button" className="seller-btn" onClick={() => manageEbayNotificationDestination('inspect')} disabled={ebayNotificationRegisterBusy || ebayChannelLoading}>{ebayNotificationRegisterBusy ? 'Contacting eBay…' : 'Inspect notification destination'}</button>
                    <button type="button" className="seller-btn" onClick={() => manageEbayNotificationDestination('inspect_subscriptions')} disabled={ebayNotificationRegisterBusy || ebayChannelLoading}>Inspect topics & subscriptions</button>
                    <button type="button" className="seller-btn seller-btn-primary" onClick={() => manageEbayNotificationDestination('activate_subscriptions')} disabled={ebayNotificationRegisterBusy || ebayChannelLoading}>{ebayNotificationRegisterBusy ? 'Contacting eBay…' : 'Enable order & offer notifications'}</button>
                    <button type="button" className="seller-btn seller-btn-primary" onClick={() => manageEbayNotificationDestination('register')} disabled={ebayNotificationRegisterBusy || ebayChannelLoading || !['ready_to_register', 'already_registered', 'registered'].includes(ebayNotificationRegisterResult?.status)}>{ebayNotificationRegisterBusy ? 'Registering…' : 'Register notification destination'}</button>
                  </div>
                  <div className="seller-row-actions" style={{ marginTop: 8 }}><input aria-label="Existing eBay subscription ID" placeholder="Existing subscription ID" value={ebaySubscriptionId} onChange={(e) => setEbaySubscriptionId(e.target.value)} /><button type="button" className="seller-btn" onClick={() => manageEbayNotificationDestination('test_subscription')} disabled={ebayNotificationRegisterBusy || !ebaySubscriptionId.trim()}>Request eBay signed test</button></div>
                  {ebayNotificationRegisterResult && <div className="seller-reconcile-report" role="status" aria-live="polite">
                    <strong>Notification destination: {ebayNotificationRegisterResult.status || 'result'}</strong>
                    <pre className="seller-ebay-order-json">{JSON.stringify(ebayNotificationRegisterResult, null, 2)}</pre>
                  </div>}
                </div>
                <div className="seller-ebay-date-discovery">
                  <strong>Import recent eBay sales history</strong>
                  <p className="seller-channel-note">Retrieve up to the most recent 90 days of eBay orders and save transaction lines to staging, including unmatched items. Safe to repeat. Does not change inventory or publish comps.</p>
                  <button type="button" className="seller-btn" onClick={runEbayOrderDiscovery} disabled={ebayOrderDiscoveryBusy || ebayChannelLoading}>{ebayOrderDiscoveryBusy ? "Importing orders…" : "Import last 90 days of eBay sales"}</button>
                </div>
                {ebayFinancialRunResult && <div className="seller-reconcile-report" role="status" aria-live="polite">
                  <strong>Financial reconciliation run: {ebayFinancialRunResult.results?.some((r) => r.status === "failed") ? "Failed" : ebayFinancialRunResult.results?.some((r) => r.status === "retry") ? "Retry scheduled" : ebayFinancialRunResult.results?.some((r) => r.status === "current") ? "Reconciled" : ebayFinancialRunResult.processed === 0 ? "No eligible orders" : ebayFinancialRunResult.status || "unknown"}</strong>
                  <pre className="seller-ebay-order-json">{JSON.stringify(ebayFinancialRunResult, null, 2)}</pre>
                </div>}
                {ebayFinancialQueueResult && <div className="seller-reconcile-report" role="status" aria-live="polite">
                  <strong>Financial reconciliation queue: {ebayFinancialQueueResult.status || "unknown"}</strong>
                  {ebayFinancialQueueResult.error && <p>{ebayFinancialQueueResult.error}</p>}
                  {ebayFinancialQueueResult.status === "completed" && <p>{ebayFinancialQueueResult.due_count} due · {ebayFinancialQueueResult.order_count} total · No changes made</p>}
                  <pre className="seller-ebay-order-json">{JSON.stringify(ebayFinancialQueueResult, null, 2)}</pre>
                </div>}
                {ebayPreferencesResult && <div className="seller-reconcile-report" role="status" aria-live="polite">
                  <strong>Notification preferences result</strong>
                  <pre className="seller-ebay-order-json">{JSON.stringify(ebayPreferencesResult, null, 2)}</pre>
                </div>}
              </details>}

              {ebayReconcileResult && <div className="seller-reconcile-report" role="status" aria-live="polite">
                <strong>eBay reconciliation: {ebayReconcileResult.status || "unknown"}</strong>
                {ebayReconcileResult.error && <p>{ebayReconcileResult.error}</p>}
                {ebayReconcileResult.run_id && <small>Run ID: {ebayReconcileResult.run_id}</small>}
                <div className="seller-reconcile-stats">
                  <span>Live observed <strong>{ebayReconcileResult.live_active_observed ?? "—"}</strong></span>
                  <span>Linked <strong>{ebayReconcileResult.linked_listings ?? "—"}</strong></span>
                  <span>Matched <strong>{ebayReconcileResult.matched ?? "—"}</strong></span>
                  <span>Discrepancies <strong>{ebayReconcileResult.discrepancy_count ?? "—"}</strong></span>
                  <span>Not in active feed <strong>{ebayReconcileResult.linked_not_in_active_feed ?? "—"}</strong></span>
                </div>
                {ebayReconcileResult.complete === false && <p>Pagination was incomplete. Treat this as a diagnostic only.</p>}
                {ebayReconcileResult.linked_not_in_active_feed > 0 && <p>Listings missing from the active feed are NOT confirmed sales.</p>}
                {(ebayReconcileResult.discrepancies || []).length > 0 && <details><summary>Review discrepancies</summary><div className="seller-table-wrap"><table className="seller-table"><thead><tr><th>Channel listing</th><th>Reason</th><th>eBay remaining</th><th>Hobbli available</th></tr></thead><tbody>{ebayReconcileResult.discrepancies.map((d, i) => <tr key={`${d.channel_listing_id || i}`}><td>{d.channel_listing_id || "—"}</td><td>{d.reason || "—"}</td><td>{d.live_remaining ?? "—"}</td><td>{d.hobbli_available ?? "—"}</td></tr>)}</tbody></table></div></details>}
                <p className="seller-channel-note">Diagnostic only. No inventory, eBay listings, publishing settings, or automatic sync changed.</p>
              </div>}
              {ebayOrderDiscoveryResult && <div className="seller-reconcile-report" role="status" aria-live="polite">
                <strong>eBay sales history: {ebayOrderDiscoveryResult.status || "result"}</strong>
                {ebayOrderDiscoveryResult.error && <p>{ebayOrderDiscoveryResult.error}</p>}
                {ebayOrderDiscoveryResult.saved_observations && <p>Saved/updated: {ebayOrderDiscoveryResult.saved_observations.processed} · Skipped: {ebayOrderDiscoveryResult.saved_observations.skipped} · Failed: {ebayOrderDiscoveryResult.saved_observations.failed}</p>}
                {ebayOrderDiscoveryResult.run_id && <small>Run ID: {ebayOrderDiscoveryResult.run_id}</small>}
                <div className="seller-reconcile-stats">
                  {[["Orders", "orders_observed"], ["Transactions", "transactions_observed"], ["Matched", "matched"], ["Unmatched", "unmatched"], ["Conflicts", "conflicts"]].filter(([,key]) => ebayOrderDiscoveryResult[key] != null).map(([label,key]) => <span key={key}>{label}<strong>{ebayOrderDiscoveryResult[key]}</strong></span>)}
                </div>
                <details><summary>View complete diagnostic response</summary><pre className="seller-ebay-order-json">{JSON.stringify(ebayOrderDiscoveryResult, null, 2)}</pre></details>
                <p className="seller-channel-note">Read-only diagnostic. No inventory, Hobbli orders, or eBay listings were modified.</p>
              </div>}
              {ebayChannel?.connected && ebayChannel?.import_enabled && <div className="seller-info" style={{ marginTop: 12 }}>
                <strong>Bulk eBay enrichment — linked inventory only</strong>
                <p>Import missing eBay photos, fill empty descriptions, and save shipping metadata. Completed listings are skipped on the next batch. No publishing or automatic sync.</p>
                <div className="seller-row-actions">
                  <label htmlFor="ebay-enrich-batch-size">Batch size</label>
                  <select id="ebay-enrich-batch-size" value={ebayEnrichBatchSize} onChange={(e) => setEbayEnrichBatchSize(e.target.value)} disabled={ebayEnrichBusy}>
                    <option value="5">5 listings</option><option value="10">10 listings</option><option value="25">25 listings</option><option value="50">50 listings</option><option value="100">All pending (up to 100)</option>
                  </select>
                  <button type="button" className="seller-btn seller-btn-primary" disabled={ebayEnrichBusy || ebayChannelLoading} onClick={() => runEbayEnrichment(true, true)}>{ebayEnrichBusy ? 'Enriching…' : 'Start enrichment batch'}</button>
                </div>
                {ebayEnrichProgress && <div role="status" aria-live="polite" style={{ marginTop: 10 }}>
                  <strong>{ebayEnrichProgress.processed} / {ebayEnrichProgress.total} listings</strong>
                  <div className="seller-progress"><span style={{ width: `${ebayEnrichProgress.total ? 100 * ebayEnrichProgress.processed / ebayEnrichProgress.total : 0}%` }} /></div>
                  <small>{ebayEnrichProgress.uploaded} photos uploaded · {ebayEnrichProgress.skipped} skipped · {ebayEnrichProgress.descriptions} descriptions imported · {ebayEnrichProgress.failures.length} issues</small>
                </div>}
              </div>}
              {ebayEnrichResult && (
                <div className="seller-info" style={{ marginTop: 12 }}>
                  <strong>Latest eBay enrichment result</strong>
                  <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: 380, overflowY: "auto", fontSize: 12, marginBottom: 0 }}>{JSON.stringify(ebayEnrichResult, null, 2)}</pre>
                </div>
              )}
              {ebayTradingResult?.status === "ready" && (ebayTradingResult.trading?.rows || []).length > 0 && (
                <div className="seller-info" style={{ marginTop: 12 }}>
                  <div className="seller-row-actions">
                    <button type="button" className="seller-btn seller-btn-primary" onClick={previewEbayMatches} disabled={ebayPreviewBusy}>
                      {ebayPreviewBusy ? "Comparing inventory…" : "Preview eBay Inventory Matches (Read Only)"}
                    </button>
                    <small>Compare with {inventory.length} Hobbli inventory records. No import.</small>
                  </div>
                  {ebayPreview && <>
                    <p><strong>Matching preview:</strong> {ebayPreview.length} eBay listings · {ebayPreview.filter((r) => r.match_state === "linked").length} already linked · {ebayPreview.filter((r) => r.match_state === "review").length} possible matches requiring review · {ebayPreview.filter((r) => r.match_state === "unmatched").length} no suggested match.</p>
                    <p>Suggested matches are NOT confirmed links. An unmatched listing is not necessarily a new physical item.</p>
                    <div className="seller-row-actions">
                      <label htmlFor="ebay-preview-filter">Show:</label>
                      <select id="ebay-preview-filter" value={ebayPreviewFilter} onChange={(e) => setEbayPreviewFilter(e.target.value)}>
                        <option value="all">All listings</option>
                        <option value="linked">Already linked</option>
                        <option value="review">Needs review</option>
                        <option value="unmatched">No suggested match</option>
                      </select>
                    </div>
                    <div className="seller-ebay-import-controls">
                      <strong>Import eBay listings into inventory</strong>
                      <p>Choose one, several, or all unmatched listings. Listings with possible inventory matches require manual review. Imported items stay eBay-only; publishing and automatic sync remain off.</p>
                      <div className="seller-row-actions">
                        <button type="button" className="seller-btn" disabled={ebayImportBusy || !channelLinksLoaded} onClick={() => setEbayImportSelection(new Set(ebayPreview.filter((r) => r.match_state === "unmatched" && /^\d{8,20}$/.test(String(r.item_id || ""))).map((r) => String(r.item_id))))}>Select all eligible ({ebayPreview.filter((r) => r.match_state === "unmatched" && /^\d{8,20}$/.test(String(r.item_id || ""))).length})</button>
                        <button type="button" className="seller-btn" disabled={ebayImportBusy} onClick={() => setEbayImportSelection(new Set())}>Clear selection</button>
                        <button type="button" className="seller-btn seller-btn-primary" onClick={importSelectedEbayListing}
                          disabled={!ebayChannel?.import_enabled || ebayImportBusy || !channelLinksLoaded || ebayTradingResult?.trading?.has_more || ebayImportSelection.size === 0}>
                          {ebayImportBusy ? "Importing…" : `Review & import ${ebayImportSelection.size} selected`}
                        </button>
                      </div>
                      {!ebayChannel?.import_enabled && <small>Importing is disabled by the production rollout.</small>}
                      {ebayTradingResult?.trading?.has_more && <small>Discovery is incomplete. Retrieve all eBay pages before bulk importing.</small>}
                      {ebayImportProgress && <div className="seller-ebay-import-progress" role="status" aria-live="polite">
                        <strong>{ebayImportBusy ? "Import in progress" : "Last import result"}: {ebayImportProgress.processed}/{ebayImportProgress.total}</strong>
                        <div className="seller-progress"><span style={{ width: `${ebayImportProgress.total ? 100 * ebayImportProgress.processed / ebayImportProgress.total : 0}%` }} /></div>
                        <small>{ebayImportProgress.imported} imported · {ebayImportProgress.already} already linked · {ebayImportProgress.failures.length} failed{ebayImportProgress.stopped ? " · stopped after authorization error" : ""}</small>
                        {ebayImportProgress.failures.length > 0 && <details><summary>Show failed eBay IDs</summary><ul>{ebayImportProgress.failures.map((f) => <li key={f.id}>{f.id}: {f.message}</li>)}</ul></details>}
                      </div>}
                    </div>
                    <div className="seller-table-wrap"><table className="seller-table"><thead><tr><th>Select</th><th>eBay ID</th><th>eBay title</th><th>eBay qty</th><th>Match status</th><th>Hobbli inventory candidate</th><th>Currently listed on</th></tr></thead><tbody>
                      {ebayPreview.filter((r) => ebayPreviewFilter === "all" || r.match_state === ebayPreviewFilter).map((r, index) => {
                        const candidateListing = r.match_item ? listingByInventoryId.get(r.match_item.id) : null;
                        return <tr key={`${r.item_id || "row"}-${index}`}>
                          <td><input type="checkbox" aria-label={`Select eBay listing ${r.item_id}`} disabled={r.match_state !== "unmatched" || !r.item_id || ebayImportBusy}
                            checked={ebayImportSelection.has(String(r.item_id))}
                            onChange={(event) => setEbayImportSelection((previous) => { const next = new Set(previous); if (event.target.checked) next.add(String(r.item_id)); else next.delete(String(r.item_id)); return next; })} /></td>
                          <td>{r.item_id || "—"}</td><td>{r.title || "Untitled"}</td><td>{r.quantity ?? "—"}</td>
                          <td><span className={`seller-status ${r.match_state === "linked" ? "active" : "draft"}`}>{r.match_state === "linked" ? "Already linked" : r.match_state === "review" ? "Review suggestion" : "No suggested match"}</span></td>
                          <td>{r.match_item ? <>{r.match_item.title || `Inventory ${r.match_item.id}`}<small>{r.match_state === "review" ? `Title overlap ${Math.round(r.match_score * 100)}% · ${r.candidate_count} candidate(s)` : "Existing link"}</small></> : "—"}</td>
                          <td>{r.match_state === "linked" ? <><span className="seller-marketplace-badge ebay">eBay</span> {candidateListing?.status === "published" && <span className="seller-marketplace-badge">Hobbli</span>}</> : candidateListing?.status === "published" ? "Hobbli (candidate only)" : "eBay (not imported)"}</td>
                        </tr>;
                      })}
                    </tbody></table></div>
                  </>}
                </div>
              )}
              {ebayTradingResult && (
                <div className="seller-info" style={{ marginTop: 12 }}>
                  <strong>Trading API discovery: {ebayTradingResult.status || "unknown"}</strong>
                  {ebayTradingResult.error && <p>{ebayTradingResult.error}</p>}
                  {ebayTradingResult.trading && <>
                    <p>Active listings: {ebayTradingResult.trading.total ?? "unknown"} · Pages retrieved: {ebayTradingResult.trading.pages_fetched ?? 0}{ebayTradingResult.trading.has_more ? " · More pages available" : ""}</p>
                    {ebayTradingResult.trading.error && <p>{ebayTradingResult.trading.error}</p>}
                    {(ebayTradingResult.trading.rows || []).length > 0 && <div className="seller-table-wrap"><table className="seller-table"><thead><tr><th>Item ID</th><th>Title</th><th>SKU</th><th>Price</th><th>Qty</th><th>Sold</th><th>Status</th></tr></thead><tbody>
                      {ebayTradingResult.trading.rows.map((item, index) => <tr key={`${item.item_id || "listing"}-${index}`}><td>{item.item_id || "—"}</td><td>{item.title || "Untitled"}</td><td>{item.sku || "—"}</td><td>{item.currency || ""} {item.price ?? "—"}</td><td>{item.quantity ?? "—"}</td><td>{item.quantity_sold ?? "—"}</td><td>{item.listing_status || "—"}</td></tr>)}
                    </tbody></table></div>}
                  </>}
                  <small>Read-only. No listing, inventory, or sale records were changed.</small>
                </div>
              )}
              {ebayDiscoveryResult && (
                <div className="seller-info" role="status" style={{ marginTop: 12 }}>
                  <strong>eBay discovery: {ebayDiscoveryResult.status || "unknown"}</strong>
                  {ebayDiscoveryResult.error && <p>{ebayDiscoveryResult.error}</p>}
                  {ebayDiscoveryResult.inventory && <p>Inventory API: {ebayDiscoveryResult.inventory.total ?? "unknown"} total items{ebayDiscoveryResult.inventory.error ? ` · ${ebayDiscoveryResult.inventory.error}` : ""}</p>}
                  {ebayDiscoveryResult.offers && <p>Offers API: {ebayDiscoveryResult.offers.total ?? "unknown"} total offers{ebayDiscoveryResult.offers.error ? ` · ${ebayDiscoveryResult.offers.error}` : ""}</p>}
                  {(ebayDiscoveryResult.inventory?.rows || []).length > 0 && <div><strong>Sample inventory</strong><ul>{ebayDiscoveryResult.inventory.rows.map((item, index) => <li key={`${item.sku || "item"}-${index}`}>{item.title || "Untitled"} · SKU {item.sku || "—"} · Qty {item.quantity ?? "—"}</li>)}</ul></div>}
                  {(ebayDiscoveryResult.offers?.rows || []).length > 0 && <div><strong>Sample offers</strong><ul>{ebayDiscoveryResult.offers.rows.map((offer, index) => <li key={`${offer.offer_id || "offer"}-${index}`}>{offer.sku || "No SKU"} · {offer.status || "Unknown"} · {offer.currency || ""} {offer.price ?? "—"} · Listing {offer.listing_id || "—"}</li>)}</ul></div>}
                  <p style={{ opacity: .7 }}>Read-only sample, first 20 per endpoint. No imports or synchronization performed.</p>
                </div>
              )}
              {ebayScopeResult && (
                <div className="seller-info seller-ebay-scope-report" role="status" aria-live="polite" style={{ marginTop: 12 }}>
                  <strong>eBay permissions: {ebayScopeResult.status || "unknown"}</strong>
                  {ebayScopeResult.message && <p>{ebayScopeResult.message}</p>}
                  {ebayScopeResult.status === "completed" && <>
                    <p>Token active: {ebayScopeResult.token_active ? "Yes" : "No"} · Scopes reported: {ebayScopeResult.scopes_reported ? "Yes" : "No"}</p>
                    <div className="seller-ebay-scope-grid">
                      <span>Inventory: <strong>{ebayScopeResult.has_inventory ? "Granted" : "Not confirmed"}</strong></span>
                      <span>Fulfillment: <strong>{ebayScopeResult.has_fulfillment ? "Granted" : "Not confirmed"}</strong></span>
                      <span>Finances: <strong>{ebayScopeResult.has_finances ? "Granted" : "Not confirmed"}</strong></span>
                    </div>
                    {Array.isArray(ebayScopeResult.authorized_scopes) && <details><summary>Reported scopes</summary><pre className="seller-ebay-order-json">{ebayScopeResult.authorized_scopes.join("\n") || "None returned"}</pre></details>}
                  </>}
                </div>
              )}
              {ebayFinancialIngestResult && (
                <div className="seller-info seller-ebay-ingest-report" role="status" aria-live="polite" style={{ marginTop: 12 }}>
                  <strong>eBay financial ledger import: {ebayFinancialIngestResult.status || "unknown"}</strong>
                  {ebayFinancialIngestResult.order_id && <p>Order: {ebayFinancialIngestResult.order_id}</p>}
                  {(ebayFinancialIngestResult.error || ebayFinancialIngestResult.message || ebayFinancialIngestResult.reason) && <p>{ebayFinancialIngestResult.error || ebayFinancialIngestResult.message || ebayFinancialIngestResult.reason}</p>}
                  {ebayFinancialIngestResult.status === "imported" && (
                    <>
                      <div className="seller-ebay-financial-grid">
                        <div><small>Order items</small><strong>{ebayFinancialIngestResult.line_count ?? "—"}</strong></div>
                        <div><small>Transactions</small><strong>{ebayFinancialIngestResult.transaction_count ?? "—"}</strong></div>
                        <div><small>Net proceeds (not profit)</small><strong>{money(ebayFinancialIngestResult.net_proceeds, ebayFinancialIngestResult.currency || "USD")}</strong></div>
                      </div>
                      <p className="seller-channel-note">Run the import again to verify idempotency. Counts reflect the API response; use the database audit to confirm stored totals.</p>
                    </>
                  )}
                  <details><summary>Full financial import response</summary><pre className="seller-ebay-order-json">{JSON.stringify(ebayFinancialIngestResult, null, 2)}</pre></details>
                </div>
              )}
              {ebayFinancialResult && (
                <div className="seller-info seller-ebay-financial-report" role="status" aria-live="polite" style={{ marginTop: 12 }}>
                  <strong>eBay financial discovery: {ebayFinancialResult.status || "unknown"}</strong>
                  <p>Order: {ebayFinancialResult.order_id || "22-15291-92796"} · Read-only</p>
                  {ebayFinancialResult.message && <p>{ebayFinancialResult.message}</p>}
                  {ebayFinancialResult.ebay_http_status && <p>eBay HTTP status: {ebayFinancialResult.ebay_http_status}</p>}
                  {ebayFinancialResult.fulfillment?.pricing_summary && (
                    <div className="seller-ebay-financial-grid">
                      {Object.entries(ebayFinancialResult.fulfillment.pricing_summary).map(([key, amount]) => (
                        <div key={key}><span>{key.replace(/_/g, " ")}</span><strong>{amount?.value != null ? money(amount.value, amount.currency || "USD") : "—"}</strong></div>
                      ))}
                    </div>
                  )}
                  {ebayFinancialResult.finances && <p>Financial transactions: {ebayFinancialResult.finances.transactions?.length ?? 0}{ebayFinancialResult.finances.next_page_available ? " · More pages available" : ""}</p>}
                  {ebayFinancialResult.finances?.transactions?.length > 0 && (
                    <div className="seller-table-wrap"><table className="seller-table"><thead><tr><th>Type</th><th>Transaction ID</th><th>Amount</th><th>Fees</th><th>Status</th></tr></thead><tbody>
                      {ebayFinancialResult.finances.transactions.map((transaction, index) => (
                        <tr key={transaction.transaction_id || index}>
                          <td>{transaction.transaction_type || "—"}</td><td className="seller-mono">{transaction.transaction_id || "—"}</td>
                          <td>{transaction.amount?.value != null ? money(transaction.amount.value, transaction.amount.currency || "USD") : "—"}</td>
                          <td>{transaction.total_fee_amount?.value != null ? money(transaction.total_fee_amount.value, transaction.total_fee_amount.currency || "USD") : "—"}</td>
                          <td>{transaction.transaction_status || "—"}</td>
                        </tr>
                      ))}
                    </tbody></table></div>
                  )}
                  <details><summary>Financial discovery JSON (no buyer details or tokens)</summary><pre className="seller-ebay-order-json">{JSON.stringify(ebayFinancialResult, null, 2)}</pre></details>
                </div>
              )}
              {ebayRefreshTestResult && (
                <div className="seller-info" role="status" style={{ marginTop: 12 }}>
                  <strong>Refresh test:</strong> {ebayRefreshTestResult.status || "unknown"}
                  {ebayRefreshTestResult.refreshed === true && " · New access token issued"}
                  {ebayRefreshTestResult.expires_at && ` · Expires: ${new Date(ebayRefreshTestResult.expires_at).toLocaleString()}`}
                  {ebayRefreshTestResult.message && ` · ${ebayRefreshTestResult.message}`}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {tab === "payments" && (
        <section className="seller-section seller-two-column">
          <div className="seller-card">
            <div className="seller-card-heading"><div><h2>Seller payment account</h2><p>Provider readiness for this shop. Checkout remains globally controlled by Hobbli while PayPal sandbox onboarding is validated.</p></div></div>
            {paymentAccounts.length ? paymentAccounts.map((account) => {
              const paymentsEnabled =
                account.payments_enabled ??
                account.capabilities?.payments_receivable ??
                false;
              const status =
                account.status ||
                (paymentsEnabled ? "connected" : "pending");
              const payoutsKnown =
                account.payouts_enabled !== undefined &&
                account.payouts_enabled !== null;

              return (
                <dl
                  className="seller-details"
                  key={account.payment_account_id || account.id || account.provider}
                >
                  <div><dt>Provider</dt><dd>{account.display_label || account.provider || "—"}</dd></div>
                  <div><dt>Status</dt><dd>{status}</dd></div>
                  <div><dt>Payments</dt><dd>{paymentsEnabled ? "Enabled" : "Not enabled"}</dd></div>
                  <div><dt>Payouts</dt><dd>{payoutsKnown ? (account.payouts_enabled ? "Enabled" : "Not enabled") : "Not reported"}</dd></div>
                </dl>
              );
            }) : <div className="seller-empty">{commerceLoading ? "Checking payment readiness…" : "No seller payment account is connected yet."}</div>}
          </div>
          <div className="seller-card">
            <div className="seller-card-heading"><div><h2>PayPal onboarding</h2><p>Connect this shop to PayPal Sandbox through Hobbli's server-side marketplace integration.</p></div></div>
            {paypalResult && <div className={`seller-paypal-result ${paypalResult.type}`}>{paypalResult.message}</div>}
            <div className="seller-paypal-actions">
              <button className="seller-btn seller-btn-primary" type="button" disabled={paypalBusy || !shopId} onClick={connectPayPal}>
                {paypalBusy ? "Working…" : paymentAccounts.some((account) => account.provider === "paypal") ? "Reconnect PayPal" : "Connect PayPal"}
              </button>
              <button className="seller-btn" type="button" disabled={paypalBusy || !shopId} onClick={() => refreshPayPalAccount()}>
                Check PayPal connection
              </button>
            </div>
            <div className="seller-info seller-info-spaced">Connect PayPal starts a new PayPal Sandbox onboarding session. If you already completed PayPal onboarding, use Check PayPal connection instead. Hobbli verifies the merchant directly with PayPal before enabling payments.</div>
          </div>
        </section>
      )}

      {tab === "plan" && (
        <section className="seller-section seller-two-column">
          <div className="seller-card"><div className="seller-card-heading"><div><h2>Plan & listing access</h2><p>Your resolved marketplace plan and listing capacity.</p></div></div><dl className="seller-details"><div><dt>Plan</dt><dd>{planAccess?.effective_plan?.plan_name || (unlimited ? "Pro" : "Free")}</dd></div><div><dt>Access source</dt><dd>{(planAccess?.effective_plan?.assignment_source || "default").replaceAll("_", " ")}</dd></div><div><dt>Access expires</dt><dd>{planAccess?.effective_plan?.ends_at ? new Date(planAccess.effective_plan.ends_at).toLocaleString() : "No expiration"}</dd></div><div><dt>Active listings</dt><dd>{activeCount}</dd></div><div><dt>Listing cap</dt><dd>{unlimited ? "Unlimited" : listingLimit ?? "—"}</dd></div></dl>{planAccess?.active_grants?.length > 0 && <div className="seller-info seller-info-spaced">Your current access includes a Hobbli plan grant. This is shown separately from any future paid subscription.</div>}</div>
          <div className="seller-card"><div className="seller-card-heading"><div><h2>Hobbli fees</h2><p>Effective seller fee policy after plan policy and shop overrides.</p></div></div>{feePolicy ? <dl className="seller-details">{Object.entries(feePolicy).filter(([key]) => !["shop_id"].includes(key)).slice(0,10).map(([key,value]) => <div key={key}><dt>{key.replaceAll("_", " ")}</dt><dd>{value === null ? "Inherited / none" : typeof value === "boolean" ? (value ? "Yes" : "No") : String(value)}</dd></div>)}</dl> : <div className="seller-empty">{commerceLoading ? "Loading fee policy…" : "Fee policy is not available."}</div>}</div>
        </section>
      )}

      {tab === "shipping" && (
        <section className="seller-section seller-two-column">
          <div className="seller-card">
            <div className="seller-card-heading"><div><h2>Shipping profiles</h2><p>Reusable package defaults. Listings keep their own shipping snapshot.</p></div></div>
            <div className="seller-profile-list">
              {profiles.filter((p) => p.is_active).map((profile) => <div className="seller-profile" key={profile.id}><div><strong>{profile.name}</strong><span>{profile.is_default ? "Default · " : ""}{profile.free_shipping ? "Free shipping" : money(profile.default_shipping_amount, profile.currency || "USD")}{profile.local_pickup ? " · Pickup" : ""}</span></div><button className="seller-link-btn danger" disabled={busyId === profile.id} onClick={() => archiveProfile(profile)}>Archive</button></div>)}
              {!profiles.some((p) => p.is_active) && <div className="seller-empty">No shipping profiles yet.</div>}
            </div>
          </div>
          <div className="seller-card">
            <div className="seller-card-heading"><div><h2>New shipping profile</h2><p>Enter seller-friendly U.S. units; Hobbli stores normalized metric values internally.</p></div></div>
            <form className="seller-form" onSubmit={createShippingProfile}>
              <label>Name<input value={profileDraft.name} onChange={(e) => setProfileDraft({ ...profileDraft, name: e.target.value })} placeholder="Standard Card Mailer" /></label>
              <label>Description<input value={profileDraft.description} onChange={(e) => setProfileDraft({ ...profileDraft, description: e.target.value })} /></label>
              <div className="seller-mini-grid"><label>Shipping $<input type="number" min="0" step="0.01" disabled={profileDraft.freeShipping} value={profileDraft.shippingAmount} onChange={(e) => setProfileDraft({ ...profileDraft, shippingAmount: e.target.value })} /></label><label>Tare oz<input type="number" min="0" step="0.1" value={profileDraft.tareOz} onChange={(e) => setProfileDraft({ ...profileDraft, tareOz: e.target.value })} /></label></div>
              <div className="seller-mini-grid three"><label>Length in<input type="number" min="0" step="0.1" value={profileDraft.lengthIn} onChange={(e) => setProfileDraft({ ...profileDraft, lengthIn: e.target.value })} /></label><label>Width in<input type="number" min="0" step="0.1" value={profileDraft.widthIn} onChange={(e) => setProfileDraft({ ...profileDraft, widthIn: e.target.value })} /></label><label>Height in<input type="number" min="0" step="0.1" value={profileDraft.heightIn} onChange={(e) => setProfileDraft({ ...profileDraft, heightIn: e.target.value })} /></label></div>
              <div className="seller-checks"><label><input type="checkbox" checked={profileDraft.freeShipping} onChange={(e) => setProfileDraft({ ...profileDraft, freeShipping: e.target.checked })} /> Free shipping</label><label><input type="checkbox" checked={profileDraft.localPickup} onChange={(e) => setProfileDraft({ ...profileDraft, localPickup: e.target.checked })} /> Local pickup</label><label><input type="checkbox" checked={profileDraft.combinedShipping} onChange={(e) => setProfileDraft({ ...profileDraft, combinedShipping: e.target.checked })} /> Combined shipping</label><label><input type="checkbox" checked={profileDraft.isDefault} onChange={(e) => setProfileDraft({ ...profileDraft, isDefault: e.target.checked })} /> Default profile</label></div>
              <button className="seller-btn seller-btn-primary" disabled={busyId === "new-profile"}>{busyId === "new-profile" ? "Creating…" : "Create Profile"}</button>
            </form>
          </div>
        </section>
      )}

      {tab === "shop" && (
        <section className="seller-section seller-two-column">
          <div className="seller-card">
            <div className="seller-card-heading">
              <div><h2>Shop settings</h2><p>Your seller-facing storefront identity and visibility.</p></div>
            </div>

            <form className="seller-form" onSubmit={saveShopSettings}>
              <label>
                Shop name
                <input value={shopDraft.name} onChange={(e) => setShopDraft({ ...shopDraft, name: e.target.value })} />
              </label>

              <label>
                Description
                <textarea value={shopDraft.description} onChange={(e) => setShopDraft({ ...shopDraft, description: e.target.value })} placeholder="Tell collectors about your shop…" />
              </label>

              <label>
                Visibility
                <select value={shopDraft.visibility} onChange={(e) => setShopDraft({ ...shopDraft, visibility: e.target.value })}>
                  <option value="public">Public — anyone can view</option>
                  <option value="members">Hobbli members only</option>
                </select>
              </label>

              <button className="seller-btn seller-btn-primary" disabled={busyId === "shop-settings"}>
                {busyId === "shop-settings" ? "Saving…" : "Save Shop Settings"}
              </button>
            </form>
          </div>

          <div className="seller-card">
            <div className="seller-card-heading">
              <div><h2>Shop URL</h2><p>Choose a unique Hobbli storefront address.</p></div>
            </div>

            <div className="seller-form">
              <label>
                Custom URL
                <div className="seller-slug-input">
                  <span>hobbli.com/</span>
                  <input value={slugDraft} onChange={(e) => setSlugDraft(e.target.value)} spellCheck="false" />
                </div>
              </label>

              <div className={`seller-slug-status ${slugCheck?.available ? "available" : slugCheck ? "unavailable" : ""}`}>
                {!slugCheck && "Enter a shop URL to check availability."}
                {slugCheck?.available && `✓ hobbli.com/${slugCheck.normalized_slug} is available`}
                {slugCheck && !slugCheck.available && (
                  slugCheck.reason === "reserved"
                    ? "That URL is reserved by Hobbli."
                    : slugCheck.reason === "in_use" || slugCheck.reason === "historical"
                      ? "That URL is already claimed."
                      : slugCheck.reason === "too_short"
                        ? "Shop URLs must be at least 3 characters."
                        : slugCheck.reason === "too_long"
                          ? "Shop URLs cannot exceed 50 characters."
                          : slugCheck.reason === "empty"
                            ? "Enter a shop URL."
                            : slugCheck.reason
                )}
              </div>

              <button className="seller-btn seller-btn-primary" type="button" disabled={busyId === "shop-slug" || !slugCheck?.available || slugCheck?.normalized_slug === shop?.slug} onClick={saveShopSlug}>
                {busyId === "shop-slug" ? "Saving…" : "Save Shop URL"}
              </button>
            </div>

            <div className="seller-url-actions">
              <button className="seller-btn" type="button" onClick={copyShopUrl}>Copy URL</button>
              <button className="seller-btn" type="button" onClick={() => navigate(`/${shop?.slug}`)}>View Shop</button>
            </div>

            <div className="seller-info seller-info-spaced">
              Previous shop URLs are preserved in Hobbli's slug history so the storefront route can redirect old links to the current address.
            </div>

            <dl className="seller-details seller-details-spaced">
              <div><dt>Status</dt><dd>{shop?.status || "—"}</dd></div>
              <div><dt>Seller account</dt><dd className="seller-mono">{sellerAccountId}</dd></div>
            </dl>
          </div>
        </section>
      )}

      {managedImageItem && (
        <div className="seller-image-modal-backdrop" role="presentation" onMouseDown={() => !imageManagerBusy && setImageManagerInventoryId(null)}>
          <div className="seller-image-modal" role="dialog" aria-modal="true" aria-label="Manage inventory images" onMouseDown={(e) => e.stopPropagation()}>
            <div className="seller-image-modal-header">
              <div>
                <span className="seller-eyebrow">INVENTORY MEDIA</span>
                <h2>{managedImageItem.title || `Inventory #${managedImageItem.id}`}</h2>
                <p>These images are shared by this inventory item and its marketplace listing.</p>
              </div>
              <button className="seller-btn" type="button" disabled={imageManagerBusy} onClick={() => setImageManagerInventoryId(null)}>Close</button>
            </div>

            <div className="seller-image-upload">
              <label className={`seller-btn seller-btn-primary ${imageManagerBusy ? "disabled" : ""}`}>
                {imageManagerBusy ? "Working…" : "Add Images"}
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={imageManagerBusy} onChange={uploadInventoryImages} />
              </label>
              <span>JPEG, PNG, or WebP · up to 10 MB each</span>
            </div>

            <div className="seller-image-grid">
              {sortedInventoryImages(managedImageItem).map((image, index, images) => (
                <article className={`seller-image-card ${image.is_primary ? "primary" : ""}`} key={image.id}>
                  <div className="seller-image-preview">
                    <img src={resolveImageUrl(image)} alt={`${managedImageItem.title || "Inventory"} ${index + 1}`} />
                    {image.is_primary && <span className="seller-image-primary-badge">Primary</span>}
                  </div>
                  <div className="seller-image-card-actions">
                    <button className="seller-btn seller-btn-small" type="button" disabled={imageManagerBusy || index === 0} onClick={() => moveInventoryImage(image, -1)}>←</button>
                    <button className="seller-btn seller-btn-small" type="button" disabled={imageManagerBusy || index === images.length - 1} onClick={() => moveInventoryImage(image, 1)}>→</button>
                    {!image.is_primary && (
                      <button className="seller-btn seller-btn-small" type="button" disabled={imageManagerBusy} onClick={() => setPrimaryInventoryImage(image)}>Make Primary</button>
                    )}
                    <button className="seller-btn seller-btn-small seller-btn-danger" type="button" disabled={imageManagerBusy} onClick={() => deleteInventoryImage(image)}>Remove</button>
                  </div>
                </article>
              ))}
              {!sortedInventoryImages(managedImageItem).length && (
                <div className="seller-image-empty">
                  <strong>No images yet.</strong>
                  <span>Add the first image for this inventory item.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
